import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft, Send, Copy, Check, Loader2, User, FileText, Eye, Clock, ChevronDown, ChevronUp, HelpCircle, Target, Activity, MessageSquare, TrendingUp, CheckCircle2, Timer, UserCircle, RefreshCw, X, Calendar, AlertCircle, Edit3 } from 'lucide-react';
import { getContactmomentNummer } from '../../utils/contactmomentNummer';
import { getClientDoelenByName } from '../../data/clientDoelen';

// Constanten buiten component voor stabiele referenties
const BEGELEIDERS = [
  'Dilek', 'Karim', 'Nassim', 'Seyed', 'Jasmina', 'Nisserine', 'Safia', 'Ilias B', 'Fatima', 'Faouzi', 'Ismail'
];

// Stagiaires - aparte lijst
const STAGIAIRES = ['Imad', 'Elif'];

export const CLIENTEN = {
  'groep-zwaluw': [
    { voornaam: 'Yassin', achternaam: 'Houba', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Sietse', achternaam: 'Bouwmans', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Axcel', achternaam: 'Trinidad', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Saied', achternaam: 'Raouf', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Danillio', achternaam: 'Wassink', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Mike', achternaam: 'Reenalda', locatie: 'groep-zwaluw', ambulant: false },
    { voornaam: 'Gerrie', achternaam: 'Du Prie', locatie: 'groep-zwaluw', ambulant: false },
  ],
  'groep-adelaars': [
    { voornaam: 'Melvin', achternaam: 'Hartman', locatie: 'groep-adelaars', ambulant: false },
    { voornaam: 'Rinaldo', achternaam: 'Bon', locatie: 'groep-adelaars', ambulant: false },
    { voornaam: 'Faraah', achternaam: 'Ibrahim', locatie: 'groep-adelaars', ambulant: false },
    { voornaam: 'Tess', achternaam: 'Baliga', locatie: 'groep-adelaars', ambulant: false },
  ],
  'ambulant': [
    { voornaam: 'Rudrix', achternaam: 'Fecunda', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Mahad', achternaam: 'Abib', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Rick', achternaam: 'Sluijmers', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Dennis', achternaam: 'Koster', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Maisson', achternaam: 'Salhuteru', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Ambrielle', achternaam: 'van der Krans', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Lorenzo', achternaam: 'Clarijs', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Jeroen', achternaam: 'Ben Smith', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Robin', achternaam: 'Hartman', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Melissa', achternaam: 'Ouwerkerk', ambulant: true, locatie: 'ambulant' },
    { voornaam: 'Benito', achternaam: 'Pistorius', ambulant: true, locatie: 'ambulant' },
  ]
};

// Uren naar contactmomenten mapping voor ambulant
const getAmbulantContactMomenten = (uren) => {
  if (uren <= 5) return uren;
  if (uren === 6) return 5;
  if (uren === 7) return 6;
  return uren;
};

// Smiley opties voor doel-rapportage
const SMILEY_OPTIES = [
  { value: 'niet_aan_bod', emoji: '😞', label: 'Niet aan bod gekomen', color: 'red' },
  { value: 'wel_aan_bod', emoji: '😊', label: 'Wel aan bod gekomen', color: 'emerald' }
];

// Diensttypen voor groepswonen
// BELANGRIJK: Contactmomenten zijn ALTIJD sequentieel genummerd (1, 2, 3), NOOIT tijdsgebonden
// Dit voorkomt dat de AI onbetrouwbare tijdsaanduidingen (ochtend/middag/avond) gebruikt
const DIENST_TYPES = [
  { 
    value: 'volledig', 
    label: 'Volledige dienst (08:00 - 22:00)', 
    tijden: '08:00 - 22:00',
    contactMomenten: 3,
    momenten: ['Contactmoment 1', 'Contactmoment 2', 'Contactmoment 3'],
    beschrijving: 'Je rapporteert 3 contactmomenten per cliënt'
  },
  { 
    value: 'dag', 
    label: 'Dagdienst (08:00 - 19:00)', 
    tijden: '08:00 - 19:00',
    contactMomenten: 2,
    momenten: ['Contactmoment 1', 'Contactmoment 2'],
    beschrijving: 'Je rapporteert 2 contactmomenten per cliënt'
  },
  { 
    value: 'avond', 
    label: 'Avonddienst (19:00 - 22:00)', 
    tijden: '19:00 - 22:00',
    contactMomenten: 1,
    momenten: ['Contactmoment 1'],
    beschrijving: 'Je rapporteert 1 contactmoment per cliënt'
  },
];

// De 7 PGB-vragen voor elk contactmoment
const PGB_VRAGEN = [
  {
    id: 'aanleiding',
    titel: '1. Aanleiding van het contactmoment',
    icon: HelpCircle,
    color: 'blue',
    vragen: [
      'Waarom was dit contactmoment nodig?',
      'Hoe was de cliënt (bijv. gespannen, onrustig, geen structuur)?',
      'Wat maakte dat begeleiding nodig was?'
    ],
    placeholder: 'Beschrijf de aanleiding: bijv. cliënt was onrustig, had moeite met dagstructuur, was gespannen over een afspraak...'
  },
  {
    id: 'doel',
    titel: '2. Doel van de begeleiding',
    icon: Target,
    color: 'violet',
    vragen: [
      'Wat was het doel van dit contactmoment?',
      'Waar heb je aan gewerkt?',
      'Wat wilde je bereiken met de begeleiding?'
    ],
    placeholder: 'Beschrijf het doel: bijv. rust creëren, structuur aanbrengen, helpen met persoonlijke verzorging...'
  },
  {
    id: 'activiteit',
    titel: '3. Wat heb je gedaan?',
    icon: Activity,
    color: 'emerald',
    vragen: [
      'Welke activiteiten heb je uitgevoerd?',
      'Hoe heb je de cliënt geholpen?',
      'Wat heb je concreet gedaan?'
    ],
    placeholder: 'Beschrijf de activiteit: bijv. samen ontbijt gemaakt, gesprek gevoerd, geholpen met ADL, activiteit begeleid...'
  },
  {
    id: 'waarom',
    titel: '4. Waarom was begeleiding nodig?',
    icon: MessageSquare,
    color: 'amber',
    vragen: [
      'Waarom kon de cliënt dit niet zelfstandig?',
      'Wat zou er gebeuren zonder jouw hulp?',
      'Wat is de beperking van de cliënt hierin?'
    ],
    placeholder: 'Leg uit waarom: bijv. cliënt kan niet zelfstandig structuur aanbrengen, vergeet afspraken, heeft moeite met emotieregulatie...'
  },
  {
    id: 'reactie',
    titel: '5. Hoe reageerde de cliënt?',
    icon: User,
    color: 'cyan',
    vragen: [
      'Hoe reageerde de cliënt op de begeleiding?',
      'Was de cliënt tevreden/rustig/gespannen?',
      'Hoe voelde de cliënt zich na de begeleiding?'
    ],
    placeholder: 'Beschrijf de reactie: bijv. cliënt werd rustiger, was dankbaar, voelde zich gehoord, had moeite met accepteren...'
  },
  {
    id: 'resultaat',
    titel: '6. Wat was het resultaat?',
    icon: TrendingUp,
    color: 'rose',
    vragen: [
      'Wat is er bereikt met dit contactmoment?',
      'Wat heeft de cliënt geleerd of gedaan?',
      'Is het doel behaald?'
    ],
    placeholder: 'Beschrijf het resultaat: bijv. cliënt at zijn ontbijt, werd rustiger, ging naar bed, voerde activiteit uit...'
  },
  {
    id: 'doelen',
    titel: '7. Koppeling aan zorgdoelen',
    icon: CheckCircle2,
    color: 'indigo',
    vragen: [
      'Hoe past dit binnen het zorgplan?',
      'Welke ontwikkeling zie je?'
    ],
    placeholder: 'Koppel aan zorgdoelen: bijv. werken aan zelfstandigheid, structuur in dagritme, sociale vaardigheden...'
  }
];

const RapporteerHelperPage = ({ onBack, userLocation, userLocations, userRole, userName }) => {
  const [selectedClient, setSelectedClient] = useState('');
  // Actuele cliënt, ook binnen trage async opslag-callbacks. Voorkomt dat het (late) antwoord van een opslag
  // voor cliënt A het 'bewerken'-ID van de inmiddels gekozen cliënt B overschrijft, waardoor de volgende
  // opslag de rapportage van A zou vervangen (ticket #268311).
  const selectedClientRef = useRef('');
  useEffect(() => { selectedClientRef.current = selectedClient; }, [selectedClient]);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [selectedDienst, setSelectedDienst] = useState('');
  const [ambulantUren, setAmbulantUren] = useState(2);
  const [contactMomenten, setContactMomenten] = useState([]);
  const [rapportage, setRapportage] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [currentStep, setCurrentStep] = useState(0);
  const [expandedMoment, setExpandedMoment] = useState(0);
  const [savingToDb, setSavingToDb] = useState(false);
  const [begeleiderNaam, setBegeleiderNaam] = useState(userName || '');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [abortController, setAbortController] = useState(null);
  const [loadingMomentIndex, setLoadingMomentIndex] = useState(null);
  const [gerapporteerdeMomenten, setGerapporteerdeMomenten] = useState({});
  const [viewingRapportageIndex, setViewingRapportageIndex] = useState(null);
  const [rapportageDatum, setRapportageDatum] = useState(new Date().toISOString().split('T')[0]); // Datum voor rapportage (standaard vandaag)
  
  // Yassin Houba doel-rapportage state
  const [selectedDoelen, setSelectedDoelen] = useState([]); // Max 2 doelen per rapportage sessie
  const [doelRapportages, setDoelRapportages] = useState({}); // { doelId: { smiley: '', toelichting: '', antwoorden: { vraagId: '' } } }
  const [gerapporteerdeDoelen, setGerapporteerdeDoelen] = useState({}); // { doelId: { rapportage: '', datum: '', begeleider: '' } }
  
  // Mijn Rapportages state
  const [showMijnRapportages, setShowMijnRapportages] = useState(false);
  const [mijnRapportages, setMijnRapportages] = useState([]);
  const [loadingMijnRapportages, setLoadingMijnRapportages] = useState(false);
  const [selectedMijnRapportage, setSelectedMijnRapportage] = useState(null);
  const [editingRapportageId, setEditingRapportageId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Melding als een eerder opgeslagen rapportage van vandaag automatisch is geladen
  const [hervatMelding, setHervatMelding] = useState('');
  // Een volledig afgeronde rapportage van vandaag die WEL gevonden is, maar nog NIET
  // automatisch in het formulier is geladen (zie hervatRapportageVanVandaag) - de
  // begeleider kiest zelf via een knop of hij deze wil bekijken/bewerken of een
  // nieuwe, aparte rapportage wil starten. NIET VERWIJDEREN (support-fix 27 sep 2026 -
  // voorkomt dat een zojuist afgeronde rapportage het formulier 'vastzet').
  const [pendingHervatting, setPendingHervatting] = useState(null);
  
  // Sjabloon met doelkoppeling state (voor alle cliënten)
  const [geselecteerdDoelPerMoment, setGeselecteerdDoelPerMoment] = useState({}); // { momentIndex: doelId }
  const [doelVragenPerMoment, setDoelVragenPerMoment] = useState({}); // { momentIndex: { vraag1: '', vraag2: '', vraag3: '' } }
  const [gegenereerdeDoelRapportages, setGegenereerdeDoelRapportages] = useState({}); // { momentIndex: 'rapportage tekst' }
  const [loadingDoelRapportage, setLoadingDoelRapportage] = useState({}); // { momentIndex: boolean }
  const [bijzonderhedenPerMoment, setBijzonderhedenPerMoment] = useState({}); // { momentIndex: 'bijzonderheden tekst' }
  
  // AI-gegenereerde doel vragen state
  const [aiGeneratedQuestions, setAiGeneratedQuestions] = useState({}); // { goalId: { questions: [...], loading: bool, error: string } }
  const [loadingAiQuestions, setLoadingAiQuestions] = useState({}); // { goalId: boolean }
  
  // Yassin Houba opgeslagen rapportages state
  const [sjabloonOpgeslagenRapportages, setSjabloonOpgeslagenRapportages] = useState([]);
  const [loadingSjabloonRapportages, setLoadingSjabloonRapportages] = useState(false);
  const [showSjabloonRapportages, setShowSjabloonRapportages] = useState(false);
  const [savingSjabloonRapportage, setSavingSjabloonRapportage] = useState(false);
  const [editingSjabloonRapportageId, setEditingSjabloonRapportageId] = useState(null);
  
  // Doel-rapportage vragen (verschijnen bij elke aangevinkte doel)
  const DOEL_RAPPORTAGE_VRAGEN = [
    {
      id: 'begeleiding',
      titel: '1. Welke begeleiding heb je vandaag geboden om aan het zorgdoel te werken?',
      beschrijving: 'Beschrijf concreet welke ondersteuning je hebt geboden, wat je samen met de cliënt hebt gedaan en hoe je de cliënt hierbij hebt begeleid.',
      placeholder: 'Beschrijf de begeleiding die je hebt geboden...'
    },
    {
      id: 'reactie',
      titel: '2. Hoe heeft de cliënt gereageerd op de begeleiding en wat heeft de cliënt zelf gedaan?',
      beschrijving: 'Beschrijf hoe de cliënt heeft meegewerkt, wat zelfstandig is gelukt en waarbij ondersteuning nodig was.',
      placeholder: 'Beschrijf de reactie en eigen inzet van de cliënt...'
    },
    {
      id: 'resultaat',
      titel: '3. Wat is het resultaat van de begeleiding en welke vervolgstappen zijn nodig?',
      beschrijving: 'Beschrijf welke vooruitgang is geboekt, wat nog niet is gelukt en welke begeleiding de cliënt nodig heeft om verder aan het doel te werken.',
      placeholder: 'Beschrijf het resultaat en de vervolgstappen...'
    }
  ];

  const effectiveLocation = userLocation || 'groep-zwaluw';
  const isCombinedAccount = userLocations && userLocations.length > 1;
  const isAdmin = userRole === 'admin' || userRole === 'zorgmanager';
  
  // DYNAMISCHE LOCATIE-BEPALING - geen hard-coded client checks meer nodig
  // Bepaal de locatie van de geselecteerde client direct uit de data
  const clientLocatie = useMemo(() => {
    if (!selectedClientData?.locatie) return 'onbekend';
    const loc = selectedClientData.locatie.toLowerCase();
    if (loc.includes('zwaluw')) return 'groep-zwaluw';
    if (loc.includes('adelaar')) return 'groep-adelaars';
    if (loc.includes('ambulant')) return 'ambulant';
    return 'onbekend';
  }, [selectedClientData?.locatie]);
  
  // Locatie-gebaseerde booleans (dynamisch)
  const isGroepZwaluwClient = clientLocatie === 'groep-zwaluw';
  const isGroepAdelaarsClient = clientLocatie === 'groep-adelaars';
  const isAmbulantClient = clientLocatie === 'ambulant';
  
  // Speciale case: Maisson Salhuteru heeft maar 1 contactmoment (i.p.v. 2)
  const isMaisonSalhuteru = useMemo(() => {
    if (!selectedClientData) return false;
    const voornaam = selectedClientData.voornaam?.toLowerCase() || '';
    const achternaam = selectedClientData.achternaam?.toLowerCase() || '';
    return (voornaam === 'maison' || voornaam === 'maisson') && achternaam === 'salhuteru';
  }, [selectedClientData]);
  
  // ALLE cliënten krijgen nu de sjabloon-flow met doelkoppeling
  // Dit zorgt ervoor dat IEDEREEN de nieuwe rapportage tool ziet met 2 contactmomenten en doelen
  const isSjabloonMetDoelkoppeling = selectedClientData !== null;
  
  // DYNAMISCH: Check of deze cliënt gedefinieerde doelen heeft via de centrale lookup
  // Dit werkt automatisch voor alle cliënten in clientDoelen.js zonder hard-coding
  const clientFullName = selectedClientData ? `${selectedClientData.voornaam} ${selectedClientData.achternaam}` : '';
  const clientDoelenFromLookup = useMemo(() => {
    if (!clientFullName) return null;
    return getClientDoelenByName(clientFullName);
  }, [clientFullName]);
  
  // Cliënt heeft doelen als de lookup een non-null, non-empty array retourneert
  const heeftGedefinieerdeDoelen = clientDoelenFromLookup && clientDoelenFromLookup.length > 0;
  
  // Cliënten zonder gedefinieerde doelen gebruiken een vrij tekstveld waar begeleider zelf het doel invult
  const gebruiktVraag7AlsTekst = isSjabloonMetDoelkoppeling && !heeftGedefinieerdeDoelen;
  
  // Oude doel-rapportage flow is UITGESCHAKELD - iedereen krijgt de sjabloon-flow
  const isDoelRapportageClient = false;
  
  // DYNAMISCH: Helper om de juiste doelen array te krijgen
  const getClientDoelen = useCallback(() => {
    return clientDoelenFromLookup || [];
  }, [clientDoelenFromLookup]);
  
  // DYNAMISCH: Helper om client info te krijgen voor doel-rapportage
  const getDoelRapportageClientInfo = useCallback(() => {
    if (!selectedClientData) return { naam: '', locatie: '', voornaam: '' };
    
    const naam = `${selectedClientData.voornaam} ${selectedClientData.achternaam}`;
    const voornaam = selectedClientData.voornaam;
    
    // Bepaal locatie op basis van clientData
    let locatie = 'Ambulant';
    if (selectedClientData.locatie) {
      const loc = selectedClientData.locatie.toLowerCase();
      if (loc.includes('zwaluw')) locatie = 'Leefgroep Zwaluw';
      else if (loc.includes('adelaar')) locatie = 'Leefgroep Adelaars';
      else if (loc.includes('ambulant')) locatie = 'Ambulant';
      else locatie = selectedClientData.locatie;
    }
    
    return { naam, locatie, voornaam };
  }, [selectedClientData]);

  // Functie om AI-gegenereerde vragen op te halen voor een specifiek doel
  const fetchAiGeneratedQuestions = useCallback(async (doel, clientInfo, forceRegenerate = false) => {
    if (!doel || !clientInfo?.naam) return null;
    
    const goalId = `${clientInfo.naam.replace(/\s+/g, '_')}_doel_${doel.id}`;
    
    // Check of we al vragen hebben geladen of aan het laden zijn (skip als force regenerate)
    if (!forceRegenerate && (aiGeneratedQuestions[goalId]?.questions || loadingAiQuestions[goalId])) {
      return aiGeneratedQuestions[goalId]?.questions;
    }
    
    setLoadingAiQuestions(prev => ({ ...prev, [goalId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/generate-goal-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          goal_id: goalId,
          goal_title: doel.titel,
          goal_description: doel.beschrijving || '',
          client_name: clientInfo.naam,
          force_regenerate: forceRegenerate
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiGeneratedQuestions(prev => ({
          ...prev,
          [goalId]: { questions: data.questions, error: null }
        }));
        return data.questions;
      } else {
        console.error('Failed to fetch AI questions:', response.status);
        setAiGeneratedQuestions(prev => ({
          ...prev,
          [goalId]: { questions: null, error: 'Kon vragen niet genereren' }
        }));
        return null;
      }
    } catch (error) {
      console.error('Error fetching AI questions:', error);
      setAiGeneratedQuestions(prev => ({
        ...prev,
        [goalId]: { questions: null, error: error.message }
      }));
      return null;
    } finally {
      setLoadingAiQuestions(prev => ({ ...prev, [goalId]: false }));
    }
  }, [aiGeneratedQuestions, loadingAiQuestions]);

  // Helper om de vragen voor een doel te krijgen (AI-gegenereerd of fallback naar vaste vragen)
  const getQuestionsForGoal = useCallback((doel, clientInfo) => {
    if (!doel) return DOEL_RAPPORTAGE_VRAGEN;
    
    const goalId = `${clientInfo?.naam?.replace(/\s+/g, '_')}_doel_${doel.id}`;
    const aiQuestions = aiGeneratedQuestions[goalId]?.questions;
    
    // Als we AI-gegenereerde vragen hebben, gebruik die
    if (aiQuestions && aiQuestions.length === 3) {
      return aiQuestions;
    }
    
    // Anders gebruik de vaste vragen uit clientDoelen.js als backup
    if (doel.vragen && doel.vragen.length > 0) {
      return doel.vragen;
    }
    
    // Laatste fallback: generieke vragen
    return DOEL_RAPPORTAGE_VRAGEN.map((v, i) => ({
      id: `vraag${i + 1}`,
      vraag: v.titel,
      placeholder: v.placeholder,
      type: i === 0 ? 'observatie' : i === 1 ? 'begeleiding' : 'resultaat'
    }));
  }, [aiGeneratedQuestions, DOEL_RAPPORTAGE_VRAGEN]);

  // Auto-fetch AI vragen wanneer een doel is geselecteerd maar nog geen AI vragen zijn geladen
  // Dit zorgt ervoor dat bij het laden van een opgeslagen rapportage de AI vragen ook worden opgehaald
  useEffect(() => {
    if (!heeftGedefinieerdeDoelen) return;
    
    const clientDoelen = getClientDoelen();
    const clientInfo = getDoelRapportageClientInfo();
    
    // Check voor elk geselecteerd doel of we AI vragen moeten ophalen
    Object.entries(geselecteerdDoelPerMoment).forEach(([momentIdx, doelId]) => {
      if (doelId === null || doelId === undefined) return;
      
      const doel = clientDoelen.find(d => d.id === doelId);
      if (!doel) return;
      
      const goalId = `${clientInfo?.naam?.replace(/\s+/g, '_')}_doel_${doelId}`;
      
      // Als we nog geen AI vragen hebben en niet al aan het laden zijn, fetch ze
      if (!aiGeneratedQuestions[goalId]?.questions && !loadingAiQuestions[goalId]) {
        fetchAiGeneratedQuestions(doel, clientInfo).catch(err => {
          console.error('Auto-fetch AI questions failed:', err);
        });
      }
    });
  }, [geselecteerdDoelPerMoment, heeftGedefinieerdeDoelen, getClientDoelen, aiGeneratedQuestions, loadingAiQuestions, fetchAiGeneratedQuestions]);


  // Initialiseer contactmomenten wanneer dienst wordt geselecteerd (voor groepswonen)
  // NIET uitvoeren als we in bewerk modus zijn (dan zijn de momenten al geladen)
  useEffect(() => {
    if (editingRapportageId) return; // Skip als we aan het bewerken zijn
    
    // Voor Yassin Houba of Melvin Hartman: reset doelen selectie bij nieuwe dienst
    if (isDoelRapportageClient && selectedDienst) {
      setSelectedDoelen([]);
      setDoelRapportages({});
      return;
    }
    
    if (selectedDienst && selectedClientData && !selectedClientData.ambulant) {
      const dienstInfo = DIENST_TYPES.find(d => d.value === selectedDienst);
      if (dienstInfo) {
        const newMomenten = dienstInfo.momenten.map((moment, index) => ({
          id: index,
          naam: moment,
          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
            acc[vraag.id] = '';
            return acc;
          }, {})
        }));
        setContactMomenten(newMomenten);
        setExpandedMoment(0);
      }
    }
  }, [selectedDienst, selectedClientData, editingRapportageId, isDoelRapportageClient]);

  // GROEP ZWALUW/ADELAARS: Automatische bypass van stap 2 naar stap 3
  // Dit zorgt ervoor dat deze cliënten NOOIT de uren-selectie slider zien
  useEffect(() => {
    if (isSjabloonMetDoelkoppeling && currentStep === 2) {
      // Direct naar stap 3 met 2 vaste contactmomenten
      setAmbulantUren(2);
      setSelectedDienst('groep_vast_2'); // Generieke code voor vaste 2 contactmomenten
      
      const newMomenten = [
        {
          id: 0,
          naam: 'Contactmoment 1',
          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
            acc[vraag.id] = '';
            return acc;
          }, {})
        },
        {
          id: 1,
          naam: 'Contactmoment 2',
          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
            acc[vraag.id] = '';
            return acc;
          }, {})
        }
      ];
      setContactMomenten(newMomenten);
      setExpandedMoment(0);
      setCurrentStep(3);
    }
  }, [isSjabloonMetDoelkoppeling, currentStep]);

  // Initialiseer contactmomenten wanneer uren worden geselecteerd (voor ambulant)
  // NIET uitvoeren als we in bewerk modus zijn of als dit Yassin Houba is
  useEffect(() => {
    if (editingRapportageId) return; // Skip als we aan het bewerken zijn
    if (isSjabloonMetDoelkoppeling) return; // Skip voor sjabloon-cliënten - hebben vaste 2 contactmomenten
    
    if (selectedClientData?.ambulant && ambulantUren > 0) {
      const aantalMomenten = getAmbulantContactMomenten(ambulantUren);
      const newMomenten = Array.from({ length: aantalMomenten }, (_, index) => ({
        id: index,
        naam: `Contactmoment ${index + 1}`,
        antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
          acc[vraag.id] = '';
          return acc;
        }, {})
      }));
      setContactMomenten(newMomenten);
      setExpandedMoment(0);
    }
  }, [ambulantUren, selectedClientData, editingRapportageId, isSjabloonMetDoelkoppeling]);

  // Functie om de storage key te krijgen per begeleider + CLIENT + DATUM
  // Dit voorkomt dat oude data van een vorige dag wordt geladen, EN dat het
  // concept van cliënt A het concept van cliënt B overschrijft wanneer dezelfde
  // begeleider op dezelfde dag meerdere cliënten rapporteert.
  // NIET VERWIJDEREN (support-fix 27 sep 2026 - root cause van de 'verkeerde cliënt' bug).
  const getStorageKey = useCallback((naam, client) => {
    const cleanName = (naam || 'onbekend').toLowerCase().trim().replace(/\s+/g, '_');
    const vandaag = new Date().toISOString().split('T')[0];
    const cleanClient = (client || '').toLowerCase().trim().replace(/\s+/g, '_');
    return cleanClient
      ? `pgb_rapportage_draft_${cleanName}_${cleanClient}_${vandaag}`
      : `pgb_rapportage_draft_${cleanName}_${vandaag}`;
  }, []);

  // Kleine 'pointer' die alleen onthoudt welke cliënt deze begeleider het laatst
  // aan het invullen was, zodat we bij een pagina-herlaad het JUISTE cliënt-specifieke
  // concept kunnen terugvinden zonder cliënten te verwarren.
  // NIET VERWIJDEREN (support-fix 27 sep 2026).
  const getLastClientPointerKey = useCallback((naam) => {
    const cleanName = (naam || 'onbekend').toLowerCase().trim().replace(/\s+/g, '_');
    const vandaag = new Date().toISOString().split('T')[0];
    return `pgb_rapportage_laatste_client_${cleanName}_${vandaag}`;
  }, []);

  // Bereken client key voor storage - vereenvoudigd om excessive dependencies te vermijden
  const getClientStorageKey = useCallback(() => {
    if (!selectedClientData) return 'client';
    const voornaam = (selectedClientData.voornaam || '').toLowerCase();
    const achternaam = (selectedClientData.achternaam || '').toLowerCase();
    return `${voornaam}_${achternaam}`.replace(/\s+/g, '_');
  }, [selectedClientData]);

  // Storage keys voor doel-rapportages - nu met vereenvoudigde client key
  const getDoelDraftStorageKey = useCallback(() => {
    const begeleiderKey = (begeleiderNaam || 'unknown').toLowerCase().replace(/\s+/g, '_');
    const clientKey = getClientStorageKey();
    return `${clientKey}_doel_draft_${begeleiderKey}_${rapportageDatum}`;
  }, [begeleiderNaam, getClientStorageKey, rapportageDatum]);

  const getDoelGerapporteerdeKey = useCallback(() => {
    const begeleiderKey = (begeleiderNaam || 'unknown').toLowerCase().replace(/\s+/g, '_');
    const clientKey = getClientStorageKey();
    return `${clientKey}_doel_gerapporteerd_${begeleiderKey}_${rapportageDatum}`;
  }, [begeleiderNaam, getClientStorageKey, rapportageDatum]);

  // CENTRALE RESET FUNCTIE - Reset ALLE state voor een schone start naar andere cliënt
  // NIET VERWIJDEREN - Voorkomt dat gebruikers vastzitten na rapporteren (support-fix 26 sep 2026)
  const resetForNewClient = useCallback(() => {
    // Reset cliënt selectie
    setSelectedClient('');
    setSelectedClientData(null);
    setSelectedDienst('');
    
    // Reset contactmomenten en rapportage
    setContactMomenten([]);
    setRapportage('');

    // KRITIEK: reset de rapportagedatum terug naar vandaag. Zonder dit bleef een
    // eerder gekozen inhaal-datum (voor een oude/gemiste rapportage) onterecht
    // staan voor de VOLGENDE cliënt, waardoor die nieuwe rapportage per ongeluk
    // op de verkeerde dag terechtkwam. NIET VERWIJDEREN (support-fix 27 sep 2026).
    setRapportageDatum(new Date().toISOString().split('T')[0]);
    setHervatMelding('');
    setPendingHervatting(null);

    // Reset navigatie naar stap 2 (cliëntselectie) - bestaand gedrag, bewust ongewijzigd
    // (regressietests verwachten dat "Andere cliënt"/"Volgende cliënt" direct naar
    // "Stap 2: Selecteer cliënt" gaat). De datumkiezer is nu ook op deze stap
    // bereikbaar, zie renderClientSelection (support-fix 27 sep 2026).
    setCurrentStep(1);
    setShowPreview(false);
    setViewingRapportageIndex(null);
    
    // Reset alle rapportage-specifieke states
    setGerapporteerdeMomenten({});
    setBijzonderhedenPerMoment({});
    setSelectedDoelen([]);
    setDoelRapportages({});
    setGerapporteerdeDoelen({});
    
    // KRITIEK: Reset sjabloon-specifieke states
    setEditingSjabloonRapportageId(null);
    setGeselecteerdDoelPerMoment({});
    setDoelVragenPerMoment({});
    setGegenereerdeDoelRapportages({});
    
    // Reset bewerk modus
    setEditingRapportageId(null);
    
    // Reset ambulant state
    setAmbulantUren(0);
    
    // Reset error state
    setError('');
    
    // Reset auto-save state
    setLastAutoSaved(null);
    
    // LET OP: we verwijderen HIER bewust NIET het concept van de cliënt die we
    // verlaten. Deze functie wordt ook aangeroepen wanneer een begeleider een
    // rapportage MIDDENIN laat staan om een andere cliënt te rapporteren (bijv.
    // onderbroken door een andere bewoner). Het concept blijft veilig staan onder
    // zijn eigen cliënt-sleutel (pgb_rapportage_draft_<naam>_<client>_<datum>) en
    // wordt pas gewist door clearSavedDraft() na een succesvolle/opgeslagen
    // rapportage. Zo kan de begeleider later dezelfde dag teruggaan naar de
    // vorige cliënt zonder eerder ingevulde antwoorden kwijt te raken
    // (support-fix 27 sep 2026 - regressie gevonden tijdens test van de client-mixup fix).
  }, []);

  // AUTO-SAVE voor doel-rapportages - laad opgeslagen data wanneer cliënt wijzigt
  useEffect(() => {
    if (!isDoelRapportageClient || !begeleiderNaam || !selectedClient) return;
    
    // Laad gerapporteerde doelen
    const gerapporteerdeKey = getDoelGerapporteerdeKey();
    const savedGerapporteerd = localStorage.getItem(gerapporteerdeKey);
    if (savedGerapporteerd) {
      try {
        const parsed = JSON.parse(savedGerapporteerd);
        setGerapporteerdeDoelen(parsed);
      } catch (e) {
        setGerapporteerdeDoelen({});
      }
    } else {
      // Reset als er geen opgeslagen data is voor deze cliënt
      setGerapporteerdeDoelen({});
    }
    
    // Laad draft antwoorden
    const draftKey = getDoelDraftStorageKey();
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.doelRapportages) setDoelRapportages(parsed.doelRapportages);
        if (parsed.selectedDoelen) setSelectedDoelen(parsed.selectedDoelen);
      } catch (e) {
        // Ignore parse errors
      }
    } else {
      // Reset als er geen draft is
      setDoelRapportages({});
      setSelectedDoelen([]);
    }
  }, [isDoelRapportageClient, begeleiderNaam, selectedClient, getDoelDraftStorageKey, getDoelGerapporteerdeKey]);

  // AUTO-SAVE voor doel-rapportages - sla wijzigingen op
  useEffect(() => {
    if (!isDoelRapportageClient || !begeleiderNaam) return;
    
    // Sla alleen op als er daadwerkelijk data is
    if (Object.keys(doelRapportages).length > 0 || selectedDoelen.length > 0) {
      const draftKey = getDoelDraftStorageKey();
      const dataToSave = {
        doelRapportages,
        selectedDoelen,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(draftKey, JSON.stringify(dataToSave));
    }
  }, [isDoelRapportageClient, begeleiderNaam, doelRapportages, selectedDoelen, getDoelDraftStorageKey]);

  // AUTO-SAVE: Laad opgeslagen data alleen als begeleider naam bekend is
  useEffect(() => {
    if (!begeleiderNaam) return;
    // KRITIEK: nooit het concept van een ANDERE cliënt over een al gekozen
    // cliënt heen zetten. Dit was de root cause van rapportages die onder de
    // verkeerde cliënt belandden. NIET VERWIJDEREN (support-fix 27 sep 2026).
    if (selectedClient) return;
    // KRITIEK: dit herstel mag ALLEEN bij het openen/heropenen van de tool
    // gebeuren (page-load), niet elke keer dat selectedClient tijdelijk leeg
    // wordt omdat de begeleider zelf "Andere cliënt" of "Volgende cliënt
    // rapporteren" indrukt. Zonder deze guard springt de begeleider na het
    // klikken op die knoppen direct terug naar de cliënt die hij net wilde
    // verlaten (regressie gevonden tijdens test van de client-mixup fix,
    // support-fix 27 sep 2026).
    if (heeftAlHersteldRef.current) return;
    heeftAlHersteldRef.current = true;
    
    // Cleanup: Verwijder oude drafts van vorige dagen
    const vandaag = new Date().toISOString().split('T')[0];
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('pgb_rapportage_draft_') || key.startsWith('pgb_gerapporteerd_')) {
          // Check of de key een datum bevat en of het niet vandaag is
          const dateMatch = key.match(/\d{4}-\d{2}-\d{2}/);
          if (dateMatch && dateMatch[0] !== vandaag) {
            localStorage.removeItem(key);
          }
          // Verwijder ook oude keys zonder datum (legacy)
          if (!dateMatch && key.startsWith('pgb_rapportage_draft_')) {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (e) {
      // Silently ignore cleanup errors
    }
    
    // KRITIEK: eerst opzoeken WELKE cliënt deze begeleider het laatst invulde
    // (pointer), en dan ALLEEN het concept van precies die cliënt laden. Zo kan
    // het concept van cliënt A nooit in het formulier van cliënt B belanden.
    // NIET VERWIJDEREN (support-fix 27 sep 2026).
    const pointerKey = getLastClientPointerKey(begeleiderNaam);
    const pointerRaw = localStorage.getItem(pointerKey);
    let laatsteClient = null;
    if (pointerRaw) {
      try {
        const pointer = JSON.parse(pointerRaw);
        if (pointer.timestamp && Date.now() - pointer.timestamp < 24 * 60 * 60 * 1000) {
          laatsteClient = pointer.selectedClient || null;
        }
      } catch (e) {
        // Negeer kapotte pointer
      }
    }

    const storageKey = getStorageKey(begeleiderNaam, laatsteClient);
    const savedData = localStorage.getItem(storageKey);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Check of data van vandaag is en van dezelfde begeleider
        if (parsed.timestamp && 
            Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000 &&
            parsed.begeleiderNaam?.toLowerCase() === begeleiderNaam.toLowerCase()) {
          if (parsed.selectedClient) setSelectedClient(parsed.selectedClient);
          if (parsed.selectedDienst) setSelectedDienst(parsed.selectedDienst);
          if (parsed.ambulantUren) {
            // Zorg ervoor dat het een nummer is
            const uren = typeof parsed.ambulantUren === 'number' 
              ? parsed.ambulantUren 
              : parseInt(parsed.ambulantUren) || 2;
            setAmbulantUren(uren);
          }
          if (parsed.contactMomenten && parsed.contactMomenten.length > 0) {
            // KRITIEK: Controleer of de contactMomenten de juiste structuur hebben
            // Als ze de oude flow structuur hebben (7 vragen, 3 momenten), forceer 2 momenten
            // NIET VERWIJDEREN - voorkomt oude flow bij laden (support-fix 26 sep 2026)
            let momenten = parsed.contactMomenten;
            const isMaisson = parsed.selectedClient?.toLowerCase().includes('maison') || 
                             parsed.selectedClient?.toLowerCase().includes('maisson');
            const expectedMomenten = isMaisson ? 1 : 2;
            
            // Als er meer momenten zijn dan verwacht, neem alleen de eerste 2 (of 1 voor Maisson)
            if (momenten.length > expectedMomenten) {
              momenten = momenten.slice(0, expectedMomenten);
            }
            // Als er minder momenten zijn, vul aan tot het verwachte aantal
            while (momenten.length < expectedMomenten) {
              momenten.push({
                id: momenten.length,
                naam: `Contactmoment ${momenten.length + 1}`,
                antwoorden: {
                  aanleiding: '', doel: '', activiteit: '', waarom: '', reactie: '', resultaat: '', doelen: ''
                }
              });
            }
            setContactMomenten(momenten);
          }
          if (parsed.currentStep && parsed.currentStep >= 3) {
            setCurrentStep(parsed.currentStep);
          }
          // KRITIEK: Herstel ook selectedClientData zodat isSjabloonMetDoelkoppeling correct is
          // NIET VERWIJDEREN - voorkomt dat gebruikers de oude flow zien (support-fix 26 sep 2026)
          if (parsed.selectedClient) {
            const allClients = [
              ...(CLIENTEN['groep-zwaluw'] || []),
              ...(CLIENTEN['groep-adelaars'] || []),
              ...(CLIENTEN['ambulant'] || [])
            ];
            const clientData = allClients.find(c => 
              `${c.voornaam} ${c.achternaam}`.toLowerCase() === parsed.selectedClient.toLowerCase()
            );
            if (clientData) {
              setSelectedClientData(clientData);
              // KRITIEK: Als we een bekende cliënt hebben, forceer currentStep naar 3 (vragen)
              // Dit voorkomt dat de dienst-selectie stap wordt getoond
              setCurrentStep(3);
            } else {
              // Fallback: maak dummy client data zodat isSjabloonMetDoelkoppeling = true
              const [voornaam, ...achternaamParts] = parsed.selectedClient.split(' ');
              setSelectedClientData({
                voornaam: voornaam || parsed.selectedClient,
                achternaam: achternaamParts.join(' ') || '',
                locatie: 'onbekend',
                ambulant: false
              });
              // Ook hier forceer currentStep naar 3
              setCurrentStep(3);
            }
          }
          // Stille herstel - geen melding tonen
        }
      } catch (e) {
        console.error('Kon opgeslagen data niet laden:', e);
      }
    }
  }, [begeleiderNaam, selectedClient, getStorageKey, getLastClientPointerKey]);

  // AUTO-SAVE: Sla data ALLEEN op vanaf stap 4 (vragen beantwoorden) - stil zonder UI updates
  useEffect(() => {
    // Alleen opslaan vanaf stap 3 (= stap 4 in UI) en als er een begeleider is
    if (currentStep < 3 || !begeleiderNaam) return;
    
    // Check of er daadwerkelijk antwoorden zijn ingevuld
    const hasAnswers = contactMomenten.some(m => 
      Object.values(m.antwoorden || {}).some(a => a && a.trim().length > 0)
    );
    if (!hasAnswers) return;

    // KRITIEK: opslaan onder een sleutel die ook de cliënt bevat, zodat het
    // concept van deze cliënt nooit het concept van een andere cliënt van
    // dezelfde begeleider overschrijft. NIET VERWIJDEREN (support-fix 27 sep 2026).
    const storageKey = getStorageKey(begeleiderNaam, selectedClient);
    const dataToSave = {
      begeleiderNaam,
      selectedClient,
      selectedDienst,
      ambulantUren,
      contactMomenten,
      currentStep,
      timestamp: Date.now()
    };
    
    // Stille opslag - geen state updates die UI doen bewegen
    try {
      const dataString = JSON.stringify(dataToSave);
      // Check of data niet te groot is (max 2MB voor localStorage)
      if (dataString.length > 2000000) {
        console.warn('Auto-save data te groot, skip opslaan');
        return;
      }
      localStorage.setItem(storageKey, dataString);
      // Houd bij welke cliënt deze begeleider het laatst invulde, zodat een
      // pagina-herlaad het JUISTE cliënt-concept terugvindt (support-fix 27 sep 2026).
      if (selectedClient) {
        localStorage.setItem(
          getLastClientPointerKey(begeleiderNaam),
          JSON.stringify({ selectedClient, timestamp: Date.now() })
        );
      }
    } catch (e) {
      // QuotaExceededError of andere localStorage errors - stil negeren
      if (e.name === 'QuotaExceededError') {
        console.warn('localStorage vol - oude concepten worden verwijderd');
        // Verwijder oude concepten van andere begeleiders
        try {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('pgb_rapportage_draft_') && key !== storageKey) {
              localStorage.removeItem(key);
            }
          });
          localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        } catch (e2) {
          console.error('Kon niet opslaan na cleanup:', e2);
        }
      } else {
        console.error('Auto-save failed:', e);
      }
    }
  }, [contactMomenten, currentStep, begeleiderNaam, selectedClient, selectedDienst, ambulantUren, getStorageKey, getLastClientPointerKey]);

  // Wis opgeslagen data na succesvolle rapportage
  const clearSavedDraft = () => {
    if (begeleiderNaam) {
      localStorage.removeItem(getStorageKey(begeleiderNaam, selectedClient));
      localStorage.removeItem(getLastClientPointerKey(begeleiderNaam));
    }
  };

  // =====================================================
  // YASSIN HOUBA RAPPORTAGES DATABASE FUNCTIES
  // =====================================================
  
  // Haal opgeslagen sjabloon rapportages op uit de database
  const fetchSjabloonRapportages = useCallback(async () => {
    if (!isSjabloonMetDoelkoppeling) return;
    
    setLoadingSjabloonRapportages(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/sjabloon-rapportages?begeleider=${encodeURIComponent(begeleiderNaam)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSjabloonOpgeslagenRapportages(data.rapportages || []);
      }
    } catch (err) {
      console.error('Fout bij ophalen sjabloon rapportages:', err);
    } finally {
      setLoadingSjabloonRapportages(false);
    }
  }, [begeleiderNaam, isSjabloonMetDoelkoppeling]);
  
  // Sla sjabloon rapportage op in de database
  // Optional parameter: directeMomentData - om verse data door te geven die nog niet in state zit
  // Optional parameter: silentSave - als true, toon geen saveSuccess melding (voor auto-save)
  const saveSjabloonRapportage = useCallback(async (directeMomentData = null, silentSave = false) => {
    if (!isSjabloonMetDoelkoppeling) return;
    
    if (!silentSave) setSavingSjabloonRapportage(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Haal de juiste doelen op voor de GESELECTEERDE cliënt (niet hardcoded!)
      const clientDoelen = getClientDoelen();
      
      // Bouw contactmomenten data
      const contactmomentenData = contactMomenten.map((moment, index) => {
        const selectedDoelId = geselecteerdDoelPerMoment[index];
        // FIX: Gebruik clientDoelen i.p.v. hardcoded YASSIN_HOUBA_DOELEN
        const selectedDoel = selectedDoelId ? clientDoelen.find(d => d.id === selectedDoelId) : null;
        
        // Check of er directe data is meegegeven voor dit moment
        const directData = directeMomentData && directeMomentData.momentIndex === index ? directeMomentData : null;
        
        return {
          moment_index: index,
          moment_naam: moment.naam,
          antwoorden: {
            aanleiding: moment.antwoorden.aanleiding || '',
            doel: moment.antwoorden.doel || '',
            activiteit: moment.antwoorden.activiteit || '',
            waarom: moment.antwoorden.waarom || '',
            reactie: moment.antwoorden.reactie || '',
            resultaat: moment.antwoorden.resultaat || '',
            bijzonderheden: bijzonderhedenPerMoment[index] || '',
            // Vraag 7 vrij tekstveld (cliënten zonder gedefinieerde zorgdoelen)
            doelen: moment.antwoorden.doelen || ''
          },
          // Gebruik directe data indien beschikbaar, anders state
          standaard_rapportage: directData?.standaardRapportage || gerapporteerdeMomenten[index]?.rapportage || null,
          doel_id: selectedDoelId || null,
          doel_titel: selectedDoel?.titel || null,
          doel_vragen: doelVragenPerMoment[index] ? {
            // Gebruik vraag1/vraag2/vraag3 als primaire keys (nieuwe structuur)
            vraag1: doelVragenPerMoment[index].vraag1 || doelVragenPerMoment[index].begeleiding || '',
            vraag2: doelVragenPerMoment[index].vraag2 || doelVragenPerMoment[index].reactie || '',
            vraag3: doelVragenPerMoment[index].vraag3 || doelVragenPerMoment[index].resultaat || '',
            // Behoud ook oude keys voor backward compatibility
            begeleiding: doelVragenPerMoment[index].vraag1 || doelVragenPerMoment[index].begeleiding || '',
            reactie: doelVragenPerMoment[index].vraag2 || doelVragenPerMoment[index].reactie || '',
            resultaat: doelVragenPerMoment[index].vraag3 || doelVragenPerMoment[index].resultaat || ''
          } : null,
          doel_rapportage: directData?.doelRapportage || gegenereerdeDoelRapportages[index] || null
        };
      });
      
      const payload = {
        client_naam: selectedClient, // CRITICAL: stuur de ECHTE client naam mee!
        begeleider_naam: begeleiderNaam,
        datum: rapportageDatum,
        contactmomenten: contactmomentenData
      };
      
      let response;
      if (editingSjabloonRapportageId) {
        // Update bestaande rapportage
        response = await fetch(`${backendUrl}/api/sjabloon-rapportages/${editingSjabloonRapportageId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ client_naam: selectedClient, contactmomenten: contactmomentenData })
        });
        // 409 = de onthouden rapportage hoort bij een andere cliënt: sla op als NIEUWE rapportage
        if (response.status === 409) {
          setEditingSjabloonRapportageId(null);
          response = await fetch(`${backendUrl}/api/sjabloon-rapportages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
        }
      } else {
        // Nieuwe rapportage
        response = await fetch(`${backendUrl}/api/sjabloon-rapportages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }
      
      if (response.ok) {
        const data = await response.json();
        if (!silentSave) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        }
        
        // Update de lijst van opgeslagen rapportages (alleen bij expliciete save, niet bij auto-save)
        if (!silentSave) {
          await fetchSjabloonRapportages();
        }
        
        // Als we een nieuwe rapportage hebben gemaakt, sla het ID op
        if (data.rapportage?.id && data.rapportage.id !== editingSjabloonRapportageId
            && selectedClientRef.current === selectedClient) {
          setEditingSjabloonRapportageId(data.rapportage.id);
        }
        
        return data.rapportage;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fout bij opslaan');
      }
    } catch (err) {
      if (!silentSave) {
        setError('Fout bij opslaan rapportage: ' + err.message);
      }
      return null;
    } finally {
      if (!silentSave) setSavingSjabloonRapportage(false);
    }
  }, [isSjabloonMetDoelkoppeling, getClientDoelen, contactMomenten, geselecteerdDoelPerMoment,
      bijzonderhedenPerMoment, gerapporteerdeMomenten, doelVragenPerMoment, gegenereerdeDoelRapportages,
      selectedClient, begeleiderNaam, rapportageDatum, editingSjabloonRapportageId, fetchSjabloonRapportages]);

  // Laad een opgeslagen sjabloon rapportage in het formulier
  const loadSjabloonRapportage = (rapportage) => {
    if (!rapportage || !rapportage.contactmomenten) return;
    
    setEditingSjabloonRapportageId(rapportage.id);
    setRapportageDatum(rapportage.datum);
    
    // KRITIEK: Zet de juiste cliënt op basis van de rapportage data
    if (rapportage.client_naam) {
      setSelectedClient(rapportage.client_naam);
      
      // Zoek de client data in de CLIENTEN lijst
      const allClients = [
        ...(CLIENTEN['groep-zwaluw'] || []),
        ...(CLIENTEN['groep-adelaars'] || []),
        ...(CLIENTEN['ambulant'] || [])
      ];
      const clientData = allClients.find(c => 
        `${c.voornaam} ${c.achternaam}`.toLowerCase() === rapportage.client_naam.toLowerCase()
      );
      if (clientData) {
        setSelectedClientData(clientData);
      } else {
        // FALLBACK: Als de client niet in de CLIENTEN lijst staat, maak een dummy object
        // Dit zorgt ervoor dat isSjabloonMetDoelkoppeling = true blijft
        // NIET VERWIJDEREN - voorkomt dat gebruikers de oude flow zien (support-fix 26 sep 2026)
        const [voornaam, ...achternaamParts] = rapportage.client_naam.split(' ');
        const achternaam = achternaamParts.join(' ');
        setSelectedClientData({
          voornaam: voornaam || rapportage.client_naam,
          achternaam: achternaam || '',
          locatie: 'onbekend',
          ambulant: false
        });
        console.warn(`Client "${rapportage.client_naam}" niet gevonden in CLIENTEN lijst, fallback client data aangemaakt`);
      }
    }
    
    // MIGRATIE: Converteer oude dagdeel-namen ("Ochtend", "Middag", "Avond", "Nacht") 
    // naar sequentiële contactmomenten ("Contactmoment 1", "Contactmoment 2", etc.)
    const migratieMapping = {
      'ochtend': 'Contactmoment 1',
      'middag': 'Contactmoment 2', 
      'avond': 'Contactmoment 3',
      'nacht': 'Contactmoment 4'
    };
    
    const migreerNaam = (naam, index) => {
      if (!naam) return `Contactmoment ${index + 1}`;
      const naamLower = naam.toLowerCase().trim();
      // Als het al een "Contactmoment X" naam is, behoud het
      if (naamLower.startsWith('contactmoment')) return naam;
      // Check op bekende dagdeel-namen
      if (migratieMapping[naamLower]) return migratieMapping[naamLower];
      // Voor andere gevallen, val terug op sequentiële nummering
      return `Contactmoment ${index + 1}`;
    };
    
    // Bouw contactMomenten state met migratie
    const nieuweMomenten = rapportage.contactmomenten.map((cm, index) => ({
      id: index,
      naam: migreerNaam(cm.moment_naam, index),
      antwoorden: {
        aanleiding: cm.antwoorden?.aanleiding || '',
        doel: cm.antwoorden?.doel || '',
        activiteit: cm.antwoorden?.activiteit || '',
        waarom: cm.antwoorden?.waarom || '',
        reactie: cm.antwoorden?.reactie || '',
        resultaat: cm.antwoorden?.resultaat || '',
        doelen: cm.antwoorden?.doelen || ''
      }
    }));
    setContactMomenten(nieuweMomenten);
    
    // Bouw bijzonderheden state
    const nieuweBijzonderheden = {};
    rapportage.contactmomenten.forEach((cm, index) => {
      if (cm.antwoorden?.bijzonderheden) {
        nieuweBijzonderheden[index] = cm.antwoorden.bijzonderheden;
      }
    });
    setBijzonderhedenPerMoment(nieuweBijzonderheden);
    
    // Bouw gerapporteerde momenten state
    const nieuweGerapporteerd = {};
    rapportage.contactmomenten.forEach((cm, index) => {
      if (cm.standaard_rapportage) {
        nieuweGerapporteerd[index] = {
          rapportage: cm.standaard_rapportage,
          timestamp: rapportage.updated_at || rapportage.created_at
        };
      }
    });
    setGerapporteerdeMomenten(nieuweGerapporteerd);
    
    // Bouw doel selectie state
    const nieuweDoelen = {};
    rapportage.contactmomenten.forEach((cm, index) => {
      if (cm.doel_id) {
        nieuweDoelen[index] = cm.doel_id;
      }
    });
    setGeselecteerdDoelPerMoment(nieuweDoelen);
    
    // Bouw doel vragen state - met backward compatibility voor oude en nieuwe keys
    const nieuweDoelVragen = {};
    rapportage.contactmomenten.forEach((cm, index) => {
      if (cm.doel_vragen) {
        nieuweDoelVragen[index] = {
          // Nieuwe keys (vraag1/vraag2/vraag3) hebben prioriteit, fallback naar oude keys
          vraag1: cm.doel_vragen.vraag1 || cm.doel_vragen.begeleiding || '',
          vraag2: cm.doel_vragen.vraag2 || cm.doel_vragen.reactie || '',
          vraag3: cm.doel_vragen.vraag3 || cm.doel_vragen.resultaat || '',
          // Behoud ook oude keys voor backward compatibility met displays
          begeleiding: cm.doel_vragen.vraag1 || cm.doel_vragen.begeleiding || '',
          reactie: cm.doel_vragen.vraag2 || cm.doel_vragen.reactie || '',
          resultaat: cm.doel_vragen.vraag3 || cm.doel_vragen.resultaat || ''
        };
      }
    });
    setDoelVragenPerMoment(nieuweDoelVragen);
    
    // Bouw gegenereerde doel rapportages state
    const nieuweDoelRapportages = {};
    rapportage.contactmomenten.forEach((cm, index) => {
      if (cm.doel_rapportage) {
        nieuweDoelRapportages[index] = cm.doel_rapportage;
      }
    });
    setGegenereerdeDoelRapportages(nieuweDoelRapportages);
    
    // Ga naar stap 4 (vragen)
    setShowSjabloonRapportages(false);
    setCurrentStep(3);
    setExpandedMoment(0);
  };

  // Bij het kiezen van een cliënt: laad de rapportage die deze begeleider vandaag al voor deze
  // cliënt heeft opgeslagen, zodat eerder ingevulde antwoorden weer zichtbaar zijn (andere
  // telefoon, pagina herladen, cliënt opnieuw gekozen). NIET VERWIJDEREN (support-fix 25 sep 2026).
  const laatsteClientKeuzeRef = useRef('');
  // Zorgt dat het automatisch herstellen van een opgeslagen concept maar één
  // keer gebeurt per keer dat de pagina wordt geopend, niet steeds opnieuw als
  // de begeleider zelf naar een andere cliënt navigeert (support-fix 27 sep 2026).
  const heeftAlHersteldRef = useRef(false);
  const contactMomentenRef = useRef(contactMomenten);
  useEffect(() => { contactMomentenRef.current = contactMomenten; }, [contactMomenten]);

  const hervatRapportageVanVandaag = async (clientNaam, aantalMomenten) => {
    laatsteClientKeuzeRef.current = clientNaam;
    if (!begeleiderNaam || !clientNaam) return;
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({ begeleider: begeleiderNaam, datum: rapportageDatum });
      const response = await fetch(`${backendUrl}/api/sjabloon-rapportages?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      // Alleen toepassen als de begeleider nog dezelfde cliënt heeft gekozen
      if (laatsteClientKeuzeRef.current !== clientNaam) return;
      // En niet als de begeleider intussen al is begonnen met typen
      const alIngevuld = (contactMomentenRef.current || []).some(m =>
        Object.values(m?.antwoorden || {}).some(v => typeof v === 'string' && v.trim())
      );
      if (alIngevuld) return;
      const bestaande = (data.rapportages || []).find(r =>
        (r.client_naam || '').trim().toLowerCase() === clientNaam.trim().toLowerCase()
      );
      if (!bestaande || !Array.isArray(bestaande.contactmomenten) || bestaande.contactmomenten.length === 0) return;
      // Sorteer op moment en vul aan tot het verwachte aantal contactmomenten
      const momenten = [...bestaande.contactmomenten].sort((a, b) => (a.moment_index ?? 0) - (b.moment_index ?? 0));
      for (let i = momenten.length; i < aantalMomenten; i++) {
        momenten.push({ moment_index: i, moment_naam: `Contactmoment ${i + 1}`, antwoorden: {} });
      }
      const volledigGerapporteerd = momenten.length >= aantalMomenten &&
        momenten.every(m => !!m.standaard_rapportage);
      if (volledigGerapporteerd) {
        // KRITIEK: een AL VOLLEDIG afgeronde rapportage van vandaag niet automatisch
        // in het (net leeggemaakte) formulier laden - dat zou de zojuist afgeronde
        // rapportage weer tonen en de indruk wekken dat je "vastzit" en geen nieuwe
        // rapportage kunt starten. In plaats daarvan bieden we een expliciete keuze
        // aan: bekijken/bewerken van de opgeslagen rapportage, of gewoon doorgaan met
        // het lege formulier voor een nieuwe, aparte rapportage.
        // NIET VERWIJDEREN (support-fix 27 sep 2026).
        setPendingHervatting({ ...bestaande, contactmomenten: momenten });
        setHervatMelding(`Je hebt vandaag al een volledige rapportage voor ${bestaande.client_naam} opgeslagen.`);
        return;
      }
      loadSjabloonRapportage({ ...bestaande, contactmomenten: momenten });
      setHervatMelding(`Je eerder opgeslagen rapportage van vandaag voor ${bestaande.client_naam} is geladen. Je kunt verder waar je gebleven was.`);
    } catch (err) {
      console.error('Fout bij laden van eerdere rapportage:', err);
    }
  };

  // Tweede, aparte rapportage op dezelfde dag (bijv. een tweede gesprek): maak het formulier
  // leeg en koppel het los van de geladen rapportage, zodat die niet wordt overschreven.
  // NIET VERWIJDEREN (support-fix 26 sep 2026).
  const startAparteRapportage = () => {
    const aantal = contactMomenten.length || 2;
    setEditingSjabloonRapportageId(null);
    setEditingRapportageId(null); // ook de oude opslag loskoppelen, anders overschrijft de volgende opslag de eerste rapportage
    setContactMomenten(Array.from({ length: aantal }, (_, i) => ({
      id: i,
      naam: `Contactmoment ${i + 1}`,
      antwoorden: PGB_VRAGEN.reduce((acc, vraag) => { acc[vraag.id] = ''; return acc; }, {})
    })));
    setBijzonderhedenPerMoment({});
    setGeselecteerdDoelPerMoment({});
    setDoelVragenPerMoment({});
    setGegenereerdeDoelRapportages({});
    setGerapporteerdeMomenten({});
    try { localStorage.removeItem(getGerapporteerdeKey()); } catch (e) { /* negeren */ }
    setExpandedMoment(0);
    setHervatMelding('');
    setPendingHervatting(null);
  };

  // Laad alsnog de volledig afgeronde rapportage van vandaag die door
  // hervatRapportageVanVandaag werd gevonden maar niet automatisch is toegepast.
  // NIET VERWIJDEREN (support-fix 27 sep 2026).
  const bekijkPendingHervatting = () => {
    if (!pendingHervatting) return;
    loadSjabloonRapportage(pendingHervatting);
    setHervatMelding(`Je eerder opgeslagen rapportage van vandaag voor ${pendingHervatting.client_naam} is geladen.`);
    setPendingHervatting(null);
  };

  // Haal gerapporteerde momenten key voor deze sessie (per begeleider + cliënt + datum)
  const getGerapporteerdeKey = useCallback(() => {
    const clientKey = (selectedClient || 'unknown').toLowerCase().replace(/\s+/g, '_');
    const begeleiderKey = (begeleiderNaam || 'unknown').toLowerCase().replace(/\s+/g, '_');
    return `pgb_gerapporteerd_${begeleiderKey}_${clientKey}_${rapportageDatum}`;
  }, [selectedClient, begeleiderNaam, rapportageDatum]);

  // Laad gerapporteerde momenten uit localStorage
  useEffect(() => {
    if (selectedClient && begeleiderNaam) {
      const key = getGerapporteerdeKey();
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          setGerapporteerdeMomenten(JSON.parse(saved));
        } catch (e) {
          setGerapporteerdeMomenten({});
        }
      } else {
        setGerapporteerdeMomenten({});
      }
    }
  }, [selectedClient, begeleiderNaam, getGerapporteerdeKey]);

  // Haal sjabloon rapportages op wanneer de client wordt geselecteerd
  useEffect(() => {
    if (isSjabloonMetDoelkoppeling && begeleiderNaam && currentStep >= 3) {
      fetchSjabloonRapportages();
    }
  }, [isSjabloonMetDoelkoppeling, begeleiderNaam, currentStep, fetchSjabloonRapportages]);

  // Auto-save ref voor debounce timer
  const autoSaveTimerRef = useRef(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);

  // AUTO-SAVE: Sla antwoorden automatisch op naar database na 2 seconden inactiviteit
  // Dit zorgt ervoor dat begeleiders hun werk niet kwijtraken als ze later terugkomen
  // NIET VERWIJDEREN - kritieke feature voor gebruikerservaring (support-fix 26 sep 2026)
  useEffect(() => {
    // Alleen voor sjabloon-cliënten en als er data is om op te slaan
    if (!isSjabloonMetDoelkoppeling || !begeleiderNaam || !selectedClient || currentStep < 3) return;
    
    // Check of er daadwerkelijk antwoorden zijn ingevuld
    const heeftAntwoorden = contactMomenten.some(m => 
      Object.values(m?.antwoorden || {}).some(v => typeof v === 'string' && v.trim().length > 0)
    );
    const heeftDoelVragen = Object.keys(doelVragenPerMoment).length > 0 && 
      Object.values(doelVragenPerMoment).some(dv => 
        Object.values(dv || {}).some(v => typeof v === 'string' && v.trim().length > 0)
      );
    const heeftBijzonderheden = Object.values(bijzonderhedenPerMoment).some(v => typeof v === 'string' && v.trim().length > 0);
    const heeftDoelRapportage = Object.values(gegenereerdeDoelRapportages).some(v => typeof v === 'string' && v.trim().length > 0);
    // Een geselecteerd doel (nog zonder ingevulde doel-vragen) telt ook als data om te bewaren,
    // anders gaat de keuze verloren als de begeleider wegnavigeert voordat de 3 vragen zijn ingevuld.
    const heeftDoelSelectie = Object.values(geselecteerdDoelPerMoment).some(v => v !== null && v !== undefined);

    if (!heeftAntwoorden && !heeftDoelVragen && !heeftBijzonderheden && !heeftDoelRapportage && !heeftDoelSelectie) return;
    
    // Clear bestaande timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Start nieuwe timer - sla op na 2 seconden inactiviteit
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setAutoSaving(true);
        await saveSjabloonRapportage(null, true); // silentSave = true
        setLastAutoSaved(new Date());
      } catch (err) {
        console.warn('Auto-save mislukt:', err);
      } finally {
        setAutoSaving(false);
      }
    }, 2000);
    
    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [contactMomenten, doelVragenPerMoment, bijzonderhedenPerMoment, geselecteerdDoelPerMoment, gegenereerdeDoelRapportages,
      isSjabloonMetDoelkoppeling, begeleiderNaam, selectedClient, currentStep, saveSjabloonRapportage]);

  // Markeer een contactmoment als gerapporteerd
  const markeerAlsGerapporteerd = (momentIndex, rapportageText) => {
    const nieuweStatus = {
      ...gerapporteerdeMomenten,
      [momentIndex]: {
        gerapporteerd: true,
        rapportage: rapportageText,
        timestamp: Date.now()
      }
    };
    setGerapporteerdeMomenten(nieuweStatus);
    
    // Sla op in localStorage
    const key = getGerapporteerdeKey();
    localStorage.setItem(key, JSON.stringify(nieuweStatus));
  };

  // RESET ALLE STATE VOOR NIEUWE RAPPORTAGE
  // Dit wordt aangeroepen wanneer een begeleider een nieuwe rapportage start
  // Let op: resetForNewClient is de centrale reset functie voor navigatie naar andere cliënt
  const resetVoorNieuweRapportage = () => {
    // Reset alle state naar beginwaarden
    setHervatMelding('');
    setPendingHervatting(null);
    setSelectedClient('');
    setSelectedClientData(null);
    setSelectedDienst('volledig');
    setAmbulantUren(2); // Reset naar standaard 2 uur (moet een nummer zijn, geen object!)
    setContactMomenten([]);
    setRapportage('');
    setGerapporteerdeMomenten({});
    setEditingRapportageId(null);
    setViewingRapportageIndex(null);
    setShowPreview(false);
    setError('');
    setSaveSuccess(false);
    setSelectedMijnRapportage(null);
    setGeselecteerdDoelPerMoment({}); // Reset doelselectie
    setDoelVragenPerMoment({}); // Reset doel vragen
    setGegenereerdeDoelRapportages({}); // Reset gegenereerde doel-rapportages
    setLoadingDoelRapportage({}); // Reset loading states
    setBijzonderhedenPerMoment({}); // Reset bijzonderheden
    setEditingSjabloonRapportageId(null); // Reset sjabloon rapportage editing
    setShowSjabloonRapportages(false); // Hide sjabloon rapportages panel
    // EXTRA: Reset ook doel states
    setSelectedDoelen([]);
    setDoelRapportages({});
    setGerapporteerdeDoelen({});
    // Reset auto-save indicator
    setLastAutoSaved(null);
    // Ga naar stap 2 (cliënt selectie)
    setCurrentStep(1);
  };

  // Check of een moment volledig is ingevuld
  const isMomentVolledig = (moment, momentIndex = null) => {
    // Voor Yassin Houba: alleen vragen 1-6 moeten ingevuld zijn (vraag 7 doelen is aparte rapportage)
    if (isSjabloonMetDoelkoppeling) {
      // Tel alleen antwoorden voor vragen 1-6 (niet 'doelen')
      const antwoordenZonderDoelen = Object.entries(moment.antwoorden)
        .filter(([key]) => key !== 'doelen')
        .filter(([, value]) => value && value.trim().length > 0);
      return antwoordenZonderDoelen.length >= 6; // 6 vragen (1-6)
    }
    
    return Object.values(moment.antwoorden).filter(a => a && a.trim().length > 0).length >= 7;
  };

  // Check of een moment al gerapporteerd is
  const isMomentGerapporteerd = (momentIndex) => {
    return gerapporteerdeMomenten[momentIndex]?.gerapporteerd === true;
  };

  // Haal de opgeslagen rapportage op voor een moment
  const getOpgeslagenRapportage = (momentIndex) => {
    return gerapporteerdeMomenten[momentIndex]?.rapportage || null;
  };

  // WIS CONTACTMOMENT - Reset een contactmoment zodat begeleider opnieuw kan invullen
  const wisContactMoment = (momentIndex) => {
    // Bevestiging vragen
    const moment = contactMomenten[momentIndex];
    const momentNaam = moment?.naam || `Moment ${momentIndex + 1}`;
    
    if (!window.confirm(`Weet je zeker dat je het contactmoment "${momentNaam}" wilt wissen?\n\nAlle antwoorden en de gegenereerde rapportage voor dit moment worden verwijderd.`)) {
      return;
    }
    
    // Reset de antwoorden voor dit moment
    const nieuweMomenten = contactMomenten.map((m, idx) => {
      if (idx === momentIndex) {
        return {
          ...m,
          antwoorden: {} // Wis alle antwoorden
        };
      }
      return m;
    });
    setContactMomenten(nieuweMomenten);
    
    // Verwijder uit gerapporteerde momenten
    const nieuweGerapporteerd = { ...gerapporteerdeMomenten };
    delete nieuweGerapporteerd[momentIndex];
    setGerapporteerdeMomenten(nieuweGerapporteerd);
    
    // Update localStorage
    const key = getGerapporteerdeKey();
    localStorage.setItem(key, JSON.stringify(nieuweGerapporteerd));
    
    // Als we dit moment aan het bekijken waren, sluit de preview
    if (viewingRapportageIndex === momentIndex) {
      setViewingRapportageIndex(null);
      setShowPreview(false);
      setRapportage('');
    }
    
    // Open dit moment zodat begeleider direct kan invullen
    setExpandedMoment(momentIndex);
    
    // Toon bevestiging
    setError(''); // Clear any error
    setSaveSuccess(false);
  };

  // Bekijk de opgeslagen rapportage voor een moment
  const bekijkOpgeslagenRapportage = (momentIndex) => {
    const opgeslagenRapportage = getOpgeslagenRapportage(momentIndex);
    if (opgeslagenRapportage) {
      setRapportage(opgeslagenRapportage);
      setViewingRapportageIndex(momentIndex);
      setCurrentStep(5);
      setShowPreview(true);
    }
  };

  // Tel niet-gerapporteerde, volledig ingevulde momenten
  const getNietGerapporteerdeVolledigeMomenten = () => {
    return contactMomenten.filter((m, idx) => 
      isMomentVolledig(m, idx) && !isMomentGerapporteerd(idx)
    );
  };

  // Annuleer lopende request
  const cancelRequest = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setLoading(false);
      setLoadingProgress(0);
      setError('Genereren geannuleerd. Je data is bewaard.');
    }
  };

  const getGroupedClients = () => {
    if (isCombinedAccount) {
      const groups = {
        'groep-zwaluw': { label: 'Groep Zwaluw', color: 'blue', clients: CLIENTEN['groep-zwaluw'] },
        'groep-adelaars': { label: 'Groep Adelaars', color: 'orange', clients: CLIENTEN['groep-adelaars'] },
        'ambulant': { label: 'Ambulant', color: 'purple', clients: CLIENTEN['ambulant'] }
      };
      
      if (locationFilter === 'all') return groups;
      if (locationFilter === 'groep-zwaluw') return { 'groep-zwaluw': groups['groep-zwaluw'], 'ambulant': groups['ambulant'] };
      if (locationFilter === 'groep-adelaars') return { 'groep-adelaars': groups['groep-adelaars'], 'ambulant': groups['ambulant'] };
      return groups;
    }
    
    const locClients = CLIENTEN[effectiveLocation] || CLIENTEN['groep-zwaluw'];
    return {
      [effectiveLocation]: { 
        label: effectiveLocation === 'groep-zwaluw' ? 'Groep Zwaluw' : 'Groep Adelaars', 
        color: effectiveLocation === 'groep-zwaluw' ? 'blue' : 'orange',
        clients: locClients 
      },
      'ambulant': { label: 'Ambulant', color: 'purple', clients: CLIENTEN['ambulant'] }
    };
  };

  const groupedClients = getGroupedClients();

  const updateAntwoord = (momentIndex, vraagId, value) => {
    setContactMomenten(prev => {
      const updated = [...prev];
      updated[momentIndex] = {
        ...updated[momentIndex],
        antwoorden: {
          ...updated[momentIndex].antwoorden,
          [vraagId]: value
        }
      };
      return updated;
    });
  };

  // Welke vragen tellen mee voor het percentage. In de sjabloon-flow is vraag 7 (doelen) de
  // doelkeuze in Stap 2 en geen tekstveld, dus die telt niet mee (anders max 6/7 = 86%).
  // NIET VERWIJDEREN (support-fix 25 sep 2026).
  const getProgressVragen = () => isSjabloonMetDoelkoppeling
    ? PGB_VRAGEN.filter(vraag => vraag.id !== 'doelen')
    : PGB_VRAGEN;

  const telBeantwoord = (moment) => getProgressVragen()
    .filter(vraag => String(moment?.antwoorden?.[vraag.id] ?? '').trim().length > 0).length;

  const getMomentProgress = (moment) => {
    return Math.round((telBeantwoord(moment) / getProgressVragen().length) * 100);
  };

  const getTotalProgress = () => {
    if (contactMomenten.length === 0) return 0;
    const totalQuestions = contactMomenten.length * getProgressVragen().length;
    const answeredQuestions = contactMomenten.reduce((acc, m) => acc + telBeantwoord(m), 0);
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const canGenerate = () => {
    // Check of er minstens één niet-gerapporteerd, volledig ingevuld moment is
    if (!selectedClient || contactMomenten.length === 0) return false;
    if (!selectedClientData?.ambulant && !isSjabloonMetDoelkoppeling && !selectedDienst) return false;
    
    // Er moet minstens één niet-gerapporteerd, volledig ingevuld moment zijn
    return contactMomenten.some((m, idx) => 
      isMomentVolledig(m, idx) && !isMomentGerapporteerd(idx)
    );
  };

  // Check of een enkel moment klaar is om te rapporteren
  const canGenerateSingleMoment = (momentIndex) => {
    const moment = contactMomenten[momentIndex];
    return moment && isMomentVolledig(moment, momentIndex) && !isMomentGerapporteerd(momentIndex);
  };

  // Tel het aantal volledig ingevulde contactmomenten (inclusief gerapporteerde)
  const getCompletedMomentsCount = () => {
    return contactMomenten.filter((m, idx) => isMomentVolledig(m, idx)).length;
  };

  // Tel het aantal gerapporteerde contactmomenten
  const getGerapporteerdeMomentenCount = () => {
    return contactMomenten.filter((m, idx) => isMomentGerapporteerd(idx)).length;
  };

  // Tel het aantal nog te rapporteren momenten (volledig maar niet gerapporteerd)
  const getNogTeRapporterenCount = () => {
    return contactMomenten.filter((m, idx) => 
      isMomentVolledig(m, idx) && !isMomentGerapporteerd(idx)
    ).length;
  };

  // Sla sjabloon op in database - update bestaande of maak nieuwe
  // Met retry logica voor betrouwbaarheid
  const saveSjabloonToDb = async (generatedRapportage, alleContactMomenten, maxRetries = 3) => {
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    if (!token || !backendUrl) {
      console.error('Geen token of backend URL beschikbaar');
      return null;
    }
    
    // Gebruik alle contactmomenten (ook lege) zodat we de volledige staat opslaan
    let momentenToSave = alleContactMomenten || contactMomenten;
    
    // BELANGRIJK: Zorg dat ALLE verwachte momenten worden meegestuurd
    // Alle contactmomenten zijn nu sequentieel genummerd (Contactmoment 1, 2, 3)
    if (!selectedClientData?.ambulant && selectedDienst) {
      const dienstInfo = DIENST_TYPES.find(d => d.value === selectedDienst);
      if (dienstInfo && dienstInfo.momenten) {
        // Maak een map van bestaande momenten
        const bestaandeMomenten = {};
        for (const m of momentenToSave) {
          bestaandeMomenten[m.naam] = m;
        }
        
        // Zorg dat alle verwachte momenten aanwezig zijn
        const completeMomenten = dienstInfo.momenten.map((momentNaam, index) => {
          if (bestaandeMomenten[momentNaam]) {
            return bestaandeMomenten[momentNaam];
          }
          // Voeg ontbrekend moment toe met lege antwoorden
          return {
            id: index,
            naam: momentNaam,
            antwoorden: {}
          };
        });
        momentenToSave = completeMomenten;
      }
    }
    
    // Tel hoeveel momenten volledig zijn ingevuld
    const volledigeMomenten = momentenToSave.filter(m => 
      Object.values(m.antwoorden || {}).filter(a => a && a.trim().length > 0).length >= 7
    ).length;
    
    const payload = {
      client_naam: selectedClient,
      client_locatie: selectedClientData?.locatie || effectiveLocation,
      dienst_type: selectedClientData?.ambulant ? 'ambulant' : selectedDienst,
      ambulant_uren: selectedClientData?.ambulant ? ambulantUren : null,
      contact_momenten: momentenToSave.map(m => ({
        naam: m.naam,
        antwoorden: m.antwoorden || {}
      })),
      gegenereerde_rapportage: generatedRapportage,
      begeleider_naam_input: begeleiderNaam,
      totaal_momenten_dienst: momentenToSave.length,
      volledige_momenten: volledigeMomenten,
      is_voltooid: volledigeMomenten === momentenToSave.length, // Alle momenten ingevuld?
      rapportage_datum: rapportageDatum // Datum waarvoor de rapportage is
    };
    
    // Retry loop voor betrouwbaarheid
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Check of we een bestaande rapportage aan het bewerken zijn
        let gebruikId = editingRapportageId;
        if (gebruikId) {
          // Update bestaande rapportage
          const response = await fetch(`${backendUrl}/api/rapportage-sjablonen/${gebruikId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          
          if (response.ok) {
            const data = await response.json();
            return data.id || editingRapportageId;
          } else if (response.status === 401) {
            console.error('Sessie verlopen bij opslaan');
            return null; // Niet opnieuw proberen bij auth error
          } else if (response.status === 409) {
            // Het onthouden rapportage-ID hoort bij een andere cliënt (ticket #268311): vergeet het en
            // bewaar als NIEUWE rapportage in plaats van de rapportage van die andere cliënt te overschrijven.
            setEditingRapportageId(null);
            gebruikId = null;
          }
        }
        if (!gebruikId) {
          // Check of er al een rapportage bestaat voor deze begeleider + cliënt + datum
          const checkResponse = await fetch(
            `${backendUrl}/api/rapportage-sjablonen?begeleider=${encodeURIComponent(begeleiderNaam)}&client=${encodeURIComponent(selectedClient)}&datum=${rapportageDatum}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            const bestaandeRapportage = checkData.sjablonen?.find(r => 
              r.client_naam === selectedClient && 
              r.datum === rapportageDatum &&
              r.dienst_type === (selectedClientData?.ambulant ? 'ambulant' : selectedDienst)
            );
            
            if (bestaandeRapportage) {
              // Update bestaande rapportage
              const updateResponse = await fetch(`${backendUrl}/api/rapportage-sjablonen/${bestaandeRapportage.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
              });
              
              if (updateResponse.ok) {
                if (selectedClientRef.current === selectedClient) setEditingRapportageId(bestaandeRapportage.id);
                return bestaandeRapportage.id;
              }
            }
          }
          
          // Geen bestaande gevonden, maak nieuwe aan
          const response = await fetch(`${backendUrl}/api/rapportage-sjablonen`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          
          if (response.ok) {
            const data = await response.json();
            if (selectedClientRef.current === selectedClient) setEditingRapportageId(data.id);
            return data.id;
          } else if (response.status === 401) {
            console.error('Sessie verlopen bij opslaan');
            return null;
          }
        }
        
        // Als we hier komen, is het niet gelukt maar ook geen auth error
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * attempt)); // Exponential backoff
        }
      } catch (err) {
        console.error(`Fout bij opslaan poging ${attempt}/${maxRetries}:`, err);
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    }
    
    console.error('Alle pogingen om rapportage op te slaan zijn mislukt');
    return null;
  };

  const buildTranscriptie = (momentenToInclude = null) => {
    // Bouw een gestructureerde transcriptie van de opgegeven contactmomenten
    // Als momentenToInclude null is, neem alle niet-gerapporteerde volledig ingevulde momenten
    let transcriptie = `BEGELEIDER: ${begeleiderNaam}\n`;
    
    const momenten = momentenToInclude || contactMomenten;
    
    momenten.forEach((moment, index) => {
      // Echt volgnummer binnen ALLE contactmomenten (niet de positie in de doorgegeven lijst):
      // een los tweede moment moet als CONTACTMOMENT 2 naar de AI (ticket #268311).
      const nummer = getContactmomentNummer(moment, contactMomenten, index);
      transcriptie += `\n=== CONTACTMOMENT ${nummer}: ${moment.naam} ===\n`;
      
      PGB_VRAGEN.forEach(vraag => {
        const antwoord = moment.antwoorden[vraag.id];
        if (antwoord && antwoord.trim()) {
          transcriptie += `\n${vraag.titel}:\n${antwoord.trim()}\n`;
        }
      });
    });
    
    return transcriptie.trim();
  };

  // Genereer rapportage voor een enkel contactmoment (tussentijds)
  const generateSingleMomentRapportage = async (momentIndex) => {
    const moment = contactMomenten[momentIndex];
    if (!isMomentVolledig(moment, momentIndex)) {
      // Voor Yassin Houba Stap 1: alleen vragen 1-6 nodig, NIET de doel-selectie
      if (isSjabloonMetDoelkoppeling) {
        setError('Vul alle 6 vragen in voor dit contactmoment');
      } else {
        setError('Vul alle 7 vragen in voor dit contactmoment');
      }
      return;
    }

    setLoading(true);
    setLoadingMomentIndex(momentIndex);
    setError('');
    setRapportage('');
    setLoadingProgress(5);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Je bent niet ingelogd. Log opnieuw in.');
      setLoading(false);
      setLoadingMomentIndex(null);
      setLoadingProgress(0);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      setError('Server configuratie ontbreekt.');
      setLoading(false);
      setLoadingMomentIndex(null);
      setLoadingProgress(0);
      return;
    }

    // Bouw transcriptie voor alleen dit ene moment
    let singleMomentTranscriptie = buildTranscriptie([moment]);
    
    // Voor Yassin Houba: voeg bijzonderheden toe aan de transcriptie (NIET zorgdoelen - die komen in Stap 2)
    if (isSjabloonMetDoelkoppeling) {
      const bijzonderheden = bijzonderhedenPerMoment[momentIndex];
      if (bijzonderheden && bijzonderheden.trim()) {
        singleMomentTranscriptie += `\n\nBIJZONDERHEDEN VOOR DE OVERDRACHT:\n${bijzonderheden.trim()}`;
      }
    }

    const requestBody = {
      client_voornaam: selectedClient,
      locatie: selectedClientData?.locatie || effectiveLocation,
      transcriptie: singleMomentTranscriptie,
      dienst: selectedClientData?.ambulant ? 'ambulant' : selectedDienst,
      aantal_contactmomenten: 1,
      contact_momenten_namen: [moment.naam],
      ambulant_uren: selectedClientData?.ambulant ? ambulantUren : null,
      begeleider: begeleiderNaam,
      is_tussentijds: true, // Markeer als tussentijdse rapportage
      skip_zorgdoelen_koppeling: isSjabloonMetDoelkoppeling // Voor sjabloon-cliënten: geen zorgdoelen in Stap 1
    };

    // Gebruik dezelfde retry logica als de volledige rapportage
    const executeWithRetry = async () => {
      let attemptCount = 0;
      let highestProgress = 5;
      
      while (true) {
        attemptCount++;
        
        let controller = null;
        let signal = null;
        try {
          if (typeof AbortController !== 'undefined') {
            controller = new AbortController();
            signal = controller.signal;
            setAbortController(controller);
          }
        } catch (e) {
          controller = null;
          signal = null;
        }
        
        const progressInterval = setInterval(function() {
          if (highestProgress < 92) {
            highestProgress += (92 - highestProgress) * 0.03;
            setLoadingProgress(Math.min(Math.round(highestProgress), 92));
          }
        }, 400);

        const timeoutMs = 180000;
        let timeoutId = null;
        if (controller) {
          timeoutId = setTimeout(function() { 
            try { controller.abort(); } catch(err) { 
              console.warn('AbortController.abort() failed:', err);
            } 
          }, timeoutMs);
        }

        try {
          const fetchOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(requestBody)
          };
          
          if (signal) {
            fetchOptions.signal = signal;
          }
          
          const response = await fetch(backendUrl + '/api/voice/generate-rapportage', fetchOptions);

          if (timeoutId) clearTimeout(timeoutId);
          clearInterval(progressInterval);

          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('AUTH_ERROR');
            }
            await new Promise(function(r) { setTimeout(r, 3000); });
            continue;
          }

          highestProgress = 95;
          setLoadingProgress(95);
          const data = await response.json();

          if (data && (data.success || data.rapportage)) {
            highestProgress = 100;
            setLoadingProgress(100);
            return data;
          } else {
            await new Promise(function(r) { setTimeout(r, 2000); });
            continue;
          }
        } catch (err) {
          if (timeoutId) clearTimeout(timeoutId);
          clearInterval(progressInterval);

          if (err && err.message === 'AUTH_ERROR') {
            throw err;
          }

          await new Promise(function(r) { setTimeout(r, 3000); });
          continue;
        }
      }
    };

    try {
      const data = await executeWithRetry();
      
      setError('');
      setRapportage(data.rapportage);
      
      // BACKUP: Sla rapportage lokaal op als fallback (voor het geval server save mislukt)
      try {
        const backupKey = `rapportage_backup_${selectedClient}_${new Date().toISOString().split('T')[0]}`;
        localStorage.setItem(backupKey, JSON.stringify({
          client: selectedClient,
          begeleider: begeleiderNaam,
          rapportage: data.rapportage,
          contactMomenten: contactMomenten,
          timestamp: new Date().toISOString()
        }));
      } catch (backupErr) {
        console.warn('Kon backup niet opslaan:', backupErr);
      }
      
      // Markeer dit moment als gerapporteerd
      markeerAlsGerapporteerd(momentIndex, data.rapportage);
      
      // Voor Yassin Houba: blijf op het formulier zodat gebruiker de doel-rapportage kan toevoegen
      // Voor anderen: ga naar preview
      if (!isSjabloonMetDoelkoppeling) {
        // Ga naar preview (dit is een nieuwe rapportage, niet een opgeslagen)
        setViewingRapportageIndex(null);
        setCurrentStep(5);
        setShowPreview(true);
      } else {
        // Voor sjabloon-cliënten: blijf op stap 4 (vragen) en toon success message
        setRapportage(data.rapportage);
        // Scroll naar de gegenereerde rapportage binnen Stap 1
      }
      
      // Sla de rapportage op
      // Voor Yassin Houba: gebruik de speciale Yassin save functie
      // Voor anderen: gebruik de standaard sjabloon save
      try {
        if (isSjabloonMetDoelkoppeling) {
          // Yassin Houba: direct opslaan met de gegenereerde rapportage data
          await saveSjabloonRapportage({
            momentIndex: momentIndex,
            standaardRapportage: data.rapportage,
            doelRapportage: null
          });
          // Ververs ook de sjabloon rapportages lijst
          await fetchSjabloonRapportages();
        } else {
          const savedId = await saveSjabloonToDb(data.rapportage, contactMomenten);
          if (savedId) {
            // Ververs de "Mijn Rapportages" lijst zodat de begeleider het direct ziet
            if (begeleiderNaam) {
              await fetchMijnRapportages(begeleiderNaam);
            }
            // Toon korte success melding (optioneel via toast of state)
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
          } else {
            setError('Let op: De rapportage is gegenereerd maar kon niet worden opgeslagen. Probeer opnieuw.');
          }
        }
      } catch (saveErr) {
        console.error('Kritieke fout bij opslaan rapportage:', saveErr);
        // Toon foutmelding maar laat de UI wel de gegenereerde rapportage zien
        setError('Let op: De rapportage is gegenereerd maar kon mogelijk niet worden opgeslagen. Probeer de pagina te verversen.');
      }
      
    } catch (err) {
      setLoadingProgress(0);
      
      if (err.message === 'AUTH_ERROR') {
        setError('Je sessie is verlopen. Sluit dit venster, log opnieuw in en probeer het nog een keer. Je antwoorden zijn bewaard.');
      }
    } finally {
      setLoading(false);
      setLoadingMomentIndex(null);
      setAbortController(null);
    }
  };

  const generateRapportage = async () => {
    // Haal alleen niet-gerapporteerde, volledig ingevulde momenten op
    const momentenToGenerate = contactMomenten.filter((m, idx) => 
      isMomentVolledig(m, idx) && !isMomentGerapporteerd(idx)
    );

    if (momentenToGenerate.length === 0) {
      setError('Er zijn geen nieuwe contactmomenten om te rapporteren. Vul een contactmoment in of kies een ander moment.');
      return;
    }

    setLoading(true);
    setError('');
    setRapportage('');
    setLoadingProgress(5);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Je bent niet ingelogd. Log opnieuw in.');
      setLoading(false);
      setLoadingProgress(0);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      setError('Server configuratie ontbreekt.');
      setLoading(false);
      setLoadingProgress(0);
      return;
    }

    const transcriptie = buildTranscriptie(momentenToGenerate);
    
    // Voeg geselecteerde doelen toe aan de transcriptie (voor alle cliënten met doelkoppeling)
    // FIX: Gebruik getClientDoelen() i.p.v. hardcoded YASSIN_HOUBA_DOELEN
    let extraDoelInfo = '';
    if (isSjabloonMetDoelkoppeling) {
      const clientDoelen = getClientDoelen();
      momentenToGenerate.forEach((moment, idx) => {
        const originalIdx = contactMomenten.findIndex(m => m.id === moment.id);
        const selectedDoelId = geselecteerdDoelPerMoment[originalIdx];
        if (selectedDoelId) {
          const selectedDoel = clientDoelen.find(d => d.id === selectedDoelId);
          if (selectedDoel) {
            extraDoelInfo += `\n\nGESELECTEERD ZORGDOEL VOOR ${moment.naam}: Doel ${selectedDoel.id}: ${selectedDoel.titel}\nDoelbeschrijving: ${selectedDoel.beschrijving}`;
          }
        }
      });
    }
    
    const dienstInfo = selectedClientData?.ambulant 
      ? { contactMomenten: getAmbulantContactMomenten(ambulantUren), momenten: contactMomenten.map(m => m.naam) }
      : DIENST_TYPES.find(d => d.value === selectedDienst);

    const requestBody = {
      client_voornaam: selectedClient,
      locatie: selectedClientData?.locatie || effectiveLocation,
      transcriptie: transcriptie + extraDoelInfo,
      dienst: selectedClientData?.ambulant ? 'ambulant' : selectedDienst,
      aantal_contactmomenten: momentenToGenerate.length,
      contact_momenten_namen: momentenToGenerate.map(m => m.naam),
      ambulant_uren: selectedClientData?.ambulant ? ambulantUren : null,
      begeleider: begeleiderNaam,
      is_tussentijds: momentenToGenerate.length < contactMomenten.length // Tussentijds als niet alle momenten
    };

    // Functie voor het uitvoeren van de API call - blijft ALTIJD proberen totdat het lukt
    // Compatibel met alle browsers inclusief oudere versies
    const executeWithRetry = async () => {
      let attemptCount = 0;
      let highestProgress = 5; // Houd de hoogste progress bij - gaat NOOIT omlaag
      
      // Blijf oneindig proberen totdat het lukt
      while (true) {
        attemptCount++;
        
        // AbortController met fallback voor oudere browsers
        let controller = null;
        let signal = null;
        try {
          if (typeof AbortController !== 'undefined') {
            controller = new AbortController();
            signal = controller.signal;
            setAbortController(controller);
          }
        } catch (e) {
          // AbortController niet beschikbaar in deze browser
          controller = null;
          signal = null;
        }
        
        // Progress simulatie - gaat ALLEEN omhoog, nooit omlaag
        const progressInterval = setInterval(function() {
          // Langzaam naar 92% - nooit hoger totdat we klaar zijn
          if (highestProgress < 92) {
            highestProgress += (92 - highestProgress) * 0.02;
            setLoadingProgress(Math.min(Math.round(highestProgress), 92));
          }
        }, 500);

        // Timeout per poging - 180 seconden
        const timeoutMs = 180000;
        let timeoutId = null;
        if (controller) {
          timeoutId = setTimeout(function() { 
            try { controller.abort(); } catch(abortErr) { 
              console.warn('Abort failed:', abortErr.message);
            } 
          }, timeoutMs);
        }

        try {
          // Fetch opties - met of zonder signal afhankelijk van browser support
          const fetchOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(requestBody)
          };
          
          if (signal) {
            fetchOptions.signal = signal;
          }
          
          const response = await fetch(backendUrl + '/api/voice/generate-rapportage', fetchOptions);

          if (timeoutId) clearTimeout(timeoutId);
          clearInterval(progressInterval);

          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('AUTH_ERROR');
            }
            // Bij server errors, wacht en probeer opnieuw (progress blijft stijgen)
            await new Promise(function(r) { setTimeout(r, 3000); });
            continue;
          }

          // Bijna klaar
          highestProgress = 95;
          setLoadingProgress(95);
          const data = await response.json();

          if (data && (data.success || data.rapportage)) {
            highestProgress = 100;
            setLoadingProgress(100);
            return data;
          } else {
            // Ongeldige response, probeer opnieuw (progress blijft waar het is)
            await new Promise(function(r) { setTimeout(r, 2000); });
            continue;
          }
        } catch (err) {
          if (timeoutId) clearTimeout(timeoutId);
          clearInterval(progressInterval);

          if (err && err.message === 'AUTH_ERROR') {
            throw err; // Alleen auth errors stoppen het proces
          }

          // Bij ALLE andere errors, wacht en probeer gewoon opnieuw (progress blijft stijgen)
          await new Promise(function(r) { setTimeout(r, 3000); });
          continue;
        }
      }
    };

    try {
      const data = await executeWithRetry();
      
      setError('');
      setRapportage(data.rapportage);
      
      // BACKUP: Sla rapportage lokaal op als fallback (voor het geval server save mislukt)
      try {
        const backupKey = `rapportage_backup_${selectedClient}_${new Date().toISOString().split('T')[0]}`;
        localStorage.setItem(backupKey, JSON.stringify({
          client: selectedClient,
          begeleider: begeleiderNaam,
          rapportage: data.rapportage,
          contactMomenten: contactMomenten,
          timestamp: new Date().toISOString()
        }));
      } catch (backupErr) {
        console.warn('Kon backup niet opslaan:', backupErr);
      }
      
      setViewingRapportageIndex(null); // Dit is een nieuwe rapportage
      setCurrentStep(5);
      setShowPreview(true);
      
      // Markeer alle gegenereerde momenten als gerapporteerd
      contactMomenten.forEach((m, idx) => {
        if (momentenToGenerate.includes(m)) {
          markeerAlsGerapporteerd(idx, data.rapportage);
        }
      });
      
      // Als alle momenten nu gerapporteerd zijn, wis draft
      const alleGerapporteerd = contactMomenten.every((m, idx) => 
        isMomentGerapporteerd(idx) || momentenToGenerate.includes(m)
      );
      if (alleGerapporteerd) {
        clearSavedDraft();
      }
      
      // Sla de volledige rapportage op (alle momenten in één rapportage)
      // KRITIEK: We wachten op succes en tonen feedback
      try {
        const savedId = await saveSjabloonToDb(data.rapportage, contactMomenten);
        if (savedId) {
          // Ververs de "Mijn Rapportages" lijst zodat de begeleider het direct ziet
          if (begeleiderNaam) {
            await fetchMijnRapportages(begeleiderNaam);
          }
          // Toon success feedback
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        } else {
          setError('Let op: De rapportage is gegenereerd maar kon niet worden opgeslagen. Probeer opnieuw.');
        }
      } catch (saveErr) {
        console.error('Kritieke fout bij opslaan rapportage:', saveErr);
        setError('Let op: De rapportage is gegenereerd maar kon mogelijk niet worden opgeslagen. Probeer de pagina te verversen.');
      }
      
    } catch (err) {
      setLoadingProgress(0);
      
      // Alleen bij sessie verlopen tonen we iets
      if (err.message === 'AUTH_ERROR') {
        setError('Je sessie is verlopen. Sluit dit venster, log opnieuw in en probeer het nog een keer. Je antwoorden zijn bewaard.');
      }
      // Alle andere errors worden genegeerd - het systeem blijft proberen
    } finally {
      setLoading(false);
      setAbortController(null);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(rapportage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Haal rapportages op voor een specifieke begeleider
  const fetchMijnRapportages = async (naam) => {
    if (!naam) return;
    
    setLoadingMijnRapportages(true);
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Haal BEIDE bronnen op: oude doel-rapportages EN nieuwe sjabloon-rapportages
      const [doelResponse, sjabloonResponse] = await Promise.all([
        fetch(`${backendUrl}/api/rapportage-sjablonen?begeleider=${encodeURIComponent(naam)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${backendUrl}/api/sjabloon-rapportages?begeleider=${encodeURIComponent(naam)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      let alleRapportages = [];
      
      // Oude doel-rapportages
      if (doelResponse.ok) {
        const doelData = await doelResponse.json();
        const doelRapportages = (doelData.sjablonen || []).map(r => ({
          ...r,
          _type: 'doel_rapportage',
          _label: 'Doel-rapportage'
        }));
        alleRapportages = [...alleRapportages, ...doelRapportages];
      }
      
      // Nieuwe sjabloon-rapportages (Groep Zwaluw/Adelaars/Ambulant)
      if (sjabloonResponse.ok) {
        const sjabloonData = await sjabloonResponse.json();
        const sjabloonRapportages = (sjabloonData.rapportages || []).map(r => ({
          ...r,
          _type: 'sjabloon_rapportage',
          _label: 'Sjabloon-rapportage',
          // Map velden voor consistente weergave
          client_naam: r.client_naam,
          begeleider_naam: r.begeleider_naam,
          datum: r.datum,
          contact_momenten: r.contactmomenten // Map naar verwacht veld
        }));
        alleRapportages = [...alleRapportages, ...sjabloonRapportages];
      }
      
      // Sorteer op datum (nieuwste eerst)
      alleRapportages.sort((a, b) => {
        const dateA = new Date(a.datum || a.created_at || 0);
        const dateB = new Date(b.datum || b.created_at || 0);
        return dateB - dateA;
      });
      
      setMijnRapportages(alleRapportages);
    } catch (err) {
      console.error('Kon rapportages niet ophalen:', err);
    } finally {
      setLoadingMijnRapportages(false);
    }
  };

  // Bekijk een opgeslagen rapportage uit "Mijn Rapportages"
  // Kan een enkele rapportage of een array van gegroepeerde rapportages zijn
  const bekijkMijnRapportage = (rapportageData, allRapportagesInGroup = null) => {
    if (allRapportagesInGroup && allRapportagesInGroup.length > 1) {
      // Maak een samengevoegd object voor de view
      const alleMomenten = [];
      const alleRapportages = [];
      
      for (const rap of allRapportagesInGroup) {
        if (rap.gegenereerde_rapportage) {
          alleRapportages.push(rap.gegenereerde_rapportage);
        }
        for (const moment of (rap.contact_momenten || [])) {
          const existing = alleMomenten.find(m => m.naam === moment.naam);
          if (existing) {
            // Merge antwoorden
            for (const [key, value] of Object.entries(moment.antwoorden || {})) {
              if (value && value.trim() && !existing.antwoorden[key]) {
                existing.antwoorden[key] = value;
              }
            }
          } else {
            alleMomenten.push({ ...moment, antwoorden: { ...(moment.antwoorden || {}) } });
          }
        }
      }
      
      // Sorteer momenten op sequentie
      const volgorde = ['Contactmoment 1', 'Contactmoment 2', 'Contactmoment 3', 'Contactmoment 4', 'Contactmoment 5'];
      alleMomenten.sort((a, b) => {
        const aIdx = volgorde.indexOf(a.naam);
        const bIdx = volgorde.indexOf(b.naam);
        return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
      });
      
      setSelectedMijnRapportage({
        ...rapportageData,
        contact_momenten: alleMomenten,
        gegenereerde_rapportage: alleRapportages.join('\n\n---\n\n'),
        _allRapportages: allRapportagesInGroup // Bewaar voor bewerken
      });
    } else {
      setSelectedMijnRapportage(rapportageData);
    }
  };

  // Bewerk een opgeslagen rapportage - laad antwoorden in formulier
  // Kan zowel een enkele rapportage als een gegroepeerde set rapportages ontvangen
  const bewerkRapportage = (rapportageData, allRapportagesInGroup = null) => {
    // Als we een groep hebben, voeg alle contactmomenten samen
    const rapportagesToMerge = allRapportagesInGroup || [rapportageData];
    
    // Sla op welke rapportage we bewerken (de primaire)
    setEditingRapportageId(rapportageData.id);
    
    // Laad de cliënt gegevens
    setSelectedClient(rapportageData.client_naam);
    
    // Zoek de cliënt data in alle locaties - match op volledige naam (voornaam + achternaam)
    let clientData = null;
    for (const locatie of Object.keys(CLIENTEN)) {
      const found = CLIENTEN[locatie].find(c => {
        const fullName = `${c.voornaam} ${c.achternaam}`;
        return fullName === rapportageData.client_naam || c.voornaam === rapportageData.client_naam;
      });
      if (found) {
        clientData = found;
        break;
      }
    }
    
    if (clientData) {
      setSelectedClientData(clientData);
    } else {
      // Als cliënt niet gevonden, maak een basis object
      setSelectedClientData({
        voornaam: rapportageData.client_naam,
        achternaam: '',
        locatie: rapportageData.client_locatie,
        ambulant: rapportageData.dienst_type === 'ambulant'
      });
    }
    
    // Laad dienst type - bij meerdere rapportages gebruik 'volledig' voor alle dagdelen
    const effectiefDienstType = rapportagesToMerge.length > 1 ? 'volledig' : rapportageData.dienst_type;
    setSelectedDienst(effectiefDienstType);
    
    // Laad ambulant uren indien van toepassing
    if (rapportageData.ambulant_uren) {
      // Zorg ervoor dat het een nummer is
      const uren = typeof rapportageData.ambulant_uren === 'number' 
        ? rapportageData.ambulant_uren 
        : parseInt(rapportageData.ambulant_uren) || 2;
      setAmbulantUren(uren);
    }
    
    // Verzamel ALLE contactmomenten uit alle rapportages in de groep
    const alleMomenten = {};
    const gerapporteerdeIndexen = {};
    
    for (const rap of rapportagesToMerge) {
      for (const moment of (rap.contact_momenten || [])) {
        const naam = moment.naam;
        if (!alleMomenten[naam]) {
          alleMomenten[naam] = { naam, antwoorden: {} };
        }
        // Merge antwoorden - nieuwere overschrijft lege
        for (const [key, value] of Object.entries(moment.antwoorden || {})) {
          if (value && value.trim()) {
            alleMomenten[naam].antwoorden[key] = value;
          }
        }
        // Check of dit moment al gerapporteerd is (heeft gegenereerde rapportage)
        if (rap.gegenereerde_rapportage && rap.gegenereerde_rapportage.includes(naam)) {
          // Zoek de index van dit moment
          const idx = Object.keys(alleMomenten).indexOf(naam);
          if (idx >= 0) {
            gerapporteerdeIndexen[idx] = rap.gegenereerde_rapportage;
          }
        }
      }
    }
    
    // Bepaal welke standaard momenten er moeten zijn
    // DYNAMISCH: Kijk of de cliënt doelen heeft gedefinieerd
    // Alle cliënten met doelen krijgen 2 contactmomenten (behalve Maisson: 1)
    
    const clientNaam = clientData ? `${clientData.voornaam} ${clientData.achternaam}` : '';
    const clientHeeftDoelen = clientNaam && getClientDoelenByName(clientNaam)?.length > 0;
    
    const isMaisson = clientData && (clientData.voornaam === 'Maison' || clientData.voornaam === 'Maisson');
    
    let standaardMomenten;
    if (isMaisson) {
      // Maisson heeft 1 contactmoment
      standaardMomenten = ['Contactmoment 1'];
    } else if (clientHeeftDoelen || clientData) {
      // Cliënten met doelen OF alle andere cliënten krijgen 2 contactmomenten
      standaardMomenten = ['Contactmoment 1', 'Contactmoment 2'];
    } else if (rapportagesToMerge.length > 1) {
      // Bij gegroepeerde rapportages: 3 contactmomenten
      standaardMomenten = ['Contactmoment 1', 'Contactmoment 2', 'Contactmoment 3'];
    } else {
      // Andere cliënten: afhankelijk van dienst type
      standaardMomenten = DIENST_TYPES.find(d => d.value === rapportageData.dienst_type)?.momenten || ['Contactmoment 1', 'Contactmoment 2', 'Contactmoment 3'];
    }
    
    // Voeg ontbrekende standaard momenten toe (zodat begeleider die nog kan invullen)
    for (const momentNaam of standaardMomenten) {
      if (!alleMomenten[momentNaam]) {
        alleMomenten[momentNaam] = { naam: momentNaam, antwoorden: {} };
      }
    }
    
    // Voor cliënten met doelen of Maisson: verwijder eventuele extra momenten (zoals oude Ochtend/Middag/Avond)
    // die niet in standaardMomenten zitten
    if (clientHeeftDoelen || isMaisson) {
      for (const key of Object.keys(alleMomenten)) {
        if (!standaardMomenten.includes(key)) {
          delete alleMomenten[key];
        }
      }
    }
    
    // Sorteer momenten in logische volgorde
    // Alle contactmomenten zijn nu sequentieel genummerd (1, 2, 3)
    const volgorde = ['Contactmoment 1', 'Contactmoment 2', 'Contactmoment 3', 'Contactmoment 4', 'Contactmoment 5'];
    const gesorteerd = Object.values(alleMomenten).sort((a, b) => {
      const aIdx = volgorde.indexOf(a.naam);
      const bIdx = volgorde.indexOf(b.naam);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });
    
    // Laad de contactmomenten
    const loadedMomenten = gesorteerd.map((moment, index) => ({
      id: index,
      naam: moment.naam,
      antwoorden: moment.antwoorden || {}
    }));
    setContactMomenten(loadedMomenten);
    
    // Markeer momenten die al gerapporteerd zijn
    const nieuweGerapporteerd = {};
    for (const rap of rapportagesToMerge) {
      if (rap.gegenereerde_rapportage) {
        for (let i = 0; i < loadedMomenten.length; i++) {
          const momentNaam = loadedMomenten[i].naam;
          // Check of dit moment in de gegenereerde rapportage staat
          if (rap.gegenereerde_rapportage.toLowerCase().includes(momentNaam.toLowerCase())) {
            nieuweGerapporteerd[i] = rap.gegenereerde_rapportage;
          }
        }
      }
    }
    setGerapporteerdeMomenten(nieuweGerapporteerd);
    
    // Sluit mijn rapportages sectie
    setShowMijnRapportages(false);
    setSelectedMijnRapportage(null);
    
    // Ga naar stap 4 (vragen formulier)
    setCurrentStep(3);
    setExpandedMoment(0);
  };

  // Bekijk een doel-rapportage - toon alle gerapporteerde doelen met hun teksten
  const bekijkDoelRapportage = (rapportageData) => {
    setSelectedMijnRapportage({
      ...rapportageData,
      _isDoelRapportage: true
    });
  };

  // Bewerk een doel-rapportage - laad de cliënt en ga naar het doel-formulier
  const bewerkDoelRapportage = (rapportageData) => {
    // Laad de cliënt gegevens
    setSelectedClient(rapportageData.client_naam);
    
    // Zoek de cliënt data
    let clientData = null;
    for (const locatie of Object.keys(CLIENTEN)) {
      const found = CLIENTEN[locatie].find(c => {
        const fullName = `${c.voornaam} ${c.achternaam}`;
        return fullName === rapportageData.client_naam;
      });
      if (found) {
        clientData = found;
        break;
      }
    }
    
    if (clientData) {
      setSelectedClientData(clientData);
    }
    
    // Laad de eerder gerapporteerde doelen uit de database
    if (rapportageData.doel_rapportages) {
      const gerapporteerd = {};
      for (const doel of rapportageData.doel_rapportages) {
        if (doel.gegenereerde_rapportage) {
          gerapporteerd[doel.doel_id] = {
            rapportage: doel.gegenereerde_rapportage,
            smiley: doel.score,
            datum: rapportageData.updated_at || rapportageData.datum,
            begeleider: rapportageData.begeleider_naam
          };
        }
      }
      setGerapporteerdeDoelen(gerapporteerd);
    }
    
    // Sla op welke rapportage we bewerken
    setEditingRapportageId(rapportageData.id);
    
    // Sluit mijn rapportages sectie
    setShowMijnRapportages(false);
    setSelectedMijnRapportage(null);
    
    // Ga naar stap 3 (doel-rapportage formulier)
    setSelectedDienst('doel_rapportage');
    setCurrentStep(3);
  };

  // Groepeer rapportages op cliënt + datum zodat dezelfde dag als 1 item wordt getoond
  const groupedRapportages = React.useMemo(() => {
    const groups = {};
    for (const rap of mijnRapportages) {
      const key = `${rap.client_naam}_${rap.datum}`;
      if (!groups[key]) {
        groups[key] = {
          client_naam: rap.client_naam,
          datum: rap.datum,
          rapportages: [],
          totaal_momenten: 0,
          hoogste_score: 0,
          is_doel_rapportage: rap.is_doel_rapportage || false,
          is_sjabloon_rapportage: rap._type === 'sjabloon_rapportage',
          _type: rap._type,
          _label: rap._label
        };
      }
      groups[key].rapportages.push(rap);
      
      // Tel contactmomenten/doelen afhankelijk van type
      if (rap._type === 'sjabloon_rapportage') {
        // Nieuwe sjabloon-flow
        groups[key].totaal_momenten += (rap.contactmomenten?.length || rap.contact_momenten?.length || 0);
        groups[key].is_sjabloon_rapportage = true;
        groups[key]._type = 'sjabloon_rapportage';
        groups[key]._label = 'Sjabloon-rapportage';
      } else if (rap.is_doel_rapportage) {
        // Oude doel-rapportage flow
        groups[key].totaal_momenten += (rap.doel_rapportages?.length || 0);
        groups[key].is_doel_rapportage = true;
      } else {
        // Normale rapportages
        groups[key].totaal_momenten += (rap.contact_momenten?.length || 0);
      }
      groups[key].hoogste_score = Math.max(groups[key].hoogste_score, rap.completeness_score || 0);
    }
    return Object.values(groups).sort((a, b) => b.datum.localeCompare(a.datum));
  }, [mijnRapportages]);

  // Render Mijn Rapportages sectie
  const renderMijnRapportages = () => {
    if (!showMijnRapportages) return null;

    return (
      <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 mt-4" data-testid="mijn-rapportages-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Mijn Rapportages ({groupedRapportages.length} dag{groupedRapportages.length !== 1 ? 'en' : ''})
          </h3>
          <button
            onClick={() => {
              setShowMijnRapportages(false);
              setSelectedMijnRapportage(null);
            }}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
          >
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </div>

        {loadingMijnRapportages ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
            <span className="ml-2 text-slate-400">Rapportages laden...</span>
          </div>
        ) : selectedMijnRapportage ? (
          // Detail view van een rapportage
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedMijnRapportage(null)}
                className="flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Terug naar overzicht
              </button>
              
              {/* Bewerk knop - verschilt voor doel-rapportages */}
              {selectedMijnRapportage._isDoelRapportage || selectedMijnRapportage.is_doel_rapportage ? (
                <button
                  onClick={() => bewerkDoelRapportage(selectedMijnRapportage)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors"
                  data-testid="bewerk-doel-rapportage-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Verder rapporteren op doelen
                </button>
              ) : (
                <button
                  onClick={() => bewerkRapportage(selectedMijnRapportage, selectedMijnRapportage._allRapportages)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors"
                  data-testid="bewerk-rapportage-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Bewerk & Tussentijds rapporteren
                </button>
              )}
            </div>
            
            {/* DOEL-RAPPORTAGE Detail View */}
            {(selectedMijnRapportage._isDoelRapportage || selectedMijnRapportage.is_doel_rapportage) ? (
              <div className="space-y-4">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-violet-500/30">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-slate-400">Cliënt</div>
                      <div className="text-white font-medium flex items-center gap-2">
                        {selectedMijnRapportage.client_naam}
                        <span className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-400 text-xs">Doel-rapportage</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Datum</div>
                      <div className="text-white font-medium">{selectedMijnRapportage.datum}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Doelen gerapporteerd</div>
                      <div className="text-white font-medium">
                        {selectedMijnRapportage.doel_rapportages?.filter(d => d.gegenereerde_rapportage).length || 0} doel(en)
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Toon alle gerapporteerde doelen */}
                <div className="space-y-3">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Target className="w-4 h-4 text-violet-400" />
                    Gerapporteerde Doelen
                  </h4>
                  {selectedMijnRapportage.doel_rapportages?.filter(d => d.gegenereerde_rapportage).map((doel, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-xl p-4 border border-emerald-500/30">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-emerald-400 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          {doel.doel_titel}
                        </h5>
                        {doel.score && (
                          <span className="text-lg">
                            {doel.score === 'goed' ? '😊' : doel.score === 'redelijk' ? '😐' : '😔'}
                          </span>
                        )}
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-200 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {doel.gegenereerde_rapportage}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(doel.gegenereerde_rapportage);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                          copied ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'Gekopieerd!' : 'Kopieer'}
                      </button>
                    </div>
                  ))}
                  
                  {/* Toon niet-gerapporteerde doelen */}
                  {selectedMijnRapportage.doel_rapportages?.filter(d => !d.gegenereerde_rapportage).length > 0 && (
                    <>
                      <h4 className="font-medium text-slate-400 mt-4">Nog niet gerapporteerd</h4>
                      {selectedMijnRapportage.doel_rapportages?.filter(d => !d.gegenereerde_rapportage).map((doel, idx) => (
                        <div key={idx} className="bg-slate-900/30 rounded-xl p-3 border border-slate-700/30">
                          <span className="text-slate-500">{doel.doel_titel}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* NORMALE RAPPORTAGE Detail View */
              <>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-600/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-slate-400">Cliënt</div>
                      <div className="text-white font-medium">{selectedMijnRapportage.client_naam}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Datum</div>
                      <div className="text-white font-medium">{selectedMijnRapportage.datum}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Dienst</div>
                      <div className="text-white font-medium">{selectedMijnRapportage.dienst_type}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">Contactmomenten</div>
                      <div className="text-white font-medium">
                        {selectedMijnRapportage.volledige_momenten || selectedMijnRapportage.totaal_contactmomenten} van {selectedMijnRapportage.totaal_momenten_dienst || selectedMijnRapportage.totaal_contactmomenten} ingevuld
                        {!selectedMijnRapportage.is_voltooid && (
                          <span className="ml-2 px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                            Lopend
                          </span>
                        )}
                        {selectedMijnRapportage.is_voltooid && (
                          <span className="ml-2 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                            Voltooid
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedMijnRapportage.gegenereerde_rapportage && (
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-600/50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white">Gegenereerde Rapportage</h4>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedMijnRapportage.gegenereerde_rapportage);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                          copied ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? 'Gekopieerd!' : 'Kopieer'}
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap text-slate-200 text-sm font-sans leading-relaxed max-h-64 overflow-y-auto">
                      {selectedMijnRapportage.gegenereerde_rapportage}
                    </pre>
                  </div>
                )}

                {/* Contactmomenten details - voor sjabloon flow */}
                <div className="space-y-4">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    Ingevulde Antwoorden per Contactmoment
                  </h4>
                  
                  {/* Check voor sjabloon-rapportages (contactmomenten array) */}
                  {selectedMijnRapportage.contactmomenten?.map((moment, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-xl p-4 border border-cyan-500/30">
                      <h5 className="font-medium text-cyan-400 mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-xs">
                          {idx + 1}
                        </span>
                        {moment.moment_naam}
                      </h5>
                      
                      {/* Stap 1: Sjabloon Vragen & Antwoorden */}
                      {moment.antwoorden && Object.values(moment.antwoorden).some(v => v?.trim()) && (
                        <div className="mb-4">
                          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Stap 1 - Sjabloon Vragen</div>
                          <div className="grid gap-2 text-sm">
                            {moment.antwoorden.aanleiding && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">1. Aanleiding:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.aanleiding}</span>
                              </div>
                            )}
                            {moment.antwoorden.doel && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">2. Doel:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.doel}</span>
                              </div>
                            )}
                            {moment.antwoorden.activiteit && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">3. Activiteiten:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.activiteit}</span>
                              </div>
                            )}
                            {moment.antwoorden.waarom && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">4. Waarom nodig:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.waarom}</span>
                              </div>
                            )}
                            {moment.antwoorden.reactie && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">5. Reactie cliënt:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.reactie}</span>
                              </div>
                            )}
                            {moment.antwoorden.resultaat && (
                              <div className="p-2 rounded-lg bg-slate-800/50">
                                <span className="text-amber-400 font-medium">6. Resultaat:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.resultaat}</span>
                              </div>
                            )}
                            {moment.antwoorden.bijzonderheden && (
                              <div className="p-2 rounded-lg bg-slate-800/50 border-l-2 border-amber-500">
                                <span className="text-amber-400 font-medium">Bijzonderheden:</span>
                                <span className="ml-2 text-slate-200">{moment.antwoorden.bijzonderheden}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Stap 1: Gegenereerde Rapportage */}
                      {moment.standaard_rapportage && (
                        <div className="mb-4">
                          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Stap 1 - Gegenereerde Rapportage</div>
                          <div className="bg-emerald-900/20 rounded-lg p-3 text-sm text-slate-200 whitespace-pre-wrap border border-emerald-500/30">
                            {moment.standaard_rapportage}
                          </div>
                        </div>
                      )}
                      
                      {/* Stap 2: Doel Informatie */}
                      {moment.doel_titel && (
                        <div className="mb-4">
                          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Stap 2 - Geselecteerd Doel</div>
                          <div className="p-3 rounded-lg bg-violet-900/20 border border-violet-500/30">
                            <div className="font-medium text-violet-400 mb-2">{moment.doel_titel}</div>
                            {moment.doel_vragen && (
                              <div className="grid gap-2 text-sm mt-2">
                                {(moment.doel_vragen.vraag1 || moment.doel_vragen.begeleiding) && (
                                  <div className="p-2 rounded-lg bg-slate-800/50">
                                    <span className="text-violet-400 font-medium">Begeleiding:</span>
                                    <span className="ml-2 text-slate-200">{moment.doel_vragen.vraag1 || moment.doel_vragen.begeleiding}</span>
                                  </div>
                                )}
                                {(moment.doel_vragen.vraag2 || moment.doel_vragen.reactie) && (
                                  <div className="p-2 rounded-lg bg-slate-800/50">
                                    <span className="text-violet-400 font-medium">Reactie:</span>
                                    <span className="ml-2 text-slate-200">{moment.doel_vragen.vraag2 || moment.doel_vragen.reactie}</span>
                                  </div>
                                )}
                                {(moment.doel_vragen.vraag3 || moment.doel_vragen.resultaat) && (
                                  <div className="p-2 rounded-lg bg-slate-800/50">
                                    <span className="text-violet-400 font-medium">Resultaat:</span>
                                    <span className="ml-2 text-slate-200">{moment.doel_vragen.vraag3 || moment.doel_vragen.resultaat}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Stap 2: Gegenereerde Doel Rapportage */}
                      {moment.doel_rapportage && (
                        <div>
                          <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Stap 2 - Gegenereerde Doel Rapportage</div>
                          <div className="bg-violet-900/20 rounded-lg p-3 text-sm text-slate-200 whitespace-pre-wrap border border-violet-500/30">
                            {moment.doel_rapportage}
                          </div>
                        </div>
                      )}
                      
                      {/* Als er geen data is voor dit moment */}
                      {!moment.antwoorden?.aanleiding && !moment.standaard_rapportage && !moment.doel_titel && (
                        <div className="text-slate-500 text-sm italic">Nog niet ingevuld</div>
                      )}
                    </div>
                  ))}
                  
                  {/* Fallback voor oude rapportages (contact_momenten array) */}
                  {!selectedMijnRapportage.contactmomenten && selectedMijnRapportage.contact_momenten?.map((moment, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-xl p-4 border border-slate-600/50">
                      <h5 className="font-medium text-violet-400 mb-3">{moment.naam}</h5>
                      <div className="grid gap-2 text-sm">
                        {Object.entries(moment.antwoorden || {}).map(([key, value]) => (
                          value?.trim() && (
                            <div key={key} className="p-2 rounded-lg bg-slate-800/50">
                              <span className="text-slate-400 capitalize">{key}:</span>
                              <span className="ml-2 text-slate-200">{value}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : groupedRapportages.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Je hebt nog geen rapportages gemaakt als {begeleiderNaam}.</p>
            <p className="text-sm mt-1">Start een nieuwe rapportage door hieronder door te gaan.</p>
          </div>
        ) : (
          // Gegroepeerde lijst - 1 kaart per cliënt/datum combinatie
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {groupedRapportages.map((group) => {
              // Check type rapportage
              const isDoelRapportage = group.is_doel_rapportage;
              const isSjabloonRapportage = group.is_sjabloon_rapportage || group._type === 'sjabloon_rapportage';
              
              // SJABLOON-RAPPORTAGE (Groep Zwaluw/Adelaars/Ambulant - nieuwe flow)
              if (isSjabloonRapportage) {
                const primaryRap = group.rapportages[0];
                const contactmomenten = primaryRap.contactmomenten || primaryRap.contact_momenten || [];
                const volledigeMomenten = contactmomenten.filter(m => m.standaard_rapportage || m.doel_rapportage);
                
                return (
                  <div
                    key={`${group.client_naam}_${group.datum}`}
                    className="p-4 rounded-xl bg-slate-900/50 border border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                          volledigeMomenten.length >= contactmomenten.length ? 'bg-emerald-500/20 text-emerald-400' : 'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-white flex items-center gap-2">
                            {group.client_naam}
                            <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs">
                              Sjabloon-rapportage
                            </span>
                            {volledigeMomenten.length >= contactmomenten.length ? (
                              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                                Compleet
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                                {volledigeMomenten.length}/{contactmomenten.length}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-400">
                            {contactmomenten.length} contactmoment{contactmomenten.length !== 1 ? 'en' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                          <div className="text-sm text-slate-300">{group.datum}</div>
                        </div>
                        <button
                          onClick={() => {
                            // Map contactmomenten naar het juiste veld
                            setSelectedMijnRapportage({
                              ...primaryRap,
                              contactmomenten: contactmomenten,
                              _type: 'sjabloon_rapportage'
                            });
                          }}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                          title="Bekijk sjabloon-rapportage"
                        >
                          <Eye className="w-4 h-4 text-slate-300" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              
              if (isDoelRapportage) {
                // DOEL-RAPPORTAGE: toon doelen in plaats van contactmomenten
                const alleDoelen = group.rapportages.flatMap(r => r.doel_rapportages || []);
                const gerapporteerdeDoelen = alleDoelen.filter(d => d.gegenereerde_rapportage);
                const totaalDoelen = alleDoelen.length;
                const totaalScore = totaalDoelen > 0 ? Math.round((gerapporteerdeDoelen.length / totaalDoelen) * 100) : 0;
                const alleVoltooid = gerapporteerdeDoelen.length >= 2; // Minimaal 2 doelen voor dag compleet
                
                const primaryRap = group.rapportages[0];
                
                return (
                  <div
                    key={`${group.client_naam}_${group.datum}`}
                    className="p-4 rounded-xl bg-slate-900/50 border border-violet-500/30 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                          gerapporteerdeDoelen.length >= 2 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-violet-500/20 text-violet-400'
                        }`}>
                          <Target className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-white flex items-center gap-2">
                            {group.client_naam}
                            <span className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-400 text-xs">
                              Doel-rapportage
                            </span>
                            {alleVoltooid ? (
                              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                                Dag compleet
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                                Lopend
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-400">
                            {gerapporteerdeDoelen.length} doel{gerapporteerdeDoelen.length !== 1 ? 'en' : ''} gerapporteerd
                            {gerapporteerdeDoelen.length > 0 && (
                              <span className="ml-1">
                                ({gerapporteerdeDoelen.map(d => d.doel_titel).join(', ')})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                          <div className="text-sm text-slate-300">{group.datum}</div>
                        </div>
                        <button
                          onClick={() => bekijkDoelRapportage(primaryRap)}
                          className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                          title="Bekijk doel-rapportages"
                        >
                          <Eye className="w-4 h-4 text-slate-300" />
                        </button>
                        <button
                          onClick={() => bewerkDoelRapportage(primaryRap)}
                          className="p-2 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 transition-colors"
                          title="Verder rapporteren op doelen"
                        >
                          <RefreshCw className="w-4 h-4 text-violet-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // NORMALE RAPPORTAGE: originele logica
              // Als er 1 rapportage is, toon gewoon die rapportage
              // Als er meerdere zijn, toon samengevoegd overzicht
              const alleContactMomenten = group.rapportages.flatMap(r => r.contact_momenten || []);
              const uniqueMomenten = [...new Set(alleContactMomenten.map(m => m.naam))];
              
              // Bereken hoeveel momenten VOLLEDIG zijn ingevuld (7/7 vragen)
              // Dit is belangrijker dan de gemiddelde completeness score
              const volledigIngevuldeMomenten = alleContactMomenten.filter(m => {
                const antwoorden = m.antwoorden || {};
                const ingevuld = Object.values(antwoorden).filter(a => a && String(a).trim().length > 0).length;
                return ingevuld >= 7;
              });
              const uniqueVolledigeMomenten = [...new Set(volledigIngevuldeMomenten.map(m => m.naam))];
              
              // Bereken score op basis van volledig ingevulde momenten vs totaal verwacht
              const primaryRap = group.rapportages.reduce((best, current) => 
                (current.contact_momenten?.length || 0) > (best.contact_momenten?.length || 0) ? current : best
              );
              const dienstType = primaryRap.dienst_type;
              const totaalVerwacht = primaryRap.totaal_momenten_dienst || 
                (dienstType === 'volledig' ? 3 : dienstType === 'dag' ? 2 : 1);
              
              // Score = percentage volledig ingevulde momenten
              const totaalScore = Math.round((uniqueVolledigeMomenten.length / totaalVerwacht) * 100);
              const alleVoltooid = uniqueVolledigeMomenten.length >= totaalVerwacht;
              
              return (
                <div
                  key={`${group.client_naam}_${group.datum}`}
                  className="p-4 rounded-xl bg-slate-900/50 border border-slate-600/50 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                        totaalScore >= 70 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {totaalScore}%
                      </div>
                      <div>
                        <div className="font-medium text-white flex items-center gap-2">
                          {group.client_naam}
                          {!alleVoltooid ? (
                            <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                              Lopend
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                              Voltooid
                            </span>
                          )}
                          {group.rapportages.length > 1 && (
                            <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 text-xs">
                              {group.rapportages.length} diensten
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-400">
                          {uniqueVolledigeMomenten.length} van {totaalVerwacht} volledig ingevuld: {uniqueMomenten.join(', ')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-2">
                        <div className="text-sm text-slate-300">{group.datum}</div>
                      </div>
                      <button
                        onClick={() => bekijkMijnRapportage(primaryRap, group.rapportages)}
                        className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                        title="Bekijk rapportage"
                      >
                        <Eye className="w-4 h-4 text-slate-300" />
                      </button>
                      <button
                        onClick={() => bewerkRapportage(primaryRap, group.rapportages)}
                        className="p-2 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 transition-colors"
                        title="Bewerk & tussentijds rapporteren"
                      >
                        <RefreshCw className="w-4 h-4 text-violet-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderStepIndicator = () => {
    // Alleen voor Groep Zwaluw/Adelaars (isSjabloonMetDoelkoppeling): verberg Stap 3 (Dienst) volledig
    // AMBULANT cliënten (isDoelRapportageClient) krijgen WEL de dienst stap (uren selectie)
    const skipDienstStep = isSjabloonMetDoelkoppeling;
    
    const baseSteps = [
      { num: 0, label: 'Begeleider' },
      { num: 1, label: 'Cliënt' },
      { num: 2, label: isDoelRapportageClient ? 'Uren' : 'Dienst' },
      { num: 3, label: isDoelRapportageClient ? 'Doelen' : 'Vragen' },
      { num: 4, label: 'Rapportage' }
    ];
    
    // Filter Dienst stap uit voor doel-rapportage cliënten en Yassin Houba
    const steps = skipDienstStep 
      ? baseSteps.filter(s => s.num !== 2)
      : baseSteps;
    
    // Map currentStep naar weergave-index
    const getDisplayStep = (actualStep) => {
      if (!skipDienstStep) return actualStep;
      // Stap 2 is overgeslagen, dus 3 wordt weergegeven als 2, 4 als 3
      if (actualStep >= 3) return actualStep - 1;
      return actualStep;
    };
    
    const displayCurrentStep = getDisplayStep(currentStep);
    
    return (
      <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 flex-wrap">
        {steps.map((step, i) => {
          const displayIndex = skipDienstStep && step.num >= 3 ? step.num - 1 : step.num;
          const isActive = currentStep === step.num;
          const isCompleted = currentStep > step.num;
          
          return (
            <React.Fragment key={step.num}>
              <button
                onClick={() => {
                  if (step.num === 0) setCurrentStep(0);
                  else if (step.num === 1 && begeleiderNaam) setCurrentStep(1);
                  else if (step.num === 2 && selectedClient && !skipDienstStep) setCurrentStep(2);
                  else if (step.num === 3 && (selectedDienst || selectedClientData?.ambulant || skipDienstStep || isDoelRapportageClient)) setCurrentStep(3);
                  else if (step.num === 4 && rapportage) setCurrentStep(4);
                }}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-violet-500 text-white'
                    : isCompleted
                      ? 'bg-emerald-500/20 text-emerald-400 cursor-pointer'
                      : 'bg-slate-700 text-slate-400'
                }`}
              >
                <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                  isCompleted ? 'bg-emerald-500 text-white' : ''
                }`}>
                  {isCompleted ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : displayIndex + 1}
                </span>
                <span className="hidden sm:inline text-sm font-medium">{step.label}</span>
              </button>
              {i < steps.length - 1 && <div className={`w-4 sm:w-8 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-600'}`} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Stap 0: Begeleider naam invoeren
  const renderBegeleiderSelection = () => (
    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50" data-testid="begeleider-selection-step">
      <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
        <UserCircle className="w-5 h-5 text-cyan-400" />
        Stap 1: Wie ben jij?
      </h2>
      <p className="text-sm text-slate-400 mb-6">
        Selecteer of typ je naam zodat we weten wie deze rapportage maakt.
      </p>
      
      {/* Begeleiders sectie */}
      <div className="mb-4">
        <div className="text-sm text-slate-400 mb-3">Begeleiders:</div>
        <div className="flex flex-wrap gap-2" data-testid="begeleider-buttons">
          {BEGELEIDERS.map(naam => (
            <button
              key={naam}
              onClick={() => {
                setBegeleiderNaam(naam);
                // Laad rapportages voor deze begeleider
                fetchMijnRapportages(naam);
              }}
              data-testid={`begeleider-btn-${naam.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                begeleiderNaam === naam
                  ? 'bg-cyan-500 text-white ring-2 ring-cyan-500/50'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {naam}
            </button>
          ))}
        </div>
      </div>
      
      {/* Stagiaires sectie */}
      <div className="mb-6">
        <div className="text-sm text-slate-400 mb-3">Stagiaires:</div>
        <div className="flex flex-wrap gap-2" data-testid="stagiaire-buttons">
          {STAGIAIRES.map(naam => (
            <button
              key={naam}
              onClick={() => {
                setBegeleiderNaam(naam);
                fetchMijnRapportages(naam);
              }}
              data-testid={`stagiaire-btn-${naam.toLowerCase()}`}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                begeleiderNaam === naam
                  ? 'bg-purple-500 text-white ring-2 ring-purple-500/50'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {naam}
            </button>
          ))}
        </div>
      </div>
      
      {/* Of handmatig invoeren */}
      <div className="mb-6">
        <div className="text-sm text-slate-400 mb-2">Of typ je naam:</div>
        <input
          type="text"
          value={begeleiderNaam}
          onChange={(e) => setBegeleiderNaam(e.target.value)}
          onBlur={(e) => {
            // Laad rapportages wanneer naam is ingevuld
            if (e.target.value.trim()) {
              fetchMijnRapportages(e.target.value.trim());
            }
          }}
          placeholder="Je naam..."
          data-testid="begeleider-naam-input"
          className="w-full p-4 rounded-xl bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
        />
      </div>

      {/* Mijn Rapportages knop - alleen tonen als begeleider is geselecteerd */}
      {begeleiderNaam.trim() && (
        <>
          {/* Datum kiezer voor inhaalrapportages */}
          <div className="mb-4 p-4 bg-slate-900/50 rounded-xl border border-slate-600/50">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-slate-300">Rapportage datum</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Standaard vandaag. Kies een andere datum om in te halen of meerdere keren voor dezelfde cliënt te rapporteren.
            </p>
            <input
              type="date"
              value={rapportageDatum}
              onChange={(e) => setRapportageDatum(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              data-testid="rapportage-datum-input"
              className="w-full p-3 rounded-xl bg-slate-800 border border-slate-600 text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            {rapportageDatum !== new Date().toISOString().split('T')[0] && (
              <div className="mt-2 flex items-center gap-2 text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Je rapporteert voor: {new Date(rapportageDatum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setShowMijnRapportages(!showMijnRapportages);
              if (!showMijnRapportages) {
                fetchMijnRapportages(begeleiderNaam);
              }
            }}
            data-testid="mijn-rapportages-btn"
            className={`w-full mb-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
              showMijnRapportages
                ? 'bg-emerald-500/20 text-emerald-400 border-2 border-emerald-500/50'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700 border-2 border-slate-600/50'
            }`}
          >
            <FileText className="w-5 h-5" />
            {showMijnRapportages ? 'Verberg mijn rapportages' : `Bekijk mijn rapportages`}
            {groupedRapportages.length > 0 && !showMijnRapportages && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold">
                {groupedRapportages.length}
              </span>
            )}
          </button>
        </>
      )}

      {/* Mijn Rapportages sectie */}
      {renderMijnRapportages()}
      
      {/* Doorgaan knop */}
      <button
        onClick={resetVoorNieuweRapportage}
        disabled={!begeleiderNaam.trim()}
        data-testid="begeleider-continue-btn"
        className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
          begeleiderNaam.trim()
            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
            : 'bg-slate-700 text-slate-400 cursor-not-allowed'
        }`}
      >
        {begeleiderNaam.trim() ? `Nieuwe rapportage als ${begeleiderNaam}` : 'Vul je naam in'}
      </button>
    </div>
  );

  const renderClientSelection = () => (
    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50" data-testid="client-selection-step">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <User className="w-5 h-5 text-violet-400" />
          Stap 2: Selecteer cliënt
        </h2>
        {isCombinedAccount && (
          <div className="flex gap-2" data-testid="location-filters">
            {['all', 'groep-zwaluw', 'groep-adelaars'].map(filter => (
              <button
                key={filter}
                onClick={() => setLocationFilter(filter)}
                data-testid={`location-filter-${filter}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  locationFilter === filter
                    ? filter === 'all' ? 'bg-violet-500 text-white'
                      : filter === 'groep-zwaluw' ? 'bg-blue-500 text-white' : 'bg-orange-500 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {filter === 'all' ? 'Alle' : filter === 'groep-zwaluw' ? 'Zwaluw' : 'Adelaars'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Compacte datumkiezer - ook op deze stap bereikbaar, zodat de datum voor een
          nieuwe rapportage (bijv. na "Andere cliënt"/"Volgende cliënt") aangepast kan
          worden zonder terug te hoeven naar stap 1. NIET VERWIJDEREN (support-fix 27 sep 2026). */}
      <div className="mb-4 p-3 bg-slate-900/50 rounded-xl border border-slate-600/50 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-300 shrink-0">
          <Calendar className="w-4 h-4 text-amber-400" />
          Rapportage datum
        </div>
        <input
          type="date"
          value={rapportageDatum}
          onChange={(e) => setRapportageDatum(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          data-testid="rapportage-datum-input-stap2"
          className="flex-1 min-w-[10rem] p-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />
        {rapportageDatum !== new Date().toISOString().split('T')[0] && (
          <div className="flex items-center gap-1.5 text-amber-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Inhaalrapportage</span>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(groupedClients).map(([groupKey, group]) => (
          <div key={groupKey}>
            <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${
              group.color === 'blue' ? 'border-blue-500/30' :
              group.color === 'orange' ? 'border-orange-500/30' : 'border-purple-500/30'
            }`}>
              <div className={`w-3 h-3 rounded-full ${
                group.color === 'blue' ? 'bg-blue-500' :
                group.color === 'orange' ? 'bg-orange-500' : 'bg-purple-500'
              }`} />
              <span className={`text-sm font-semibold ${
                group.color === 'blue' ? 'text-blue-400' :
                group.color === 'orange' ? 'text-orange-400' : 'text-purple-400'
              }`}>{group.label}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`client-group-${groupKey}`}>
              {group.clients.map(client => {
                const fullName = `${client.voornaam} ${client.achternaam}`;
                return (
                <button
                  key={`${groupKey}-${client.voornaam}-${client.achternaam}`}
                  onClick={() => {
                    setSelectedClient(fullName);
                    setSelectedClientData(client);
                    setHervatMelding('');
                    setPendingHervatting(null);
                    // CRITICAL: Reset ALLE rapportage states bij wisselen van cliënt
                    // Dit voorkomt dat data van de vorige cliënt wordt overgenomen
                    setBijzonderhedenPerMoment({});
                    setSelectedDoelen([]);
                    setDoelRapportages({});
                    setEditingSjabloonRapportageId(null); // FIX: vergeet de vorige opgeslagen rapportage (anders wordt die overschreven)
                    setEditingRapportageId(null);
                    setGeselecteerdDoelPerMoment({}); // TOEGEVOEGD: Reset doelselectie
                    setDoelVragenPerMoment({}); // TOEGEVOEGD: Reset doel vragen
                    setGegenereerdeDoelRapportages({}); // TOEGEVOEGD: Reset gegenereerde rapportages
                    setGerapporteerdeMomenten({}); // TOEGEVOEGD: Reset gerapporteerde momenten
                    setLoadingDoelRapportage({}); // TOEGEVOEGD: Reset loading states
                    
                    // MAISSON: Speciaal geval - slechts 1 contactmoment met 1 doel
                    const isMaisson = (client.voornaam === 'Maison' || client.voornaam === 'Maisson') && client.achternaam === 'Salhuteru';
                    
                    if (isMaisson) {
                      setAmbulantUren(1);
                      setSelectedDienst('groep_vast_1');
                      
                      // Direct 1 contactmoment aanmaken voor Maisson
                      const newMomenten = [
                        {
                          id: 0,
                          naam: 'Contactmoment 1',
                          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
                            acc[vraag.id] = '';
                            return acc;
                          }, {})
                        }
                      ];
                      setContactMomenten(newMomenten);
                      setExpandedMoment(0);
                      setCurrentStep(3);
                    } else {
                      // ALLE ANDERE CLIËNTEN: 2 contactmomenten met doelrapportage
                      // Dit geldt voor Groep Zwaluw, Groep Adelaars EN Ambulant
                      setAmbulantUren(2);
                      setSelectedDienst('groep_vast_2');
                      
                      // Direct 2 contactmomenten aanmaken
                      const newMomenten = [
                        {
                          id: 0,
                          naam: 'Contactmoment 1',
                          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
                            acc[vraag.id] = '';
                            return acc;
                          }, {})
                        },
                        {
                          id: 1,
                          naam: 'Contactmoment 2',
                          antwoorden: PGB_VRAGEN.reduce((acc, vraag) => {
                            acc[vraag.id] = '';
                            return acc;
                          }, {})
                        }
                      ];
                      setContactMomenten(newMomenten);
                      setExpandedMoment(0);
                      setCurrentStep(3); // Direct naar vragen (skip dienst selectie)
                    }
                    // Eerder vandaag opgeslagen antwoorden voor deze cliënt weer laden
                    hervatRapportageVanVandaag(fullName, isMaisson ? 1 : 2);
                  }}
                  data-testid={`client-btn-${client.voornaam.toLowerCase().replace(/\s+/g, '-')}-${client.achternaam.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedClient === fullName
                      ? 'border-violet-500 bg-violet-500/20 text-white ring-2 ring-violet-500/50'
                      : 'border-slate-600/30 bg-slate-800/50 text-slate-300 hover:border-slate-500/50'
                  }`}
                >
                  <div className="font-medium">{client.voornaam}</div>
                  <div className="text-xs text-slate-400">{client.achternaam}</div>
                  {client.ambulant && (
                    <div className="mt-1 px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs inline-block">
                      Ambulant
                    </div>
                  )}
                </button>
              )})}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render voor ambulante uren selectie
  const renderAmbulantUrenSelection = () => {
    // VEILIGHEID: Yassin Houba mag NOOIT dit scherm zien - direct naar stap 3
    if (selectedClientData?.voornaam === 'Yassin' && selectedClientData?.achternaam?.toLowerCase() === 'houba') {
      if (currentStep === 2) {
        setAmbulantUren(2);
        setSelectedDienst('groep_vast_2');
        const newMomenten = [
          { id: 0, naam: 'Contactmoment 1', antwoorden: PGB_VRAGEN.reduce((acc, vraag) => { acc[vraag.id] = ''; return acc; }, {}) },
          { id: 1, naam: 'Contactmoment 2', antwoorden: PGB_VRAGEN.reduce((acc, vraag) => { acc[vraag.id] = ''; return acc; }, {}) }
        ];
        setContactMomenten(newMomenten);
        setExpandedMoment(0);
        setCurrentStep(3);
      }
      return null;
    }
    
    const aantalContactMomenten = getAmbulantContactMomenten(ambulantUren);
    
    return (
      <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50" data-testid="ambulant-uren-step">
        <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Timer className="w-5 h-5 text-purple-400" />
          Stap 3: Hoeveel uur was je bij {selectedClient}?
        </h2>
        <p className="text-sm text-slate-400 mb-6">
          Bij ambulante begeleiding bepaalt het aantal uren hoeveel contactmomenten je moet rapporteren.
        </p>
        
        {/* Uren slider */}
        <div className="bg-slate-900/50 rounded-2xl p-6 border border-purple-500/30">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-300">Aantal uren begeleiding:</span>
            <span className="text-4xl font-bold text-purple-400" data-testid="ambulant-uren-display">{ambulantUren} uur</span>
          </div>
          
          <input
            type="range"
            min="1"
            max="8"
            value={ambulantUren}
            onChange={(e) => setAmbulantUren(parseInt(e.target.value))}
            data-testid="ambulant-uren-slider"
            className="w-full h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          
          <div className="flex justify-between text-xs text-slate-500 mt-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(u => (
              <span key={u} className={ambulantUren === u ? 'text-purple-400 font-bold' : ''}>{u}u</span>
            ))}
          </div>
          
          {/* Contactmomenten preview */}
          <div className="mt-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400">Dit resulteert in:</div>
                <div className="text-2xl font-bold text-purple-400">
                  {aantalContactMomenten} contactmoment{aantalContactMomenten > 1 ? 'en' : ''}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: aantalContactMomenten }, (_, i) => (
                  <div key={i} className="w-8 h-8 rounded-lg bg-purple-500/30 flex items-center justify-center text-purple-300 text-sm font-bold">
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Bevestig knop */}
        <button
          onClick={() => setCurrentStep(3)}
          data-testid="ambulant-continue-btn"
          className="w-full mt-6 py-4 rounded-xl bg-purple-500 text-white font-semibold text-lg hover:bg-purple-600 transition-colors"
        >
          Doorgaan met {aantalContactMomenten} contactmoment{aantalContactMomenten > 1 ? 'en' : ''}
        </button>
      </div>
    );
  };

  const renderDienstSelection = () => {
    // VEILIGHEID: Yassin Houba mag NOOIT dit scherm zien
    if (selectedClientData?.voornaam === 'Yassin' && selectedClientData?.achternaam?.toLowerCase() === 'houba') {
      // Forceer naar stap 3 met 2 contactmomenten
      if (currentStep === 2) {
        setAmbulantUren(2);
        setSelectedDienst('groep_vast_2');
        const newMomenten = [
          { id: 0, naam: 'Contactmoment 1', antwoorden: PGB_VRAGEN.reduce((acc, vraag) => { acc[vraag.id] = ''; return acc; }, {}) },
          { id: 1, naam: 'Contactmoment 2', antwoorden: PGB_VRAGEN.reduce((acc, vraag) => { acc[vraag.id] = ''; return acc; }, {}) }
        ];
        setContactMomenten(newMomenten);
        setExpandedMoment(0);
        setCurrentStep(3);
      }
      return null; // Render niets
    }
    
    return (
    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50" data-testid="dienst-selection-step">
      <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-400" />
        Stap 3: Selecteer jouw dienst voor {selectedClient}
      </h2>
      <p className="text-sm text-slate-400 mb-6">
        Kies de dienst die je hebt gedraaid. Het aantal contactmomenten bepaalt hoeveel keer je de vragen moet beantwoorden.
      </p>
      
      <div className="space-y-4" data-testid="dienst-options">
        {DIENST_TYPES.map(dienst => (
          <button
            key={dienst.value}
            onClick={() => {
              setSelectedDienst(dienst.value);
              setCurrentStep(3);
            }}
            data-testid={`dienst-btn-${dienst.value}`}
            className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
              selectedDienst === dienst.value
                ? 'border-amber-500 bg-amber-500/20 text-white ring-2 ring-amber-500/50'
                : 'border-slate-600/30 bg-slate-800/50 text-slate-300 hover:border-amber-500/50 hover:bg-slate-700/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold text-white">{dienst.label}</div>
                <div className="text-sm text-slate-400 mt-1">{dienst.beschrijving}</div>
              </div>
              <div className={`flex flex-col items-center justify-center w-20 h-20 rounded-2xl ${
                dienst.contactMomenten === 1 ? 'bg-emerald-500/20 border-2 border-emerald-500/50' :
                dienst.contactMomenten === 2 ? 'bg-blue-500/20 border-2 border-blue-500/50' :
                'bg-violet-500/20 border-2 border-violet-500/50'
              }`}>
                <span className={`text-3xl font-bold ${
                  dienst.contactMomenten === 1 ? 'text-emerald-400' :
                  dienst.contactMomenten === 2 ? 'text-blue-400' :
                  'text-violet-400'
                }`}>
                  {dienst.contactMomenten}
                </span>
                <span className="text-xs text-slate-400">
                  moment{dienst.contactMomenten > 1 ? 'en' : ''}
                </span>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {dienst.momenten.map((moment, i) => (
                <span key={`${dienst.value}-moment-${moment}`} className="px-3 py-1 rounded-lg bg-slate-700/50 text-sm text-slate-300">
                  {moment}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
  };

  // ===== YASSIN HOUBA DOEL-RAPPORTAGE SECTIE =====
  
  // Toggle doel selectie (max 2 doelen)
  const toggleDoelSelectie = (doelId) => {
    setSelectedDoelen(prev => {
      if (prev.includes(doelId)) {
        // Verwijder doel
        const newDoelen = prev.filter(id => id !== doelId);
        // Verwijder ook de rapportage data
        setDoelRapportages(current => {
          const updated = { ...current };
          delete updated[doelId];
          return updated;
        });
        return newDoelen;
      } else if (prev.length < 2) {
        // Voeg doel toe (max 2)
        return [...prev, doelId];
      }
      return prev;
    });
  };
  
  // Update doel rapportage (smiley, toelichting, of vraag-antwoord)
  const updateDoelRapportage = (doelId, field, value, vraagId = null) => {
    setDoelRapportages(prev => {
      const currentDoel = prev[doelId] || { smiley: '', antwoorden: {} };
      
      if (vraagId) {
        // Update een specifiek vraag-antwoord
        return {
          ...prev,
          [doelId]: {
            ...currentDoel,
            antwoorden: {
              ...currentDoel.antwoorden,
              [vraagId]: value
            }
          }
        };
      } else {
        // Update smiley of toelichting
        return {
          ...prev,
          [doelId]: {
            ...currentDoel,
            [field]: value
          }
        };
      }
    });
  };
  
  // Check of een specifiek doel volledig is ingevuld (smiley + alle 4 PGB-vragen)
  const isDoelCompleet = (doelId) => {
    const rapportage = doelRapportages[doelId];
    if (!rapportage?.smiley) return false;
    
    const clientDoelen = getClientDoelen();
    const doel = clientDoelen.find(d => d.id === doelId);
    const pgbVragen = doel?.vragen.filter(v => v.type === 'pgb') || [];
    const antwoorden = rapportage.antwoorden || {};
    
    return pgbVragen.every(vraag => antwoorden[vraag.id]?.trim());
  };
  
  // Check of minimaal 1 doel compleet is (voor tussentijds opslaan)
  const isDoelRapportageCompleet = () => {
    if (selectedDoelen.length === 0) return false;
    // Check of minstens 1 geselecteerd doel compleet is (voor tussentijds)
    return selectedDoelen.some(doelId => isDoelCompleet(doelId));
  };
  
  // Haal de complete doelen op (voor de genereer functie)
  const getCompleteDoelen = () => {
    return selectedDoelen.filter(doelId => isDoelCompleet(doelId));
  };
  
  // Tel totaal aantal gerapporteerde doelen voor vandaag (cumulatief)
  const getTotaalGerapporteerdeDoelen = () => {
    const alGerapporteerd = Object.keys(gerapporteerdeDoelen).length;
    const nuCompleet = getCompleteDoelen().length;
    return alGerapporteerd + nuCompleet;
  };
  
  // Check of de dag als compleet kan worden gemarkeerd (minimaal 2 doelen totaal, behalve Maisson: 1 doel)
  const isDagCompleet = () => {
    const minDoelen = isMaisonSalhuteru ? 1 : 2;
    return getTotaalGerapporteerdeDoelen() >= minDoelen;
  };
  
  // Bereken voortgang per doel
  const getDoelProgress = (doelId) => {
    const rapportage = doelRapportages[doelId];
    if (!rapportage) return 0;
    
    const clientDoelen = getClientDoelen();
    const doel = clientDoelen.find(d => d.id === doelId);
    if (!doel) return 0;
    
    let completed = 0;
    const pgbVragen = doel.vragen.filter(v => v.type === 'pgb');
    const observatieVragen = doel.vragen.filter(v => v.type === 'observatie');
    
    // Smiley telt mee
    if (rapportage.smiley) completed += 20;
    
    // PGB vragen zijn belangrijker (60% van totaal)
    const antwoorden = rapportage.antwoorden || {};
    pgbVragen.forEach(vraag => {
      if (antwoorden[vraag.id]?.trim()) completed += 20;
    });
    
    // Observatie vragen (20% van totaal)
    let observatieCompleted = 0;
    observatieVragen.forEach(vraag => {
      if (antwoorden[vraag.id]?.trim()) observatieCompleted++;
    });
    if (observatieVragen.length > 0) {
      completed += Math.round((observatieCompleted / observatieVragen.length) * 20);
    }
    
    return Math.min(completed, 100);
  };
  
  // Genereer doel-rapportage via AI - APART per doel
  const generateDoelRapportage = async () => {
    const completeDoelen = getCompleteDoelen();
    if (completeDoelen.length === 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const now = new Date().toISOString();
      
      // Haal client info dynamisch op
      const clientInfo = getDoelRapportageClientInfo();
      const clientDoelen = getClientDoelen();
      
      // Genereer voor ELK doel APART een rapportage
      const doelRapportagesMetAITekst = [];
      let laatsteRapportage = '';
      
      for (const doelId of completeDoelen) {
        const doel = clientDoelen.find(d => d.id === doelId);
        const rapportage = doelRapportages[doelId];
        const smileyInfo = SMILEY_OPTIES.find(s => s.value === rapportage.smiley);
        
        // Verzamel alle beantwoorde vragen
        const beantwoordeVragen = doel.vragen
          .filter(v => rapportage.antwoorden?.[v.id]?.trim())
          .map(v => ({
            vraag: v.vraag,
            antwoord: rapportage.antwoorden[v.id]
          }));
        
        // Bouw input voor ALLEEN dit ene doel
        const inputText = `
BEGELEIDER: ${begeleiderNaam}
CLIËNT: ${clientInfo.naam}
LOCATIE: ${clientInfo.locatie}

RAPPORTAGE OP ZORGDOEL:

=== DOEL 1: ${doel.titel} (${doel.categorie}) ===
Doelomschrijving: ${doel.beschrijving}
Score: ${smileyInfo?.emoji || ''} ${smileyInfo?.label || rapportage.smiley}

Observaties van de begeleider:
${beantwoordeVragen.map(qa => `- ${qa.vraag}\n  Antwoord: ${qa.antwoord}`).join('\n')}
        `.trim();
        
        // Genereer rapportage voor DIT doel
        const response = await fetch(`${API_URL}/api/voice/generate-rapportage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            client_voornaam: clientInfo.voornaam,
            locatie: clientInfo.locatie,
            transcriptie: inputText,
            dienst: selectedDienst || 'tussentijds',
            begeleider: begeleiderNaam,
            report_type: 'doel_rapportage'
          })
        });
        
        if (!response.ok) throw new Error(`Fout bij genereren rapportage voor doel ${doel.titel}`);
        
        const data = await response.json();
        const generatedRapportage = data.rapportage || data.report || '';
        laatsteRapportage = generatedRapportage;
        
        // Sla dit doel op met zijn EIGEN rapportage
        doelRapportagesMetAITekst.push({
          doel_id: doelId,
          doel_titel: doel.titel,
          doel_beschrijving: doel.beschrijving,
          categorie: doel.categorie,
          smiley: rapportage.smiley,
          antwoorden: rapportage.antwoorden || {},
          gerapporteerd_op: now,
          gegenereerde_rapportage: generatedRapportage  // EIGEN rapportage per doel!
        });
      }
      
      // Toon de laatste gegenereerde rapportage in de UI
      setRapportage(laatsteRapportage);
      
      // Sla de gerapporteerde doelen op met timestamp
      const nieuweGerapporteerd = { ...gerapporteerdeDoelen };
      doelRapportagesMetAITekst.forEach(doelData => {
        nieuweGerapporteerd[doelData.doel_id] = {
          rapportage: doelData.gegenereerde_rapportage,
          datum: now,
          begeleider: begeleiderNaam,
          smiley: doelData.smiley
        };
      });
      setGerapporteerdeDoelen(nieuweGerapporteerd);
      
      // Sla op in localStorage
      const gerapporteerdeKey = getDoelGerapporteerdeKey();
      localStorage.setItem(gerapporteerdeKey, JSON.stringify(nieuweGerapporteerd));
      
      // Bepaal locatie key voor database - dynamisch op basis van clientLocatie
      const locatieKey = clientLocatie || 'onbekend';
      
      // Sla ook op in de database voor Rapportage Controle
      try {
        await fetch(`${API_URL}/api/rapportage-sjablonen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            client_naam: clientInfo.naam,
            client_locatie: locatieKey,
            dienst_type: 'doel_rapportage',
            contact_momenten: [],
            gegenereerde_rapportage: null,  // Geen gecombineerde rapportage
            begeleider_naam_input: begeleiderNaam,
            is_doel_rapportage: true,
            doel_rapportages: doelRapportagesMetAITekst,  // Elk doel heeft zijn EIGEN rapportage
            rapportage_datum: rapportageDatum // Datum waarvoor de rapportage is
          })
        });
      } catch (saveErr) {
        console.error('Fout bij opslaan in database:', saveErr);
      }
      
      // Verwijder de gerapporteerde doelen uit de huidige selectie
      setSelectedDoelen(prev => prev.filter(id => !completeDoelen.includes(id)));
      
      // Verwijder de antwoorden van gerapporteerde doelen
      const nieuweRapportages = { ...doelRapportages };
      completeDoelen.forEach(doelId => {
        delete nieuweRapportages[doelId];
      });
      setDoelRapportages(nieuweRapportages);
      
      setCurrentStep(4);
      
    } catch (err) {
      console.error('Fout bij genereren doel-rapportage:', err);
      setError('Er ging iets mis bij het genereren van de rapportage. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };
  
  // Render doel-rapportage formulier (voor Yassin Houba en Melvin Hartman)
  const renderDoelRapportageFormulier = () => {
    // Bereken totale voortgang
    const clientDoelen = getClientDoelen();
    const clientInfo = getDoelRapportageClientInfo();
    const totalProgress = selectedDoelen.length === 0 ? 0 : 
      Math.round(selectedDoelen.reduce((sum, doelId) => sum + getDoelProgress(doelId), 0) / 2);
    
    return (
      <div className="space-y-4" data-testid="doel-rapportage-formulier">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500/20 to-emerald-500/20 rounded-2xl p-4 border border-violet-500/30">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-violet-400" />
                Rapportage op Zorgdoelen - {clientInfo.naam}
              </h2>
              <p className="text-sm text-slate-400">
                Selecteer doelen waarop je wilt rapporteren (tussentijds mogelijk)
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-violet-400">{totalProgress}%</div>
              <div className="text-xs text-slate-400">voltooid</div>
            </div>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-violet-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${totalProgress}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-slate-500">
              {selectedDoelen.length} doel{selectedDoelen.length !== 1 ? 'en' : ''} geselecteerd
              {Object.keys(gerapporteerdeDoelen).length > 0 && (
                <span className="text-emerald-400 ml-2">
                  • {Object.keys(gerapporteerdeDoelen).length} al gerapporteerd vandaag
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Instructie */}
        <div className="bg-amber-500/10 rounded-xl p-3 border border-amber-500/30">
          <p className="text-amber-300 text-sm flex items-center gap-2">
            <HelpCircle className="w-4 h-4 flex-shrink-0" />
            Vul per doel de smiley-score in en beantwoord de PGB-vragen. {isMaisonSalhuteru ? 'Voor een volledige dag moet minimaal 1 doel gerapporteerd worden.' : 'Voor een volledige dag moeten er minimaal 2 doelen in totaal gerapporteerd worden.'}
          </p>
        </div>
        
        {/* Progress indicator voor dag-totaal */}
        {(Object.keys(gerapporteerdeDoelen).length > 0 || getCompleteDoelen().length > 0) && (
          <div className={`rounded-xl p-3 border ${isDagCompleet() ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-violet-500/10 border-violet-500/30'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isDagCompleet() ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-300 text-sm font-medium">Dag compleet: {getTotaalGerapporteerdeDoelen()} doelen gerapporteerd</span>
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 text-violet-400" />
                    <span className="text-violet-300 text-sm font-medium">
                      Nog {2 - getTotaalGerapporteerdeDoelen()} doel{2 - getTotaalGerapporteerdeDoelen() !== 1 ? 'en' : ''} nodig voor dag compleet
                    </span>
                  </>
                )}
              </div>
              <span className="text-xs text-slate-400">
                {Object.keys(gerapporteerdeDoelen).length} eerder + {getCompleteDoelen().length} nu
              </span>
            </div>
          </div>
        )}
        
        {/* Doelen Grid */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
            Alle Zorgdoelen van {clientInfo.voornaam}
          </h3>
          
          <div className="grid gap-3">
            {clientDoelen.map((doel) => {
              const isSelected = selectedDoelen.includes(doel.id);
              const isAlGerapporteerd = !!gerapporteerdeDoelen[doel.id];
              const canSelect = !isAlGerapporteerd; // Kan selecteren als nog niet gerapporteerd
              const rapportage = doelRapportages[doel.id] || { antwoorden: {} };
              const doelProgress = getDoelProgress(doel.id);
              
              return (
                <div 
                  key={doel.id}
                  className={`rounded-xl border transition-all ${
                    isAlGerapporteerd
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : isSelected 
                        ? 'bg-violet-500/20 border-violet-500/50' 
                        : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600/50 cursor-pointer'
                  }`}
                >
                  {/* Doel header - klikbaar om te selecteren */}
                  <button
                    onClick={() => canSelect && toggleDoelSelectie(doel.id)}
                    disabled={!canSelect}
                    className={`w-full p-4 flex items-start gap-3 text-left ${isAlGerapporteerd ? 'cursor-default' : ''}`}
                    data-testid={`doel-${doel.id}-toggle`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isAlGerapporteerd
                        ? 'bg-emerald-500 border-emerald-500'
                        : isSelected 
                          ? 'bg-violet-500 border-violet-500' 
                          : 'border-slate-500'
                    }`}>
                      {(isSelected || isAlGerapporteerd) && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                          Doel {doel.id}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">
                          {doel.categorie}
                        </span>
                        {isAlGerapporteerd && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Gerapporteerd
                          </span>
                        )}
                        {!isAlGerapporteerd && isSelected && doelProgress > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                            {doelProgress}% ingevuld
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-white mb-1">{doel.titel}</h4>
                      <p className="text-sm text-slate-400">{doel.beschrijving}</p>
                      {isAlGerapporteerd && gerapporteerdeDoelen[doel.id]?.datum && (
                        <p className="text-xs text-emerald-400 mt-1">
                          Gerapporteerd om {new Date(gerapporteerdeDoelen[doel.id].datum).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} door {gerapporteerdeDoelen[doel.id].begeleider}
                        </p>
                      )}
                    </div>
                  </button>
                  
                  {/* Toon eerder gegenereerde rapportage voor al gerapporteerde doelen */}
                  {isAlGerapporteerd && gerapporteerdeDoelen[doel.id]?.rapportage && !isSelected && (
                    <div className="px-4 pb-4 pt-2 border-t border-emerald-700/30 bg-emerald-500/5">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium text-emerald-300">Gegenereerde rapportage</span>
                        {gerapporteerdeDoelen[doel.id]?.smiley && (
                          <span className="text-lg">
                            {SMILEY_OPTIES.find(s => s.value === gerapporteerdeDoelen[doel.id].smiley)?.emoji || ''}
                          </span>
                        )}
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-300 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {gerapporteerdeDoelen[doel.id].rapportage}
                      </div>
                    </div>
                  )}
                  
                  {/* Rapportage invoer - alleen als geselecteerd */}
                  {isSelected && (
                    <div className="px-4 pb-4 pt-2 border-t border-slate-700/50 space-y-4">
                      {/* Toon eerder gegenereerde rapportage bovenaan als die bestaat */}
                      {isAlGerapporteerd && gerapporteerdeDoelen[doel.id]?.rapportage && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm font-medium text-emerald-300">
                              Eerder gegenereerde rapportage ({new Date(gerapporteerdeDoelen[doel.id].datum).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })})
                            </span>
                            {gerapporteerdeDoelen[doel.id]?.smiley && (
                              <span className="text-lg ml-auto">
                                {SMILEY_OPTIES.find(s => s.value === gerapporteerdeDoelen[doel.id].smiley)?.emoji || ''}
                              </span>
                            )}
                          </div>
                          <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {gerapporteerdeDoelen[doel.id].rapportage}
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            Je kunt dit doel opnieuw rapporteren door de vragen hieronder in te vullen.
                          </p>
                        </div>
                      )}
                      
                      {/* Smiley selectie */}
                      <div>
                        <label className="text-sm font-medium text-slate-300 block mb-2">
                          Is dit doel aan bod gekomen? *
                        </label>
                        <div className="flex gap-4 items-center">
                          {SMILEY_OPTIES.map((optie) => (
                            <div key={optie.value} className="relative group">
                              <button
                                onClick={() => updateDoelRapportage(doel.id, 'smiley', optie.value)}
                                className={`flex items-center gap-2 p-2 rounded-xl border-2 transition-all ${
                                  rapportage.smiley === optie.value
                                    ? optie.color === 'emerald' ? 'border-emerald-500 bg-emerald-500/20' 
                                      : 'border-red-500 bg-red-500/20'
                                    : 'border-slate-600 hover:border-slate-500'
                                }`}
                                data-testid={`doel-${doel.id}-smiley-${optie.value}`}
                              >
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  rapportage.smiley === optie.value
                                    ? optie.color === 'emerald' ? 'border-emerald-500' : 'border-red-500'
                                    : 'border-slate-500'
                                }`}>
                                  {rapportage.smiley === optie.value && (
                                    <div className={`w-3 h-3 rounded-full ${
                                      optie.color === 'emerald' ? 'bg-emerald-500' : 'bg-red-500'
                                    }`} />
                                  )}
                                </div>
                                <span className={`text-2xl ${optie.color === 'red' ? 'grayscale-0' : ''}`}>{optie.emoji}</span>
                              </button>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-slate-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                {optie.label}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-700" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Sjabloonvragen per doel - gescheiden in Observatie en PGB */}
                      <div className="space-y-4">
                        {/* Observatie vragen */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-violet-300 block flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Observaties (wat heb je gezien?)
                          </label>
                          
                          {doel.vragen.filter(v => v.type === 'observatie').map((vraag, vraagIndex) => {
                            const antwoord = rapportage.antwoorden?.[vraag.id] || '';
                            const isAnswered = antwoord.trim().length > 0;
                            
                            return (
                              <div 
                                key={vraag.id}
                                className={`rounded-lg border p-3 transition-all ${
                                  isAnswered 
                                    ? 'border-violet-500/50 bg-violet-500/10' 
                                    : 'border-slate-600/50 bg-slate-700/30'
                                }`}
                              >
                                <div className="flex items-start gap-2 mb-2">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    isAnswered 
                                      ? 'bg-violet-500 text-white' 
                                      : 'bg-slate-600 text-slate-300'
                                  }`}>
                                    {isAnswered ? <Check className="w-3 h-3" /> : vraagIndex + 1}
                                  </span>
                                  <p className="text-sm text-white font-medium">{vraag.vraag}</p>
                                </div>
                                <textarea
                                  value={antwoord}
                                  onChange={(e) => updateDoelRapportage(doel.id, null, e.target.value, vraag.id)}
                                  placeholder={vraag.placeholder}
                                  className="w-full bg-slate-800/50 border border-slate-600/50 rounded-lg p-2 text-white placeholder-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm"
                                  rows={2}
                                  data-testid={`doel-${doel.id}-vraag-${vraag.id}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* PGB vragen - belangrijker voor verantwoording */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-emerald-300 block flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            PGB Verantwoording (verplicht)
                          </label>
                          
                          {doel.vragen.filter(v => v.type === 'pgb').map((vraag, vraagIndex) => {
                            const antwoord = rapportage.antwoorden?.[vraag.id] || '';
                            const isAnswered = antwoord.trim().length > 0;
                            
                            return (
                              <div 
                                key={vraag.id}
                                className={`rounded-lg border p-3 transition-all ${
                                  isAnswered 
                                    ? 'border-emerald-500/50 bg-emerald-500/10' 
                                    : 'border-amber-500/30 bg-amber-500/5'
                                }`}
                              >
                                <div className="flex items-start gap-2 mb-2">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    isAnswered 
                                      ? 'bg-emerald-500 text-white' 
                                      : 'bg-amber-500 text-white'
                                  }`}>
                                    {isAnswered ? <Check className="w-3 h-3" /> : '!'}
                                  </span>
                                  <p className="text-sm text-white font-medium">{vraag.vraag}</p>
                                </div>
                                <textarea
                                  value={antwoord}
                                  onChange={(e) => updateDoelRapportage(doel.id, null, e.target.value, vraag.id)}
                                  placeholder={vraag.placeholder}
                                  className="w-full bg-slate-800/50 border border-slate-600/50 rounded-lg p-2 text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm"
                                  rows={2}
                                  data-testid={`doel-${doel.id}-vraag-${vraag.id}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Genereer knop */}
        <div className="sticky bottom-0 bg-gradient-to-t from-slate-900 via-slate-900 to-transparent pt-4 pb-2 -mx-4 px-4">
          <button
            onClick={generateDoelRapportage}
            disabled={!isDoelRapportageCompleet() || loading}
            className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              isDoelRapportageCompleet() && !loading
                ? 'bg-gradient-to-r from-violet-500 to-emerald-500 text-white hover:opacity-90'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="generate-doel-rapportage-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Rapportage genereren...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Genereer Rapportage ({getCompleteDoelen().length} doel{getCompleteDoelen().length !== 1 ? 'en' : ''} klaar)
              </>
            )}
          </button>
          
          {selectedDoelen.length > 0 && !isDoelRapportageCompleet() && (
            <p className="text-center text-amber-400 text-sm mt-2">
              Vul voor minimaal 1 doel de smiley en alle 4 PGB-vragen in om te genereren
            </p>
          )}
          
          {selectedDoelen.length === 0 && Object.keys(gerapporteerdeDoelen).length === 0 && (
            <p className="text-center text-slate-400 text-sm mt-2">
              Selecteer minimaal 1 doel om te rapporteren {!isMaisonSalhuteru && '(minimaal 2 doelen per dag vereist)'}
            </p>
          )}
          
          {selectedDoelen.length === 0 && Object.keys(gerapporteerdeDoelen).length === 1 && !isMaisonSalhuteru && (
            <p className="text-center text-amber-400 text-sm mt-2">
              Je hebt al 1 doel gerapporteerd. Selecteer nog minimaal 1 doel voor een volledige dag.
            </p>
          )}
          
          {selectedDoelen.length === 0 && Object.keys(gerapporteerdeDoelen).length === 1 && isMaisonSalhuteru && (
            <p className="text-center text-emerald-400 text-sm mt-2">
              Dag compleet (1 doel gerapporteerd). Je kunt nog meer doelen toevoegen.
            </p>
          )}
          
          {selectedDoelen.length === 0 && Object.keys(gerapporteerdeDoelen).length >= 2 && (
            <p className="text-center text-emerald-400 text-sm mt-2">
              Dag compleet ({Object.keys(gerapporteerdeDoelen).length} doelen gerapporteerd). Je kunt nog meer doelen toevoegen.
            </p>
          )}
          
          {error && (
            <p className="text-center text-red-400 text-sm mt-2">{error}</p>
          )}
        </div>
      </div>
    );
  };

  const renderVragenFormulier = () => {
    // Check of dit een cliënt van Groep Zwaluw/Adelaars is met vaste 2 contactmomenten flow
    const isVast2ContactmomentenClient = isSjabloonMetDoelkoppeling && !isMaisonSalhuteru;
    
    // Maisson heeft maar 1 contactmoment
    const aantalContactmomenten = isMaisonSalhuteru ? 1 : 2;
    
    const dienstInfo = isVast2ContactmomentenClient
      ? { label: `${selectedClientData?.voornaam} ${selectedClientData?.achternaam} (vast)`, contactMomenten: 2, momenten: ['Contactmoment 1', 'Contactmoment 2'] }
      : isMaisonSalhuteru
        ? { label: `${selectedClientData?.voornaam} ${selectedClientData?.achternaam} (vast)`, contactMomenten: 1, momenten: ['Contactmoment 1'] }
        : selectedClientData?.ambulant 
          ? { label: `Ambulant (${ambulantUren} uur)`, contactMomenten: getAmbulantContactMomenten(ambulantUren) }
          : DIENST_TYPES.find(d => d.value === selectedDienst);
    const progress = getTotalProgress();
    const gerapporteerd = getGerapporteerdeMomentenCount();
    const nogTeRapporteren = getNogTeRapporterenCount();
    
    return (
      <div className="space-y-4" data-testid="vragen-formulier-step">
        {/* Melding: eerder opgeslagen rapportage van vandaag is geladen, of gevonden maar nog niet geladen */}
        {hervatMelding && (
          <div className="p-4 rounded-xl bg-sky-500/15 border border-sky-500/50" data-testid="hervat-melding">
            <p className="text-sky-300 font-medium flex items-center gap-2">
              <RefreshCw className="w-5 h-5 shrink-0" />
              {hervatMelding}
            </p>
            {pendingHervatting ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={bekijkPendingHervatting}
                  className="px-3 py-1.5 rounded-lg border border-sky-400/60 text-sky-200 text-sm hover:bg-sky-500/20"
                  data-testid="bekijk-pending-hervatting"
                >
                  Bekijk/bewerk deze rapportage
                </button>
                <button
                  type="button"
                  onClick={() => { setHervatMelding(''); setPendingHervatting(null); }}
                  className="px-3 py-1.5 rounded-lg border border-slate-500/60 text-slate-300 text-sm hover:bg-slate-700/40"
                  data-testid="nieuwe-aparte-rapportage"
                >
                  Ga door met nieuwe, aparte rapportage
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Een nieuwe, aparte rapportage starten voor deze cliënt? De opgeslagen rapportage van vandaag blijft bewaard.')) {
                    startAparteRapportage();
                  }
                }}
                className="mt-3 px-3 py-1.5 rounded-lg border border-sky-400/60 text-sky-200 text-sm hover:bg-sky-500/20"
                data-testid="nieuwe-aparte-rapportage"
              >
                Nieuwe, aparte rapportage (bijv. tweede contact vandaag)
              </button>
            )}
          </div>
        )}
        {/* Auto-save indicator */}
        {isSjabloonMetDoelkoppeling && (autoSaving || lastAutoSaved) && (
          <div className="flex items-center justify-end gap-2 text-xs text-slate-400">
            {autoSaving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Opslaan...</span>
              </>
            ) : lastAutoSaved ? (
              <>
                <Check className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400">Automatisch opgeslagen om {lastAutoSaved.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</span>
              </>
            ) : null}
          </div>
        )}
        
        {/* Bewerk modus indicator */}
        {editingRapportageId && (
          <div className="bg-violet-500/10 rounded-xl p-3 border border-violet-500/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-violet-400" />
              <span className="text-violet-300 text-sm">
                Je bent een eerdere rapportage aan het bewerken. Pas de antwoorden aan en genereer opnieuw.
              </span>
            </div>
            <button
              onClick={() => resetForNewClient()}
              className="text-xs text-violet-400 hover:text-violet-300 underline"
            >
              Annuleer
            </button>
          </div>
        )}
        
        {/* Progress header */}
        <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-semibold text-white">
                {editingRapportageId ? 'Bewerk: ' : ''}Rapportage voor {selectedClient}
              </h2>
              <p className="text-sm text-slate-400">
                {dienstInfo?.label} - {dienstInfo?.contactMomenten} contactmoment{dienstInfo?.contactMomenten > 1 ? 'en' : ''}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-violet-400" data-testid="progress-percentage">{progress}%</div>
              <div className="text-xs text-slate-400">voltooid</div>
            </div>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-violet-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
          
          {/* Status overzicht */}
          {gerapporteerd > 0 && (
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-emerald-400">
                <Check className="w-4 h-4" />
                {gerapporteerd} gerapporteerd
              </span>
              {nogTeRapporteren > 0 && (
                <span className="flex items-center gap-1.5 text-amber-400">
                  <Clock className="w-4 h-4" />
                  {nogTeRapporteren} klaar om te rapporteren
                </span>
              )}
            </div>
          )}
        </div>

        {/* Yassin Houba: Knoppen voor Mijn Rapportages en Opslaan */}
        {isSjabloonMetDoelkoppeling && (
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowSjabloonRapportages(!showSjabloonRapportages)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm font-medium transition-all border border-slate-600/50"
              data-testid="yassin-mijn-rapportages-btn"
            >
              <FileText className="w-4 h-4" />
              Mijn Rapportages
              {sjabloonOpgeslagenRapportages.length > 0 && (
                <span className="bg-violet-500/30 text-violet-300 text-xs px-2 py-0.5 rounded-full">
                  {sjabloonOpgeslagenRapportages.length}
                </span>
              )}
            </button>
            
            <button
              onClick={saveSjabloonRapportage}
              disabled={savingSjabloonRapportage}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                savingSjabloonRapportage
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
              }`}
              data-testid="yassin-opslaan-btn"
            >
              {savingSjabloonRapportage ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opslaan...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {editingSjabloonRapportageId ? 'Wijzigingen Opslaan' : 'Opslaan naar Database'}
                </>
              )}
            </button>
            
            {editingSjabloonRapportageId && (
              <span className="flex items-center gap-1 text-xs text-violet-400">
                <RefreshCw className="w-3 h-3" />
                Bewerkmodus actief
              </span>
            )}
          </div>
        )}
        
        {/* Yassin Houba: Instructies voor rapportage */}
        {isSjabloonMetDoelkoppeling && (
          <div className="p-4 rounded-xl bg-cyan-900/30 border border-cyan-500/30">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-cyan-300">Na het genereren → Kopieer naar Zilliz</h4>
                <div className="space-y-3 text-sm">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-600/30">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-white border-2 border-slate-400"></div>
                      <span className="font-semibold text-white">Witte veld in Zilliz</span>
                    </div>
                    <p className="text-slate-300 ml-5">Plak hier de <strong>{isMaisonSalhuteru ? '1 contactmoment' : '2 contactmomenten'}</strong> rapportages (Stap 1) + bijzonderheden</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-violet-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                      <span className="font-semibold text-violet-300">Doelen-veld in Zilliz</span>
                    </div>
                    <p className="text-slate-300 ml-5">Plak hier de <strong>{isMaisonSalhuteru ? '1 doel' : '2 doelen'}</strong> rapportages (Stap 2)</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-900/30 border border-amber-500/30">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                      <span className="font-semibold text-amber-300">Overige doelen in Zilliz</span>
                    </div>
                    <p className="text-slate-300 ml-6">Bij doelen waar <em>niet</em> aan is gewerkt, noteer:</p>
                    <p className="text-amber-300 font-medium ml-6 mt-1">"Aan dit doel is tijdens dit contactmoment niet gewerkt."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Yassin Houba: Panel met opgeslagen rapportages */}
        {isSjabloonMetDoelkoppeling && showSjabloonRapportages && (
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-400" />
                Mijn Opgeslagen Rapportages voor Yassin Houba
              </h3>
              <button
                onClick={() => setShowSjabloonRapportages(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {loadingSjabloonRapportages ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Laden...
              </div>
            ) : sjabloonOpgeslagenRapportages.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Nog geen opgeslagen rapportages</p>
                <p className="text-xs mt-1">Klik op "Opslaan naar Database" om een rapportage op te slaan</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {sjabloonOpgeslagenRapportages.map(rapportage => (
                  <div
                    key={rapportage.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      editingSjabloonRapportageId === rapportage.id
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-slate-600/50 bg-slate-900/30 hover:border-slate-500/50'
                    }`}
                    onClick={() => loadSjabloonRapportage(rapportage)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white text-sm">
                          {rapportage.client_naam || 'Onbekende cliënt'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {new Date(rapportage.datum).toLocaleDateString('nl-NL', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {rapportage.contactmomenten?.length || 0} contactmomenten
                          {rapportage.contactmomenten?.filter(cm => cm.standaard_rapportage).length > 0 && (
                            <span className="ml-2 text-emerald-400">
                              • {rapportage.contactmomenten.filter(cm => cm.standaard_rapportage).length} gerapporteerd
                            </span>
                          )}
                          {rapportage.contactmomenten?.filter(cm => cm.doel_rapportage).length > 0 && (
                            <span className="ml-2 text-violet-400">
                              • {rapportage.contactmomenten.filter(cm => cm.doel_rapportage).length} doelen
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingSjabloonRapportageId === rapportage.id && (
                          <span className="text-xs bg-violet-500/20 text-violet-300 px-2 py-1 rounded">Actief</span>
                        )}
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Aangemaakt: {new Date(rapportage.created_at).toLocaleString('nl-NL')}
                      {rapportage.updated_at && rapportage.updated_at !== rapportage.created_at && (
                        <span> • Bijgewerkt: {new Date(rapportage.updated_at).toLocaleString('nl-NL')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contactmomenten accordions */}
        {contactMomenten.map((moment, momentIndex) => {
          const momentProgress = getMomentProgress(moment);
          const isExpanded = expandedMoment === momentIndex;
          const isGerapporteerd = isMomentGerapporteerd(momentIndex);
          const isVolledig = isMomentVolledig(moment, momentIndex);
          const isLoadingThis = loadingMomentIndex === momentIndex;
          
          return (
            <div 
              key={moment.id} 
              className={`rounded-2xl border overflow-hidden transition-all ${
                isGerapporteerd && !isSjabloonMetDoelkoppeling
                  ? 'bg-slate-800/30 border-emerald-500/30 opacity-75' 
                  : isGerapporteerd && isSjabloonMetDoelkoppeling
                    ? 'bg-slate-800/50 border-emerald-500/30'
                    : 'bg-slate-800/50 border-slate-700/50'
              }`} 
              data-testid={`contactmoment-${momentIndex}`}
            >
              {/* Moment header */}
              <button
                onClick={() => {
                  if (isGerapporteerd && !isSjabloonMetDoelkoppeling) {
                    // Bekijk de opgeslagen rapportage (niet voor sjabloon-cliënten - daar blijft het formulier)
                    bekijkOpgeslagenRapportage(momentIndex);
                  } else {
                    setExpandedMoment(isExpanded ? -1 : momentIndex);
                  }
                }}
                data-testid={`contactmoment-header-${momentIndex}`}
                className={`w-full p-4 flex items-center justify-between transition-colors ${
                  isGerapporteerd ? 'hover:bg-emerald-900/20 cursor-pointer' : 'hover:bg-slate-700/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                    isGerapporteerd ? 'bg-emerald-500/50 text-emerald-200' :
                    momentProgress === 100 ? 'bg-emerald-500 text-white' :
                    momentProgress > 50 ? 'bg-violet-500 text-white' :
                    'bg-slate-700 text-slate-300'
                  }`}>
                    {isGerapporteerd ? <Check className="w-5 h-5" /> : 
                     momentProgress === 100 ? <Check className="w-5 h-5" /> : momentIndex + 1}
                  </div>
                  <div className="text-left">
                    <div className={`font-semibold ${isGerapporteerd ? 'text-slate-400' : 'text-white'}`}>
                      Contactmoment: {moment.naam}
                      {isGerapporteerd && (
                        <span className="ml-2 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                          ✓ Gerapporteerd
                        </span>
                      )}
                    </div>
                    <div className={`text-sm ${isGerapporteerd ? 'text-slate-500' : 'text-slate-400'}`}>
                      {isGerapporteerd 
                        ? (isSjabloonMetDoelkoppeling ? 'Klik om te bewerken of doel toe te voegen' : 'Klik om rapportage te bekijken')
                        : `${momentProgress}% ingevuld - ${Object.values(moment.antwoorden).filter(a => a.trim()).length}/${isSjabloonMetDoelkoppeling ? 6 : PGB_VRAGEN.length} vragen`
                      }
                    </div>
                  </div>
                </div>
                {isGerapporteerd && !isSjabloonMetDoelkoppeling ? (
                  <Eye className="w-5 h-5 text-emerald-400" />
                ) : (
                  isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {/* Wissen knop - zichtbaar voor gerapporteerde momenten OF momenten met ingevulde antwoorden */}
              {(isGerapporteerd || Object.values(moment.antwoorden).some(a => a && a.trim().length > 0)) && (
                <div className="px-4 pb-2 border-t border-slate-700/30 bg-slate-800/30">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      wisContactMoment(momentIndex);
                    }}
                    data-testid={`wis-contactmoment-${momentIndex}`}
                    className="w-full mt-2 py-2 px-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/20 hover:border-red-500/50 transition-all flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Wis dit contactmoment
                  </button>
                </div>
              )}

              {/* Expanded content - voor Yassin: altijd tonen als expanded, voor anderen: alleen als niet gerapporteerd */}
              {isExpanded && (!isGerapporteerd || isSjabloonMetDoelkoppeling) && (
                <div className="p-4 pt-0 space-y-4 border-t border-slate-700/50">
                  {PGB_VRAGEN.map(vraag => {
                    const IconComponent = vraag.icon;
                    const hasAnswer = moment.antwoorden[vraag.id]?.trim().length > 0;
                    
                    // Voor Yassin Houba: vraag 7 (doelen) wordt APART gerenderd NA de standaard vragen
                    // Skip hier, wordt verderop gerenderd
                    if (vraag.id === 'doelen' && isSjabloonMetDoelkoppeling) {
                      return null;
                    }
                    
                    // Standaard rendering voor vragen 1-6
                    return (
                      <div key={vraag.id} className={`p-4 rounded-xl border ${
                        hasAnswer ? 'border-emerald-500/30 bg-emerald-900/10' : 'border-slate-600/30 bg-slate-900/30'
                      }`}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className={`p-2 rounded-lg bg-${vraag.color}-500/20`}>
                            <IconComponent className={`w-4 h-4 text-${vraag.color}-400`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-white">{vraag.titel}</h4>
                              {hasAnswer && <Check className="w-4 h-4 text-emerald-400" />}
                            </div>
                            <ul className="mt-1 text-xs text-slate-400 space-y-0.5">
                              {vraag.vragen.map((v, i) => (
                                <li key={`${vraag.id}-subvraag-${i}`}>• {v}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <textarea
                          value={moment.antwoorden[vraag.id] || ''}
                          onChange={(e) => updateAntwoord(momentIndex, vraag.id, e.target.value)}
                          placeholder={vraag.placeholder}
                          data-testid={`vraag-${momentIndex}-${vraag.id}`}
                          className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm"
                          rows={3}
                        />
                      </div>
                    );
                  })}
                  
                  {/* ===== STAP 1: STANDAARD RAPPORTAGE (vraag 1-6) ===== */}
                  {isSjabloonMetDoelkoppeling && (
                    <div className="p-4 rounded-xl border-2 border-emerald-500/50 bg-emerald-900/20 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">1</div>
                        <div>
                          <h4 className="font-semibold text-emerald-400">Stap 1: Standaard Rapportage</h4>
                          <p className="text-xs text-slate-400">Kopieer dit naar het witte veld in Zilliz</p>
                        </div>
                      </div>
                      
                      {/* Extra vraag: Bijzonderheden voor de overdracht */}
                      {!getOpgeslagenRapportage(momentIndex) && (
                        <div className="p-3 rounded-lg border border-slate-600/30 bg-slate-800/30">
                          <div className="flex items-start gap-2 mb-2">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                              bijzonderhedenPerMoment[momentIndex]?.trim().length > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-slate-300'
                            }`}>
                              {bijzonderhedenPerMoment[momentIndex]?.trim().length > 0 ? <Check className="w-3 h-3" /> : '!'}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-white text-sm">Bijzonderheden voor de overdracht</div>
                              <div className="text-xs text-slate-400 mt-1">Zijn er belangrijke zaken voor de volgende dienst? (optioneel)</div>
                            </div>
                          </div>
                          <textarea
                            value={bijzonderhedenPerMoment[momentIndex] || ''}
                            onChange={(e) => setBijzonderhedenPerMoment(prev => ({
                              ...prev,
                              [momentIndex]: e.target.value
                            }))}
                            placeholder="Bijv. afspraken, aandachtspunten, medicatie, gedrag..."
                            data-testid={`bijzonderheden-${momentIndex}`}
                            className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm"
                            rows={2}
                          />
                        </div>
                      )}
                      
                      {/* Toon gegenereerde rapportage als die er is */}
                      {getOpgeslagenRapportage(momentIndex) ? (
                        <div className="space-y-3">
                          {/* Gegenereerde rapportage weergave */}
                          <div className="p-3 rounded-lg bg-slate-900/50 border border-emerald-500/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                Rapportage gegenereerd
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(getOpgeslagenRapportage(momentIndex));
                                }}
                                className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1"
                              >
                                <Copy className="w-3 h-3" />
                                Kopiëren
                              </button>
                            </div>
                            <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans max-h-40 overflow-y-auto">{getOpgeslagenRapportage(momentIndex)}</pre>
                          </div>
                          
                          {/* Bewerk antwoorden knop */}
                          <button
                            onClick={() => {
                              // Wis de gegenereerde rapportage zodat gebruiker opnieuw kan genereren
                              const nieuweGerapporteerd = { ...gerapporteerdeMomenten };
                              delete nieuweGerapporteerd[momentIndex];
                              setGerapporteerdeMomenten(nieuweGerapporteerd);
                              // Update localStorage
                              const key = getGerapporteerdeKey();
                              localStorage.setItem(key, JSON.stringify(nieuweGerapporteerd));
                            }}
                            data-testid={`bewerk-standaard-${momentIndex}`}
                            className="w-full py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 font-medium text-sm flex items-center justify-center gap-2 transition-all"
                          >
                            <Edit3 className="w-4 h-4" />
                            Bewerk antwoorden & genereer opnieuw
                          </button>
                        </div>
                      ) : isVolledig ? (
                        <button
                          onClick={() => generateSingleMomentRapportage(momentIndex)}
                          disabled={loading}
                          data-testid={`generate-single-moment-${momentIndex}`}
                          className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                            loading
                              ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                              : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/25'
                          }`}
                        >
                          {isLoadingThis ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Rapportage genereren...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              Genereer Standaard Rapportage (voor witte veld Zilliz)
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="text-sm text-slate-400 text-center py-2">
                          <AlertCircle className="w-4 h-4 inline mr-2" />
                          Vul eerst alle 6 vragen hierboven in
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* ===== STAP 2: DOEL-RAPPORTAGE (vraag 7) - voor alle Groep Zwaluw/Adelaars cliënten ===== */}
                  {isSjabloonMetDoelkoppeling && (() => {
                    const vraag = PGB_VRAGEN.find(v => v.id === 'doelen');
                    const IconComponent = vraag.icon;
                    const clientDoelen = getClientDoelen();
                    const selectedDoelId = geselecteerdDoelPerMoment[momentIndex];
                    const hasDoelSelected = selectedDoelId !== undefined && selectedDoelId !== null;
                    const doelVragen = doelVragenPerMoment[momentIndex] || {};
                    // Check voor beide mogelijke ID formaten (oud: begeleiding/reactie/resultaat, nieuw: vraag1/vraag2/vraag3)
                    const hasAllDoelVragenIngevuld = (
                      (doelVragen.vraag1?.trim().length > 0 && doelVragen.vraag2?.trim().length > 0 && doelVragen.vraag3?.trim().length > 0) ||
                      (doelVragen.begeleiding?.trim().length > 0 && doelVragen.reactie?.trim().length > 0 && doelVragen.resultaat?.trim().length > 0)
                    );
                    const gegenereerdeDoelRapportage = gegenereerdeDoelRapportages[momentIndex];
                    const isLoadingDoel = loadingDoelRapportage[momentIndex];
                    
                    // Voor cliënten ZONDER gedefinieerde doelen: vraag 7 als tekstveld
                    const vraag7Tekst = moment.antwoorden?.doelen || '';
                    const hasVraag7Tekst = vraag7Tekst?.trim().length > 0;
                    
                    // Functie om aparte doel-rapportage te genereren
                    const generateDoelRapportage = async () => {
                      // Voor cliënten met doelen: controleer of doel geselecteerd en vragen ingevuld
                      if (heeftGedefinieerdeDoelen) {
                        if (!hasDoelSelected || !hasAllDoelVragenIngevuld) return;
                      } else {
                        // Voor cliënten zonder doelen: controleer of vraag 7 tekst is ingevuld
                        if (!hasVraag7Tekst) return;
                      }
                      
                      setLoadingDoelRapportage(prev => ({ ...prev, [momentIndex]: true }));
                      
                      try {
                        const token = localStorage.getItem('token');
                        const backendUrl = process.env.REACT_APP_BACKEND_URL;
                        
                        if (!token || !backendUrl) {
                          setError('Geen authenticatie token gevonden');
                          return;
                        }
                        
                        let doelTranscriptie;
                        
                        if (heeftGedefinieerdeDoelen) {
                          const selectedDoel = clientDoelen.find(d => d.id === selectedDoelId);
                          doelTranscriptie = `
DOEL-RAPPORTAGE voor ${selectedClient}
Contactmoment: ${moment.naam}
Datum: ${rapportageDatum}
Begeleider: ${begeleiderNaam}

GESELECTEERD ZORGDOEL:
Doel ${selectedDoel.id}: ${selectedDoel.titel}
Beschrijving: ${selectedDoel.beschrijving}

VRAAG 1: Welke begeleiding heb je vandaag geboden om aan het zorgdoel te werken?
${doelVragen.vraag1 || doelVragen.begeleiding || ''}

VRAAG 2: Hoe heeft de cliënt gereageerd op de begeleiding en wat heeft de cliënt zelf gedaan?
${doelVragen.vraag2 || doelVragen.reactie || ''}

VRAAG 3: Wat is het resultaat van de begeleiding en welke vervolgstappen zijn nodig?
${doelVragen.vraag3 || doelVragen.resultaat || ''}
                          `.trim();
                        } else {
                          // Voor cliënten zonder doelen: gebruik de ingevulde tekst van vraag 7
                          doelTranscriptie = `
DOEL-RAPPORTAGE voor ${selectedClient}
Contactmoment: ${moment.naam}
Datum: ${rapportageDatum}
Begeleider: ${begeleiderNaam}

AAN WELKE DOELEN IS GEWERKT:
${vraag7Tekst}
                          `.trim();
                        }
                        
                        const response = await fetch(`${backendUrl}/api/voice/generate-rapportage`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            client_voornaam: selectedClient,
                            locatie: selectedClientData?.locatie || effectiveLocation,
                            transcriptie: doelTranscriptie,
                            dienst: 'doel_rapportage',
                            aantal_contactmomenten: 1,
                            contact_momenten_namen: [moment.naam],
                            begeleider: begeleiderNaam,
                            report_type: 'doel_rapportage'
                          })
                        });
                        
                        if (!response.ok) {
                          const errorData = await response.json();
                          throw new Error(errorData.detail || 'Fout bij genereren rapportage');
                        }
                        
                        const data = await response.json();
                        
                        setGegenereerdeDoelRapportages(prev => ({
                          ...prev,
                          [momentIndex]: data.rapportage
                        }));
                        
                        // AUTOMATISCH OPSLAAN na genereren doel-rapportage
                        // We geven de data direct mee zodat we niet wachten op state update
                        try {
                          await saveSjabloonRapportage({
                            momentIndex: momentIndex,
                            standaardRapportage: gerapporteerdeMomenten[momentIndex]?.rapportage || null,
                            doelRapportage: data.rapportage
                          });
                          await fetchSjabloonRapportages();
                        } catch (saveErr) {
                          console.error('Fout bij automatisch opslaan doel-rapportage:', saveErr);
                        }
                        
                      } catch (err) {
                        setError('Fout bij genereren doel-rapportage: ' + (err.message || 'Onbekende fout'));
                      } finally {
                        setLoadingDoelRapportage(prev => ({ ...prev, [momentIndex]: false }));
                      }
                    };
                    
                    // Functie om een doel-vraag te updaten
                    const updateDoelVraag = (vraagId, value) => {
                      setDoelVragenPerMoment(prev => ({
                        ...prev,
                        [momentIndex]: {
                          ...prev[momentIndex],
                          [vraagId]: value
                        }
                      }));
                    };
                    
                    return (
                      <div className="p-4 rounded-xl border-2 border-violet-500/50 bg-violet-900/20 space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-sm">2</div>
                          <div>
                            <h4 className="font-semibold text-violet-400">Stap 2: Doel-Rapportage</h4>
                            <p className="text-xs text-slate-400">Kopieer dit naar het doelen-veld in Zilliz</p>
                          </div>
                        </div>
                        
                        {/* Vraag 7 content */}
                        <div className={`p-4 rounded-xl border ${
                          (heeftGedefinieerdeDoelen && hasDoelSelected && hasAllDoelVragenIngevuld) || (!heeftGedefinieerdeDoelen && hasVraag7Tekst) 
                            ? 'border-violet-500/30 bg-violet-900/10' 
                            : 'border-slate-600/30 bg-slate-900/30'
                        }`}>
                          <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 rounded-lg bg-violet-500/20">
                              <IconComponent className="w-4 h-4 text-violet-400" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-white">{vraag.titel}</h4>
                              <p className="mt-1 text-xs text-slate-400">
                                {heeftGedefinieerdeDoelen 
                                  ? 'Selecteer 1 zorgdoel en beantwoord de 3 vragen' 
                                  : 'Beschrijf aan welk doel er is gewerkt tijdens dit contactmoment'
                                }
                              </p>
                            </div>
                          </div>
                          
                          {/* Voor cliënten MET gedefinieerde doelen: Doelen selectie grid */}
                          {heeftGedefinieerdeDoelen ? (
                            <>
                              <div className="space-y-2 mb-4">
                                {clientDoelen.map(doel => {
                                  const isSelected = selectedDoelId === doel.id;
                                  const clientInfo = getDoelRapportageClientInfo();
                                  const goalId = `${clientInfo?.naam?.replace(/\s+/g, '_')}_doel_${doel.id}`;
                                  const isLoadingQuestions = loadingAiQuestions[goalId];
                                  
                                  return (
                                    <button
                                      key={doel.id}
                                      onClick={() => {
                                        const newSelectedId = isSelected ? null : doel.id;
                                        setGeselecteerdDoelPerMoment(prev => {
                                          const newState = {
                                            ...prev,
                                            [momentIndex]: newSelectedId
                                          };
                                          return newState;
                                        });
                                        // Als nieuw doel geselecteerd
                                        if (!isSelected && newSelectedId !== null) {
                                          // Reset gegenereerde rapportage
                                          setGegenereerdeDoelRapportages(prev => {
                                            const newState = { ...prev };
                                            delete newState[momentIndex];
                                            return newState;
                                          });
                                          // AI vragen ophalen - nu actief
                                          setTimeout(() => {
                                            fetchAiGeneratedQuestions(doel, clientInfo).catch(err => {
                                              console.error('Failed to fetch AI questions:', err);
                                            });
                                          }, 100);
                                        }
                                      }}
                                      className={`w-full p-3 rounded-xl border text-left transition-all ${
                                        isSelected
                                          ? 'border-violet-500 bg-violet-500/20 ring-2 ring-violet-500/30'
                                          : 'border-slate-600/50 bg-slate-800/50 hover:border-slate-500/50'
                                      }`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                          isSelected ? 'border-violet-500 bg-violet-500' : 'border-slate-500'
                                        }`}>
                                          {isSelected && <Check className="w-3 h-3 text-white" />}
                                          {isLoadingQuestions && !isSelected && <Loader2 className="w-3 h-3 text-slate-400 animate-spin" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-white text-sm">Doel {doel.id}: {doel.titel}</div>
                                          <div className="text-xs text-slate-400 mt-1">{doel.beschrijving}</div>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                          
                          {/* 3 Doel-rapportage vragen - alleen tonen als doel geselecteerd */}
                          {hasDoelSelected && (
                            <div className="space-y-4 pt-4 border-t border-slate-700/50">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-violet-300 font-medium">
                                  <Target className="w-4 h-4" />
                                  Vragen over: {clientDoelen.find(d => d.id === selectedDoelId)?.titel}
                                </div>
                                {/* Vernieuw vragen knop */}
                                <button
                                  onClick={() => {
                                    const selectedDoel = clientDoelen.find(d => d.id === selectedDoelId);
                                    const clientInfo = getDoelRapportageClientInfo();
                                    if (selectedDoel && clientInfo) {
                                      // Clear cached questions first
                                      const goalId = `${clientInfo.naam.replace(/\s+/g, '_')}_doel_${selectedDoel.id}`;
                                      setAiGeneratedQuestions(prev => {
                                        const newState = { ...prev };
                                        delete newState[goalId];
                                        return newState;
                                      });
                                      // Force regenerate
                                      fetchAiGeneratedQuestions(selectedDoel, clientInfo, true);
                                    }
                                  }}
                                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-violet-400 transition-colors"
                                  title="Vragen opnieuw genereren met verbeterde AI"
                                  data-testid="regenerate-questions-btn"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Vernieuw vragen
                                </button>
                              </div>
                              
                              {/* Gebruik doel-specifieke vragen uit clientDoelen.js of AI-gegenereerd */}
                              {(() => {
                                const selectedDoel = clientDoelen.find(d => d.id === selectedDoelId);
                                const clientInfo = getDoelRapportageClientInfo();
                                const goalId = `${clientInfo?.naam?.replace(/\s+/g, '_')}_doel_${selectedDoel?.id}`;
                                const isLoadingQuestions = loadingAiQuestions[goalId];
                                
                                // Haal AI-gegenereerde vragen of fallback naar vaste vragen
                                const doelSpecifiekeVragen = getQuestionsForGoal(selectedDoel, clientInfo);
                                
                                // Toon loading state als vragen worden gegenereerd
                                if (isLoadingQuestions) {
                                  return (
                                    <div className="flex items-center justify-center p-6 text-violet-400">
                                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                      <span>AI genereert vragen voor dit doel...</span>
                                    </div>
                                  );
                                }
                                
                                return doelSpecifiekeVragen.map((doelVraag, index) => {
                                  const hasAnswer = doelVragen[doelVraag.id]?.trim().length > 0;
                                  return (
                                    <div key={doelVraag.id} className={`p-3 rounded-lg border ${
                                      hasAnswer ? 'border-violet-500/30 bg-violet-900/10' : 'border-slate-600/30 bg-slate-800/30'
                                    }`}>
                                      <div className="flex items-start gap-2 mb-2">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                          hasAnswer ? 'bg-violet-500 text-white' : 'bg-slate-600 text-slate-300'
                                        }`}>
                                          {hasAnswer ? <Check className="w-3 h-3" /> : index + 1}
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-medium text-white text-sm">{index + 1}. {doelVraag.vraag}</div>
                                          <div className="text-xs text-slate-400 mt-1">{doelVraag.placeholder}</div>
                                        </div>
                                      </div>
                                      <textarea
                                        value={doelVragen[doelVraag.id] || ''}
                                        onChange={(e) => updateDoelVraag(doelVraag.id, e.target.value)}
                                        placeholder={doelVraag.placeholder}
                                        data-testid={`doel-vraag-${doelVraag.id}-${momentIndex}`}
                                        className="w-full p-3 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm"
                                        rows={3}
                                      />
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                            </>
                          ) : (
                            /* Voor cliënten ZONDER gedefinieerde doelen: Vraag 7 als tekstveld */
                            <div className="space-y-3">
                              <textarea
                                value={vraag7Tekst}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setContactMomenten(prev => prev.map((m, i) => 
                                    i === momentIndex 
                                      ? { ...m, antwoorden: { ...m.antwoorden, doelen: newValue } }
                                      : m
                                  ));
                                }}
                                placeholder="Beschrijf aan welk doel of welke doelen je hebt gewerkt tijdens dit contactmoment. Bijvoorbeeld: 'Gewerkt aan zelfstandigheid bij het koken' of 'Geoefend met sociale vaardigheden'"
                                data-testid={`vraag-doelen-tekst-${momentIndex}`}
                                className="w-full p-4 rounded-xl bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm"
                                rows={4}
                              />
                              {hasVraag7Tekst && (
                                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                                  <Check className="w-4 h-4" />
                                  Doel-beschrijving ingevuld
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Genereer Doel-Rapportage knop */}
                        {((heeftGedefinieerdeDoelen && hasDoelSelected) || (!heeftGedefinieerdeDoelen && hasVraag7Tekst)) && (
                          <button
                            onClick={generateDoelRapportage}
                            disabled={(heeftGedefinieerdeDoelen && !hasAllDoelVragenIngevuld) || isLoadingDoel}
                            data-testid={`generate-doel-rapportage-${momentIndex}`}
                            className={`w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                              ((heeftGedefinieerdeDoelen && hasAllDoelVragenIngevuld) || (!heeftGedefinieerdeDoelen && hasVraag7Tekst)) && !isLoadingDoel
                                ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white shadow-lg'
                                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            }`}
                          >
                            {isLoadingDoel ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Doel-rapportage genereren...
                              </>
                            ) : (heeftGedefinieerdeDoelen && !hasAllDoelVragenIngevuld) ? (
                              <>
                                <AlertCircle className="w-4 h-4" />
                                Beantwoord alle 3 vragen om te genereren
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Genereer Doel-Rapportage (voor doelen-veld Zilliz)
                              </>
                            )}
                          </button>
                        )}
                        
                        {/* Gegenereerde Doel-Rapportage weergave */}
                        {gegenereerdeDoelRapportage && (
                          <div className="p-4 rounded-xl border border-violet-500/30 bg-violet-900/10 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-violet-400" />
                                <span className="font-medium text-violet-300">Doel-Rapportage (voor Zilliz)</span>
                              </div>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(gegenereerdeDoelRapportage);
                                }}
                                className="text-xs px-3 py-1 rounded-lg bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 flex items-center gap-1"
                              >
                                <Copy className="w-3 h-3" />
                                Kopiëren
                              </button>
                            </div>
                            <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50 max-h-40 overflow-y-auto">
                              <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans">{gegenereerdeDoelRapportage}</pre>
                            </div>
                            
                            {/* Bewerk doel/toelichting knop */}
                            <button
                              onClick={() => {
                                // Wis de gegenereerde doel-rapportage
                                setGegenereerdeDoelRapportages(prev => {
                                  const newState = { ...prev };
                                  delete newState[momentIndex];
                                  return newState;
                                });
                              }}
                              data-testid={`bewerk-doel-${momentIndex}`}
                              className="w-full py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 font-medium text-sm flex items-center justify-center gap-2 transition-all"
                            >
                              <Edit3 className="w-4 h-4" />
                              Bewerk doel/toelichting & genereer opnieuw
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Tussentijds rapporteren knop - alleen voor NIET-Yassin cliënten */}
                  {!isSjabloonMetDoelkoppeling && isVolledig && (
                    <button
                      onClick={() => generateSingleMomentRapportage(momentIndex)}
                      disabled={loading}
                      data-testid={`generate-single-moment-${momentIndex}`}
                      className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                        loading
                          ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                          : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/25'
                      }`}
                    >
                      {isLoadingThis ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Rapportage genereren...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Rapporteer alleen &quot;{moment.naam}&quot; (tussentijds)
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Volgende moment knop */}
                  {momentIndex < contactMomenten.length - 1 && !isMomentGerapporteerd(momentIndex + 1) && (
                    <button
                      onClick={() => setExpandedMoment(momentIndex + 1)}
                      className="w-full py-3 rounded-xl bg-violet-500/20 text-violet-400 font-medium hover:bg-violet-500/30 transition-colors"
                    >
                      Ga naar volgend contactmoment ({contactMomenten[momentIndex + 1].naam})
                    </button>
                  )}
                  
                  {/* Wissen knop binnen uitgebreid formulier - alleen tonen als er iets is ingevuld */}
                  {Object.values(moment.antwoorden).some(a => a && a.trim().length > 0) && (
                    <button
                      onClick={() => wisContactMoment(momentIndex)}
                      data-testid={`wis-contactmoment-expanded-${momentIndex}`}
                      className="w-full py-2 mt-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/20 hover:border-red-500/50 transition-all flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Wis alle antwoorden van dit moment
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Generate alle niet-gerapporteerde momenten button */}
        {nogTeRapporteren > 0 && (
          <button
            onClick={generateRapportage}
            disabled={!canGenerate() || loading}
            data-testid="generate-rapportage-btn"
            className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
              canGenerate() && !loading
                ? 'bg-gradient-to-r from-violet-500 to-emerald-500 text-white hover:from-violet-600 hover:to-emerald-600 shadow-lg shadow-violet-500/25'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
          >
            {loading && loadingMomentIndex === null ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Even geduld...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {nogTeRapporteren === contactMomenten.length 
                  ? `Genereer volledige rapportage (${nogTeRapporteren} momenten)`
                  : `Genereer resterende ${nogTeRapporteren} moment${nogTeRapporteren > 1 ? 'en' : ''}`
                }
              </>
            )}
          </button>
        )}

        {/* Status berichten */}
        {!canGenerate() && !loading && gerapporteerd < contactMomenten.length && (
          <p className="text-center text-sm text-amber-400">
            Vul minstens 1 contactmoment volledig in (alle {isSjabloonMetDoelkoppeling ? '6' : '7'} vragen) om te kunnen rapporteren
          </p>
        )}

        {gerapporteerd === contactMomenten.length && (
          <div className="text-center p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-emerald-400 font-medium">
              ✓ Alle {contactMomenten.length} contactmomenten zijn gerapporteerd voor {selectedClient}!
            </p>
            <button
              onClick={() => resetForNewClient()}
              className="mt-3 px-4 py-2 rounded-lg bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors"
            >
              <User className="w-4 h-4 inline mr-2" />
              Volgende cliënt rapporteren
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPreview = () => {
    const nogTeRapporteren = getNogTeRapporterenCount();
    const gerapporteerd = getGerapporteerdeMomentenCount();
    const alleKlaar = gerapporteerd === contactMomenten.length;
    const isViewingOpgeslagen = viewingRapportageIndex !== null;
    const viewedMomentNaam = isViewingOpgeslagen && contactMomenten[viewingRapportageIndex] 
      ? contactMomenten[viewingRapportageIndex].naam 
      : null;
    
    return (
      <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50" data-testid="rapportage-preview">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-emerald-400" />
            {isViewingOpgeslagen 
              ? `Rapportage: ${viewedMomentNaam}` 
              : 'Gegenereerde Rapportage'
            }
            {isViewingOpgeslagen && (
              <span className="ml-2 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                Opgeslagen
              </span>
            )}
          </h2>
          <button
            onClick={copyToClipboard}
            data-testid="copy-rapportage-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              copied ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Gekopieerd!' : 'Kopieer'}
          </button>
        </div>
        
        <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-600/50 max-h-[60vh] overflow-y-auto" data-testid="rapportage-content">
          <pre className="whitespace-pre-wrap text-slate-200 text-sm font-sans leading-relaxed">
            {rapportage}
          </pre>
        </div>
        
        {/* Success melding na opslaan */}
        {saveSuccess && (
          <div className="mt-4 p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/50 animate-pulse">
            <p className="text-emerald-400 font-medium flex items-center gap-2">
              <Check className="w-5 h-5" />
              ✓ Rapportage is automatisch opgeslagen! Je kunt deze terugvinden in &quot;Mijn Rapportages&quot;.
            </p>
          </div>
        )}
        
        {/* Status bericht voor tussentijds rapporteren */}
        {!alleKlaar && !isViewingOpgeslagen && (
          <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <p className="text-amber-400 text-sm">
              <Clock className="w-4 h-4 inline mr-2" />
              Je hebt nog {contactMomenten.length - gerapporteerd} contactmoment{contactMomenten.length - gerapporteerd > 1 ? 'en' : ''} om te rapporteren voor {selectedClient}.
            </p>
          </div>
        )}
        
        <div className="mt-4 flex gap-3">
          {/* Terug naar vragen knop */}
          <button
            onClick={() => {
              setCurrentStep(3);
              setShowPreview(false);
              setRapportage('');
              setViewingRapportageIndex(null);
              // Zoek het eerste niet-gerapporteerde moment om te openen
              const eersteNietGerapporteerd = contactMomenten.findIndex((m, idx) => !isMomentGerapporteerd(idx));
              if (eersteNietGerapporteerd >= 0) {
                setExpandedMoment(eersteNietGerapporteerd);
              }
            }}
            data-testid="back-to-vragen-btn"
            className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
              alleKlaar 
                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                : 'bg-violet-500 text-white hover:bg-violet-600'
            }`}
          >
            {isViewingOpgeslagen ? (
              <>
                <ArrowLeft className="w-4 h-4" />
                Terug naar overzicht
              </>
            ) : alleKlaar ? (
              'Terug naar overzicht'
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Volgend contactmoment rapporteren
              </>
            )}
          </button>
          
          {/* Volgende cliënt knop - alleen tonen als niet viewing opgeslagen */}
          {!isViewingOpgeslagen && (
            <button
              onClick={() => resetForNewClient()}
              data-testid="next-client-btn"
              className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                alleKlaar
                  ? 'bg-violet-500 text-white hover:bg-violet-600'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <User className="w-4 h-4" />
              {alleKlaar ? 'Volgende cliënt rapporteren' : 'Andere cliënt'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="rapporteer-helper-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-300" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">PGB Rapportage Tool</h1>
                <p className="text-sm text-slate-400">
                  {begeleiderNaam ? `Begeleider: ${begeleiderNaam}` : 'Gestructureerde rapportages voor verantwoording'}
                </p>
              </div>
            </div>
            {/* Navigatie knoppen */}
            <div className="flex items-center gap-2">
              {/* Andere cliënt knop - altijd zichtbaar vanaf stap 2 */}
              {currentStep >= 1 && begeleiderNaam && !loading && (
                <button
                  onClick={() => resetForNewClient()}
                  data-testid="andere-client-btn"
                  className="flex items-center gap-1.5 text-xs text-white bg-violet-600 hover:bg-violet-700 px-3 py-1.5 rounded-lg transition-colors font-medium"
                >
                  <User className="w-3.5 h-3.5" />
                  Andere cliënt
                </button>
              )}
              
              {/* Concept wissen knop - alleen tonen vanaf stap 4 */}
              {currentStep >= 3 && begeleiderNaam && !loading && (
                <button
                  onClick={() => {
                    if (window.confirm('Weet je zeker dat je het concept wilt wissen? Dit verwijdert alle opgeslagen antwoorden en selecties.')) {
                      // Wis doel-rapportage specifieke localStorage
                      if (isDoelRapportageClient) {
                        const draftKey = getDoelDraftStorageKey();
                        const gerapporteerdeKey = getDoelGerapporteerdeKey();
                        localStorage.removeItem(draftKey);
                        localStorage.removeItem(gerapporteerdeKey);
                      }
                      // Gebruik centrale reset functie
                      resetForNewClient();
                    }
                  }}
                  data-testid="wissen-btn"
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 bg-slate-800 px-2 py-1 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Wissen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Error message met opnieuw proberen knop */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-300 text-sm">
            <div className="flex items-start gap-3">
              <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{error}</p>
                {error.includes('bewaard') && (
                  <p className="text-xs text-slate-400 mt-1">Je antwoorden zijn veilig opgeslagen. Klik op de knop om opnieuw te proberen.</p>
                )}
              </div>
            </div>
            {error.includes('bewaard') && canGenerate() && (
              <button
                onClick={() => {
                  setError('');
                  generateRapportage();
                }}
                className="mt-3 w-full py-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors font-medium"
              >
                🔄 Opnieuw proberen
              </button>
            )}
          </div>
        )}

        {/* Content based on step */}
        {currentStep === 0 && renderBegeleiderSelection()}
        {currentStep === 1 && renderClientSelection()}
        {currentStep === 2 && !isSjabloonMetDoelkoppeling && ((selectedClientData?.ambulant || isDoelRapportageClient) ? renderAmbulantUrenSelection() : renderDienstSelection())}
        {currentStep === 3 && (isDoelRapportageClient ? renderDoelRapportageFormulier() : renderVragenFormulier())}
        {(currentStep === 4 || currentStep === 5) && renderPreview()}
      </div>
    </div>
  );
};

export default RapporteerHelperPage;
